import { useFavoriteTab } from '../../hooks/useFavoriteTab';
import MuleBadge from '../../components/MuleBadge';
import React, { useState, useEffect, useMemo, useCallback, useRef, lazy, Suspense } from 'react';
import { createPortal } from 'react-dom';
import { GridStack } from 'gridstack';
import 'gridstack/dist/gridstack.min.css';
import {
  ExclamationTriangleIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  TrashIcon,
  ArrowTopRightOnSquareIcon,
  AdjustmentsHorizontalIcon,
  ArrowPathIcon,
  FunnelIcon,
  TruckIcon,
} from '@heroicons/react/24/outline';
import api from '../../services/api';
import userSettingsService from '../../services/userSettings.service';
import ordersService from '../../services/orders.service';
import MultiSelect, { MultiSelectOption } from '../../components/MultiSelect';
import { OrderDispatcherFilter, DriverDispatcherFilter } from '../../components/filters';
import { DRIVER_BOARD_TAB_ID, useCanSeeDriverBoard } from './driver-board/useDriverBoardAccess';
import PermissionConfigurable from '../../components/PermissionConfigurable';
import { usePermissionsMode } from '../../contexts/PermissionsModeContext';
import { usePermissions } from '../../contexts/PermissionContext';
import { useCustomizeLayoutMode } from '../../contexts/CustomizeLayoutModeContext';
import { useDispatchFooter } from '../../contexts/DispatchFooterContext';
import { useToast } from '../../contexts/ToastContext';
import StatsGrid, { StatItem, TimePeriod } from '../../components/StatsGrid';
import TabBar, { Tab } from '../../components/TabBar';
import { PlusIcon } from '@heroicons/react/24/outline';
import FirstCardModal from '../../components/FirstCardModal';
import CardResultPill from '../../components/CardResultPill';
import DuplicateCardModal from '../../components/DuplicateCardModal';
import { ProDetailsView } from '../../components/ProDetailsView';
import DriverPayAdditionModal from '../../components/pro/DriverPayAdditionModal';
import TroubleTicketEditModal from '../pro/components/TroubleTicketEditModal';
import type { TroubleTicket } from '../../services/troubleTickets.service';
import { config } from '../../config/config';
import { TanTable, BulkEditMeta } from '../../components/TanTable';
import {
  applyDeclinedBulkEdit,
  declinedRowsToSave,
  pruneDeclinedEdits,
  EMPTY_EDIT,
  type DeclinedLegEdit,
} from './declinedLegsEdits';
import { buildProBulkActions } from '../../components/proBulkActions';
import CardDocumentsTable from '../search-documents/CardDocumentsTable';
import { MAX_SEARCH_PROS } from '../../types/documentsSearch';
import { useMsal } from '@azure/msal-react';
import ProDetailSlideOver from '../../components/pro/ProDetailSlideOver';
import ChangeTemplateBulkModal from '../pro/components/ChangeTemplateBulkModal';
import { useCanEditOrderLcFields } from '../pro/useCanEditOrderLcFields';
import { useCanUseChangeAll } from '../pro/useCanUseChangeAll';
import { usePrefetchChangeAllFields } from '../../hooks/useChangeAllFields';
import { ColumnDef } from '@tanstack/react-table';
import { useFieldDefinitions } from '../../contexts/FieldDefinitionsContext';
import { customCardScopeKey } from '../../utils/customCardKeys';
import { readTabWorkspaceId, resolveWorkspaceId, writeTabWorkspaceId } from '../../utils/dashboardWorkspaceSession';
import { generateColumnDef, decorateMuleDriverColumn, getApiFieldName, getBucketFields, decorateLocationColumn, formatDateTime } from '../../utils/dynamicColumns';
import { formatCurrency } from '../../utils/money';
import { REQ_FIELD, expandAdditionalFields } from '../../utils/reqColumn';
import { COMPUTED_FIELD_OPTIONS, buildComputedColumnDefs } from '../../utils/computedColumns';
import RequirementIcons from '../../components/RequirementIcons';
import { useDisplayPreferences } from '../../contexts/DisplayPreferencesContext';
import GridLabel from '../../components/GridLabel';
import FilterBar from '../../components/FilterBar';
import { useGlobalFilters } from '../../contexts/GlobalFiltersContext';
import { ViewedProsProvider, useViewedPros } from '../../contexts/ViewedProsContext';
import styles from './DispatchDashboard.module.css';
import { useVisibilityAwareInterval } from '../../hooks/useVisibilityAwareInterval';
import { useDispatchBoardPush } from '../../hooks/useDispatchBoardPush';
import { useModalDrag } from '../../hooks/useModalDrag';
import ModalHeader from '../../components/ui/ModalHeader';

// Pinned "Driver Board" tab body. Lazy so its grid + column builders stay out of
// the dashboard bundle for the (currently most) users without the grant.
const DriverBoardTab = lazy(() => import('./driver-board/DriverBoardTab'));

interface FilterCondition {
  id: number;
  field: string;
  compare: string;
  values: string;
}

interface CardConfig {
  key: string;
  title: string;
  column: number;
  order: number;
  visible: boolean;
  span: number;
  workspaceId?: string;
  locked?: boolean;  // System/locked cards that cannot be edited or deleted
  // Data builder configuration
  filterConditions?: FilterCondition[];
  filterView?: string;
  // "Include Closed Orders" on the card's filter: query ordersall/legsall instead
  // of orderscurrent/legscurrent. Custom-filter cards only — a bucket card's scope
  // comes from the backend bucket registry.
  includeClosed?: boolean;
  visibleColumns?: string[];
  bucket?: string;  // Pre-built bucket identifier (e.g., 'per-diem-not-dispatched')
  grouping?: string[];  // Current grouping columns
  tabGrouping?: string;  // Tab-based grouping field (for bucket cards with tabs)
  // Dynamic columns configuration
  additionalFields?: string[];  // Extra fields beyond bucket defaults, triggers refetch when changed
  // StatButton configuration
  showStatButton?: boolean;  // Whether to show this card in the stats grid
  statButtonLabel?: string;  // Custom label for the stat button (defaults to card title)
  statButtonCountField?: string;  // Field to use for counting (e.g., 'perdiemstartdate', 'cutoffdatetime')
  // Card height configuration
  cardHeight?: 'sm' | 'md' | 'lg';  // Height of the card table
  // GridStack position (if set, used directly instead of computing from column/order/span)
  gsX?: number;
  gsY?: number;
  gsW?: number;
  gsH?: number;
  expandedGsH?: number;  // Stores the height when card is collapsed, so it can be restored when expanded
  isShared?: boolean;
}

interface Workspace {
  id: string;
  name: string;
  order: number;
  columnCount: number;
}

type StatCategory = 'critical' | 'important' | 'informational';

// Categorization for the main-tab stats grids. Keyed by bucket so user-duplicated
// cards inherit the same group as their source. Cards whose bucket is absent
// here are skipped from the categorized stats panel.
const STAT_CATEGORY_BY_BUCKET: Record<string, StatCategory> = {
  'dispatch-not-started': 'critical',
  'dispatch-started-not-completed': 'critical',
  'dispatched-driver-oos': 'critical',
  'cutoff-not-dispatched': 'critical',
  'terminated-not-complete': 'critical',
  'trace-terminate-no-driver': 'critical',

  'missing-waiting-time': 'important',
  'dispatches-not-accepted': 'important',
  'not-validated-chassis': 'important',
  'declined-cancelled-legs': 'important',
  'declined-last-24h': 'important',
  'lfd-today-or-past': 'important',
  'invalid-container-numbers': 'important',
  'scheduled-not-dispatched': 'important',
  'cutoff-not-terminated': 'important',
  'past-due': 'important',
  'no-outgate': 'important',
  'past-due-no-outgate': 'critical',
  'live-load-unload-no-terminate': 'important',
  'pod-bol-mr-pl-eir-required': 'important',

  'drop-released-not-dispatched': 'informational',
  'per-diem-not-dispatched': 'informational',
  'dispatched-not-yet-arrived': 'informational',
};

function DispatchDashboardContent() {
  // Permissions mode context
  const { permissionsMode, setPageComponents } = usePermissionsMode();
  const { columnCount, setColumnCount, setCustomActions } = useCustomizeLayoutMode();
  const { setDispatchButton } = useDispatchFooter();
  const { showGridHighlight, showGridLabels, showGridBorders } = useDisplayPreferences();
  const { showToast } = useToast();
  const { permissions } = usePermissions();
  const { isViewed, markViewed } = useViewedPros();

  // Field definitions for dynamic columns
  const { fieldDefinitions, loading: fieldDefinitionsLoading, error: fieldDefinitionsError, refetch: refetchFieldDefinitions } = useFieldDefinitions();

  // Trigger refetch if field definitions failed to load or are empty after loading completed
  useEffect(() => {
    if (!fieldDefinitionsLoading && fieldDefinitions.length === 0 && !fieldDefinitionsError) {
      // Field definitions didn't load but no error - try again
      refetchFieldDefinitions();
    }
  }, [fieldDefinitionsLoading, fieldDefinitions.length, fieldDefinitionsError, refetchFieldDefinitions]);

  // All fields available for dynamic columns (only displayable fields)
  const availableFields = useMemo(() => {
    return [
      // Synthetic "Req" column — composite requirement icons (HOT, Hazmat,
      // Overweight, Triaxle, Swing, Tank, Reefer). Not a real DB field; it is
      // expanded into the underlying flag fields when fetching (see reqColumn.ts).
      {
        fieldname: REQ_FIELD,
        displayname: 'Req (Requirement Icons)',
        abbreviation: null as string | null,
        type: 'string',
      },
      // Service Failure (SF) + Trouble Ticket (TT) computed columns — not real DB
      // fields; enriched server-side and rendered via buildComputedColumnDefs.
      ...COMPUTED_FIELD_OPTIONS,
      ...fieldDefinitions
        .filter(f => f.is_displayable)
        .map(f => ({
          fieldname: f.fieldname,
          displayname: f.displayname,
          abbreviation: f.abbreviation,
          type: f.type,
          expands: !!f.expands,
        })),
    ];
  }, [fieldDefinitions]);

  // To ensure we reload when making large changes
  const CURRENT_VERSION = '13'; // Bumped for Critical workspace + Trouble Tickets bucket

  // State to track which sections are collapsed
  const [collapsed, setCollapsed] = useState<{ [key: string]: boolean }>({});

  // Per-row edits for the Not Validated Chassis bucket. Default for any
  // unseen pronumber is treated as { value: 'N', comment: '' }; only rows the
  // user has touched land in this map. Cleared after a successful save.
  //
  // The cell renderers in notValidatedChassisColumns read live values via the
  // refs below and update via stable callbacks. Keeping `chassisEdits` and
  // `chassisSaving` out of the columns useMemo deps keeps the columns array
  // referentially stable across keystrokes, so TanStack Table preserves the
  // <input> instance and the focus doesn't get blown away on every character.
  type ChassisRowEdit = { value: 'N' | 'Y' | ''; comment: string };
  const [chassisEdits, setChassisEdits] = useState<Record<string, ChassisRowEdit>>({});
  const [chassisSaving, setChassisSaving] = useState(false);
  const chassisEditsRef = useRef<Record<string, ChassisRowEdit>>({});
  const chassisSavingRef = useRef(false);
  chassisEditsRef.current = chassisEdits;
  chassisSavingRef.current = chassisSaving;

  const setChassisValid = useCallback((pro: string, value: ChassisRowEdit['value']) => {
    setChassisEdits((prev) => ({
      ...prev,
      [pro]: { ...(prev[pro] ?? { value: 'N', comment: '' }), value },
    }));
  }, []);

  const setChassisComment = useCallback((pro: string, comment: string) => {
    setChassisEdits((prev) => ({
      ...prev,
      [pro]: { ...(prev[pro] ?? { value: 'N', comment: '' }), comment },
    }));
  }, []);

  const handleChassisBulkEdit = useCallback((columnId: string, rows: any[], value: unknown) => {
    setChassisEdits((prev) => {
      const updated = { ...prev };
      for (const row of rows) {
        const pro = String(row.pronumber ?? '');
        const existing = updated[pro] ?? { value: 'N' as ChassisRowEdit['value'], comment: '' };
        if (columnId === 'isvalidchassis') {
          updated[pro] = { ...existing, value: value as ChassisRowEdit['value'] };
        } else if (columnId === 'chassis_comment') {
          updated[pro] = { ...existing, comment: String(value ?? '') };
        }
      }
      return updated;
    });
  }, []);

  // Per-row edits for the Declined/Cancelled Legs bucket, keyed by
  // dispatch_recno (a pro can carry several declined legs). Same ref/stable-
  // callback pattern as the chassis card so the columns memo stays stable and
  // the inputs keep focus across keystrokes.
  const [declinedEdits, setDeclinedEdits] = useState<Record<string, DeclinedLegEdit>>({});
  const [declinedSaving, setDeclinedSaving] = useState(false);
  const declinedEditsRef = useRef<Record<string, DeclinedLegEdit>>({});
  const declinedSavingRef = useRef(false);
  declinedEditsRef.current = declinedEdits;
  declinedSavingRef.current = declinedSaving;

  const setDeclinedResolve = useCallback((recno: string, resolve: boolean) => {
    setDeclinedEdits((prev) => ({ ...prev, [recno]: { ...(prev[recno] ?? EMPTY_EDIT), resolve } }));
  }, []);

  const setDeclinedComment = useCallback((recno: string, comment: string) => {
    setDeclinedEdits((prev) => ({ ...prev, [recno]: { ...(prev[recno] ?? EMPTY_EDIT), comment } }));
  }, []);

  const handleDeclinedBulkEdit = useCallback((columnId: string, rows: any[], value: unknown) => {
    setDeclinedEdits((prev) => applyDeclinedBulkEdit(prev, columnId, rows, value));
  }, []);

  // State for saving overlay
  const [isSaving, setIsSaving] = useState(false);

  // State for trashcan modal
  const [showTrashModal, setShowTrashModal] = useState(false);
  const [trashItems, setTrashItems] = useState<any[]>([]);
  const [trashLoading, setTrashLoading] = useState(false);

  // State for ProDetailsView modal
  const [selectedProNumber, setSelectedProNumber] = useState<string | null>(null);
  const canEditOrderLcFields = useCanEditOrderLcFields();
  // Change All carries its own component since the split, so Dispatch can fan out
  // trouble tickets without holding the LC field gate. Change Template for All
  // stays on `order-lc-fields` — it rewrites the order's fields wholesale.
  const canUseChangeAll = useCanUseChangeAll();
  // Warm the Change All field registry now rather than when the first
  // checkbox mounts, so the drawer's first paint is already the registry's
  // answer and not the built-in fallback. No request for users without the
  // grant — see usePrefetchChangeAllFields.
  usePrefetchChangeAllFields(canUseChangeAll);
  // Change All / Change Template for All from a card's action bar. Change All
  // opens the order drawer in change-all mode on the first pro (anchor); the rest
  // fan out on Save. Change Template opens the bulk-template modal. Both act on
  // the pros passed in by the action bar (that card's visible/filtered rows).
  const [changeAllPros, setChangeAllPros] = useState<(number | string)[] | null>(null);
  const [ctBulkOpen, setCtBulkOpen] = useState(false);
  const [ctBulkPros, setCtBulkPros] = useState<(number | string)[]>([]);
  const openChangeTemplateForAll = (prosArg: (number | string)[]) => {
    const pros = prosArg.map(String).filter((p) => p && !p.toUpperCase().startsWith('T'));
    if (pros.length === 0) { showToast({ type: 'info', message: 'No pros in the current view to change.' }); return; }
    setCtBulkPros(pros);
    setCtBulkOpen(true);
  };
  const openChangeAll = (prosArg: (number | string)[]) => {
    const pros = prosArg.map(String).filter((p) => p && !p.toUpperCase().startsWith('T'));
    if (pros.length === 0) { showToast({ type: 'info', message: 'No pros in the current view to change.' }); return; }
    // Change All and the single-pro modal are mutually exclusive.
    if (selectedProNumber) setSelectedProNumber(null);
    setChangeAllPros(pros);
  };

  // State for DriverPayAdditionModal (opened from the Missing Waiting Time bucket)
  const [driverPayAddition, setDriverPayAddition] = useState<{ pronumber: number; recno: number } | null>(null);

  // State for the trouble-ticket editor (rendered as a right-side drawer).
  // ticketLoading covers the by-recno fetch between clicking a ticket link
  // and the editor opening.
  const [selectedTicket, setSelectedTicket] = useState<TroubleTicket | null>(null);
  const [ticketLoading, setTicketLoading] = useState(false);

  // State for duplicate card modal
  const [duplicateCardConfig, setDuplicateCardConfig] = useState<CardConfig | null>(null);

  // State for new workspace modal
  const [showNewWorkspaceModal, setShowNewWorkspaceModal] = useState(false);
  const [newWorkspaceName, setNewWorkspaceName] = useState('');

  // State for global table controls
  const [showGlobalControls, setShowGlobalControls] = useState(false);
  const [globalResetTrigger, setGlobalResetTrigger] = useState(0);
  const [globalPageSize, setGlobalPageSize] = useState<number | undefined>(undefined);
  const [globalShowFilters, setGlobalShowFilters] = useState<boolean | undefined>(undefined);
  const [globalSuperCompact, setGlobalSuperCompact] = useState<boolean | undefined>(undefined);
  const [globalControlsPosition, setGlobalControlsPosition] = useState<{ top: number; right: number } | null>(null);

  const [globalCardHeight, setGlobalCardHeight] = useState<'sm' | 'md' | 'lg' | undefined>(undefined);
  const globalControlsRef = React.useRef<HTMLDivElement>(null);
  const globalControlsDropdownRef = React.useRef<HTMLDivElement>(null);

  // State for reset layout confirmation
  const [showResetLayoutConfirm, setShowResetLayoutConfirm] = useState(false);

  // GridStack refs
  const gridRef = useRef<HTMLDivElement>(null);
  const gridInstanceRef = useRef<GridStack | null>(null);
  const isGridInitializing = useRef<boolean>(true); // Skip change events during initialization

  // GridStack item interface for positioning
  interface GridItem {
    id: string;
    x: number;
    y: number;
    w: number;
    h: number;
  }

  // Default configuration function
  const getDefaultCardConfigs = (): CardConfig[] => {
    return [
      {
        key: 'per-diem-not-dispatched',
        title: 'Per Diem Not Dispatched',
        column: 0,
        order: 0,
        visible: true,
        span: 2,
        workspaceId: 'default',
        locked: true,
        bucket: 'per-diem-not-dispatched',
        filterConditions: [],
        filterView: 'Orders',
        tabGrouping: 'perdiemstartdate'
      },
      {
        key: 'cutoff-not-dispatched',
        title: 'Cutoff Not Dispatched',
        column: 0,
        order: 1,
        visible: true,
        span: 2,
        workspaceId: 'default',
        locked: true,
        bucket: 'cutoff-not-dispatched',
        filterConditions: [],
        filterView: 'Orders',
        tabGrouping: 'cutoffdatetime'
      },
      {
        key: 'cutoff-not-terminated',
        title: 'Cutoff Not Terminated',
        column: 0,
        order: 2,
        visible: true,
        span: 2,
        workspaceId: 'default',
        locked: true,
        bucket: 'cutoff-not-terminated',
        filterConditions: [],
        filterView: 'Orders',
        tabGrouping: 'cutoffdatetime'
      },
      {
        key: 'drop-released-not-dispatched',
        title: 'Drop Released Not Dispatched',
        column: 0,
        order: 3,
        visible: true,
        span: 2,
        workspaceId: 'default',
        locked: true,
        bucket: 'drop-released-not-dispatched',
        filterConditions: [],
        filterView: 'Orders'
      },
      {
        key: 'terminated-not-complete',
        title: 'Terminated Not Complete',
        column: 0,
        order: 4,
        visible: true,
        span: 2,
        workspaceId: 'default',
        locked: true,
        bucket: 'terminated-not-complete',
        filterConditions: [],
        filterView: 'Orders'
      },
      {
        key: 'no-outgate',
        title: 'No Outgate',
        column: 0,
        order: 5,
        visible: true,
        span: 2,
        workspaceId: 'default',
        locked: true,
        bucket: 'no-outgate',
        filterConditions: [],
        filterView: 'Orders'
      },
      {
        key: 'past-due-no-outgate',
        title: 'Past Due No Outgate',
        column: 0,
        order: 6,
        visible: true,
        span: 2,
        workspaceId: 'default',
        locked: true,
        bucket: 'past-due-no-outgate',
        filterConditions: [],
        filterView: 'Orders'
      },
      {
        key: 'past-due',
        title: 'Past Due Per Diem',
        column: 0,
        order: 6,
        visible: true,
        span: 2,
        workspaceId: 'default',
        locked: true,
        bucket: 'past-due',
        filterConditions: [],
        filterView: 'Orders'
      },
      {
        key: 'live-load-unload-no-terminate',
        title: 'Live Load/Unload, No Terminate',
        column: 0,
        order: 7,
        visible: true,
        span: 2,
        workspaceId: 'default',
        locked: true,
        bucket: 'live-load-unload-no-terminate',
        filterConditions: [],
        filterView: 'Orders'
      },
      {
        key: 'dispatched-driver-oos',
        title: 'Dispatched - Driver OOS',
        column: 0,
        order: 8,
        visible: true,
        span: 2,
        workspaceId: 'default',
        locked: true,
        bucket: 'dispatched-driver-oos',
        filterConditions: [],
        filterView: 'Orders'
      },
      {
        key: 'dispatches-not-accepted',
        title: 'Dispatches Not Accepted',
        column: 0,
        order: 9,
        visible: true,
        span: 2,
        workspaceId: 'default',
        locked: true,
        bucket: 'dispatches-not-accepted',
        filterConditions: [],
        filterView: 'Orders'
      },
      {
        key: 'scheduled-not-dispatched',
        title: 'Scheduled Not Dispatched',
        column: 0,
        order: 10,
        visible: true,
        span: 2,
        workspaceId: 'default',
        locked: true,
        bucket: 'scheduled-not-dispatched',
        filterConditions: [],
        filterView: 'Orders',
        tabGrouping: 'd_legmiles_band'
      },
      {
        key: 'dispatch-started-not-completed',
        title: 'Dispatch Started, Not Completed',
        column: 0,
        order: 11,
        visible: true,
        span: 2,
        workspaceId: 'default',
        locked: true,
        bucket: 'dispatch-started-not-completed',
        filterConditions: [],
        filterView: 'Orders'
      },
      {
        key: 'dispatch-not-started',
        title: 'Dispatch Not Started',
        column: 0,
        order: 12,
        visible: true,
        span: 2,
        workspaceId: 'default',
        locked: true,
        bucket: 'dispatch-not-started',
        filterConditions: [],
        filterView: 'Orders'
      },
      {
        key: 'trace-terminate-no-driver',
        title: 'Trace Terminate No Driver',
        column: 0,
        order: 13,
        visible: true,
        span: 2,
        workspaceId: 'default',
        locked: true,
        bucket: 'trace-terminate-no-driver',
        filterConditions: [],
        filterView: 'Orders'
      },
      {
        key: 'pod-bol-mr-pl-eir-required',
        title: 'POD/BOL/MR/PL/EIR Required',
        column: 0,
        order: 14,
        visible: true,
        span: 2,
        workspaceId: 'default',
        locked: true,
        bucket: 'pod-bol-mr-pl-eir-required',
        filterConditions: [],
        filterView: 'Orders'
      },
      {
        key: 'invalid-container-numbers',
        title: 'Invalid Container Numbers',
        column: 0,
        order: 15,
        visible: true,
        span: 2,
        workspaceId: 'default',
        locked: true,
        bucket: 'invalid-container-numbers',
        filterConditions: [],
        filterView: 'Orders',
        visibleColumns: [
          'pronumber',
          'container',
          'dispatcher',
          'enteredby',
        ],
      },
      {
        key: 'missing-waiting-time',
        title: 'Missing Waiting Time',
        column: 0,
        order: 16,
        visible: true,
        span: 2,
        workspaceId: 'default',
        locked: true,
        bucket: 'missing-waiting-time',
        filterConditions: [],
        filterView: 'Orders',
        visibleColumns: [
          'pronumber',
          'keyaccount',
          'container',
          'movetype',
          'scac',
          'dpdriver',
          'dpeventdate',
          'dpintime',
          'dpouttime',
          'dplocation',
          'dpdescription',
        ],
      },
      {
        key: 'dispatched-not-yet-arrived',
        title: 'Dispatched Not Yet Arrived',
        column: 0,
        order: 17,
        visible: true,
        span: 2,
        workspaceId: 'default',
        locked: true,
        bucket: 'dispatched-not-yet-arrived',
        filterConditions: [],
        filterView: 'Orders'
      },
      {
        key: 'not-validated-chassis',
        title: 'Not Validated Chassis',
        column: 0,
        order: 18,
        visible: true,
        span: 2,
        workspaceId: 'default',
        locked: true,
        bucket: 'not-validated-chassis',
        filterConditions: [],
        filterView: 'Orders',
        cardHeight: 'sm',
      },
      {
        key: 'declined-cancelled-legs',
        title: 'Declined/Cancelled Legs',
        column: 0,
        order: 19,
        visible: true,
        span: 2,
        workspaceId: 'default',
        locked: true,
        bucket: 'declined-cancelled-legs',
        filterConditions: [],
        filterView: 'Orders',
        cardHeight: 'sm',
      },
      {
        key: 'declined-last-24h',
        title: 'Declined Dispatches (24h)',
        column: 0,
        order: 20,
        visible: true,
        span: 2,
        workspaceId: 'default',
        locked: true,
        bucket: 'declined-last-24h',
        filterConditions: [],
        filterView: 'Orders',
        cardHeight: 'sm',
      },
      {
        key: 'lfd-today-or-past',
        title: 'LFD Today or Past Due',
        column: 0,
        order: 21,
        visible: true,
        span: 2,
        workspaceId: 'default',
        locked: true,
        bucket: 'lfd-today-or-past',
        filterConditions: [],
        filterView: 'Orders',
      },
      // ── Critical workspace cards ──────────────────────────────────────
      {
        key: 'trouble-tickets-dispatcher',
        title: 'Trouble Tickets - Dispatcher',
        column: 0,
        order: 0,
        visible: true,
        span: 2,
        workspaceId: 'critical',
        locked: true,
        bucket: 'trouble-tickets-dispatcher',
        filterConditions: [],
        filterView: 'Orders'
      },
      {
        key: 'mismatched-drivers',
        title: 'Dispatches with Mismatched Drivers',
        column: 0,
        order: 1,
        visible: true,
        span: 2,
        workspaceId: 'critical',
        locked: true,
        bucket: 'mismatched-drivers',
        filterConditions: [],
        filterView: 'Orders',
        visibleColumns: [
          'pronumber',
          'legnum',
          'next_legnum',
          'destapptdate',
          'next_destapptdate',
          'driver',
          'next_driver'
        ]
      }
    ];
  };

  // Default workspaces
  const getDefaultWorkspaces = (): Workspace[] => {
    return [
      { id: 'default', name: 'Main', order: 0, columnCount: 2 },
      { id: 'critical', name: 'Critical', order: 1, columnCount: 2 }
    ];
  };

  // System ("locked") buckets are defined in code (getDefaultCardConfigs) and backed by
  // canonical SQL on the server. Their query is not user-editable: customization happens by
  // duplicating a bucket, never by editing it in place. This map (key -> default config) is
  // the single source of truth for "is this a system bucket?" — used both to hide the filter
  // cog and to scrub any query overrides a user saved back when in-place editing existed.
  const systemBucketDefaults = useMemo(() => {
    const map = new Map<string, CardConfig>();
    getDefaultCardConfigs().forEach(c => {
      if (c.locked) map.set(c.key, c);
    });
    return map;
    // getDefaultCardConfigs is a pure constant factory; the set never changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // State for card configuration - initialize with defaults, will be loaded from DB
  const [cardConfigs, setCardConfigs] = useState<CardConfig[]>(getDefaultCardConfigs());
  const [configsLoaded, setConfigsLoaded] = useState(false);

  // State for workspaces
  const [workspaces, setWorkspaces] = useState<Workspace[]>(getDefaultWorkspaces());
  const [currentWorkspaceId, setCurrentWorkspaceId] = useState<string>('default');
  useFavoriteTab(currentWorkspaceId, setCurrentWorkspaceId, [...workspaces.map(ws => ws.id), DRIVER_BOARD_TAB_ID], configsLoaded, workspaces.find(ws => ws.id === currentWorkspaceId)?.name ?? 'Driver Board');

  // Get grid items for current workspace
  // Uses stored GridStack positions if available, otherwise computes from column/order/span
  const currentGridItems = useMemo((): GridItem[] => {
    const workspaceCards = cardConfigs
      .filter(c => c.workspaceId === currentWorkspaceId && c.visible);
    workspaceCards.sort((a, b) => {
        // If both have gsY, sort by that; otherwise sort by column then order
        if (a.gsY !== undefined && b.gsY !== undefined) {
          if (a.gsY !== b.gsY) return a.gsY - b.gsY;
          return (a.gsX || 0) - (b.gsX || 0);
        }
        if (a.column !== b.column) return a.column - b.column;
        return a.order - b.order;
      });

    // Track the next available y position for each column (for cards without gsY)
    const columnHeights: { [col: number]: number } = { 0: 0, 1: 0, 2: 0 };

    const items = workspaceCards.map(card => {
      // If GridStack positions are stored AND valid (w and h >= 1), use them directly
      if (card.gsX !== undefined && card.gsY !== undefined && card.gsW !== undefined && card.gsH !== undefined
          && card.gsW >= 1 && card.gsH >= 1) {
        return { id: card.key, x: card.gsX, y: card.gsY, w: card.gsW, h: card.gsH };
      }

      // Otherwise, compute from column/order/span (for initial layout or migration)
      const col = card.column;
      const span = card.span || 1;

      // Map column to x position (each column is 4 grid units in a 12-column grid)
      const x = col * 4;
      // Map span to width (1 span = 4 units, 2 spans = 8 units, 3 spans = 12 units)
      const w = span * 4;
      // Use the current height for this column
      const y = columnHeights[col];
      // Height based on card height setting
      const h = card.cardHeight === 'sm' ? 3 : card.cardHeight === 'md' ? 5 : 7;

      // Update column heights for cards this card spans
      for (let i = 0; i < span; i++) {
        columnHeights[col + i] = Math.max(columnHeights[col + i] || 0, y + h);
      }

      return { id: card.key, x, y, w, h };
    });
    return items;
  }, [cardConfigs, currentWorkspaceId]);

  // State for Add Card modal
  const [isAddCardModalOpen, setIsAddCardModalOpen] = useState(false);
  const [newCardTitle, setNewCardTitle] = useState('');
  const [newCardColumn, setNewCardColumn] = useState(0);

  // Drag-to-move for this page's inline modals — one hook per modal, keyed to that
  // modal's own open state so each reopen starts centered.
  const addCardDrag = useModalDrag(isAddCardModalOpen);
  const resetLayoutDrag = useModalDrag(showResetLayoutConfirm);
  const trashDrag = useModalDrag(showTrashModal);
  const newWorkspaceDrag = useModalDrag(showNewWorkspaceModal);

  // State for first card creation modal
  const [isFirstCardModalOpen, setIsFirstCardModalOpen] = useState(false);

  // State for editing existing card filters
  const [editingCardFiltersKey, setEditingCardFiltersKey] = useState<string | null>(null);

  // State for card data
  const [cardData, setCardData] = useState<{ [cardKey: string]: any[] }>({});
  const [cardDataLoading, setCardDataLoading] = useState<{ [cardKey: string]: boolean }>({});
  // Per-card load error (so a failed card shows a retry-able message instead of
  // an empty table that looks identical to a genuinely empty bucket).
  const [cardErrors, setCardErrors] = useState<{ [cardKey: string]: string | null }>({});
  // What the last response for each card actually covered: whether it reached the all
  // tables (closed orders included) and whether the backend capped it. Response-derived
  // rather than config-derived so the header pill can never describe a scope the rows
  // on screen don't have. Custom-filter cards only; bucket cards never set it.
  const [cardScopeMeta, setCardScopeMeta] = useState<{
    [cardKey: string]: { includeClosed: boolean; truncated: boolean; limit?: number }
  }>({});

  // Pre-fills the Documents view's bulk-email recipient. `account.username` is the
  // MSAL UPN — the same value PermissionContext sends as `user_email` — so it matches
  // what the rest of the app calls "the current user". Read here rather than inside
  // the documents table so that component stays free of an auth dependency (the same
  // split SearchResults makes).
  const { accounts } = useMsal();
  const currentUserEmail = accounts[0]?.username || '';

  // Tables currently repainted with document results, keyed by table (a card key, or a
  // drill kind for the grid-cell drills). Each entry holds the PRO list captured when
  // the user switched — NOT a boolean: the orders grid whose filters produced that
  // scope is no longer rendering, so there is nothing left to re-derive it from.
  // Switching back and re-filtering re-captures on the next switch. The title travels
  // with it because the drills' titles are not card configs.
  // Deliberately not persisted: a detour, not a preference.
  const [docsViewCards, setDocsViewCards] = useState<{
    [tableKey: string]: { pros: number[]; title: string }
  }>({});

  const openCardDocuments = useCallback((tableKey: string, pros: number[], title: string) => {
    if (pros.length === 0) {
      // The documents endpoint requires at least one PRO (min_length=1), so an empty
      // switch would 422. Say so rather than repainting to a dead table.
      showToast({ type: 'info', message: 'No pros in the current view to show documents for.' });
      return;
    }
    if (pros.length > MAX_SEARCH_PROS) {
      // One documents request carries at most this many PROs.
      showToast({
        type: 'warning',
        message: `${pros.length.toLocaleString()} pros in view — the Documents view takes `
          + `at most ${MAX_SEARCH_PROS.toLocaleString()}. Narrow the table first.`,
      });
      return;
    }
    setDocsViewCards(prev => ({ ...prev, [tableKey]: { pros, title } }));
  }, [showToast]);

  const closeCardDocuments = useCallback((tableKey: string) => {
    setDocsViewCards(prev => {
      if (!(tableKey in prev)) return prev;
      const next = { ...prev };
      delete next[tableKey];
      return next;
    });
  }, []);

  const [initialLoadComplete, setInitialLoadComplete] = useState(false);

  // Batch-loaded table settings for all cards (avoids individual API calls per TanTable)
  const [tableSettings, setTableSettings] = useState<{ [settingsKey: string]: any }>({});
  const [tableSettingsLoaded, setTableSettingsLoaded] = useState(false);
  const [tableSettingsLoadFailed, setTableSettingsLoadFailed] = useState(false);
  // Admin-published default layouts for shared buckets, keyed by `dispatch-card-<bucket>`.
  // Seeds tables for users with no saved settings and is the "Reset all settings" target.
  const [cardDefaults, setCardDefaults] = useState<{ [settingsKey: string]: any }>({});

  // Tab optimization state - track tab counts and selected tabs per card
  const [cardTabCounts, setCardTabCounts] = useState<{ [cardKey: string]: { tab_value: string; count: number }[] }>({});
  const [cardSelectedTabs, setCardSelectedTabs] = useState<{ [cardKey: string]: string }>({});
  const cardSelectedTabsRef = useRef<{ [cardKey: string]: string }>({});

  // Dashboard loading timer
  const [dashboardLoadSeconds, setDashboardLoadSeconds] = useState(0);
  const dashboardLoadStartRef = useRef<number>(Date.now());

  useEffect(() => {
    // Only run timer while dashboard is loading
    if (!configsLoaded || fieldDefinitionsLoading || (fieldDefinitions.length === 0 && !fieldDefinitionsError)) {
      const interval = setInterval(() => {
        setDashboardLoadSeconds(Math.floor((Date.now() - dashboardLoadStartRef.current) / 1000));
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [configsLoaded, fieldDefinitionsLoading, fieldDefinitions.length, fieldDefinitionsError]);

  // Ref to track pending API requests for cancellation
  const abortControllersRef = useRef<{ [cardKey: string]: AbortController }>({});
  const bucketCountsAbortControllerRef = useRef<AbortController | null>(null);
  const batchCardsAbortControllerRef = useRef<AbortController | null>(null);

  // Refs to track latest values without causing useCallback recreation
  const cardConfigsRef = useRef(cardConfigs);
  const collapsedRef = useRef(collapsed);
  // saveLayout is defined further down (it closes over other refs), but the
  // column handlers above it need to persist. Route through a ref to avoid the
  // temporal-dead-zone reference. Kept in sync by an effect after saveLayout.
  const saveLayoutRef = useRef<
    ((cards: CardConfig[], immediate?: boolean, onComplete?: () => void) => void) | null
  >(null);

  // Ref to track when we're restoring columnCount from a workspace switch
  // This prevents the save effect from overwriting the workspace's stored columnCount
  const isRestoringColumnCountRef = useRef(false);

  // Keep refs in sync with state
  useEffect(() => {
    cardConfigsRef.current = cardConfigs;
  }, [cardConfigs]);

  useEffect(() => {
    collapsedRef.current = collapsed;
  }, [collapsed]);

  useEffect(() => {
    cardSelectedTabsRef.current = cardSelectedTabs;
  }, [cardSelectedTabs]);

  // State for active tabs per card (multi-tab selection)
  const [cardActiveTabsMulti, setCardActiveTabsMulti] = useState<{ [cardKey: string]: string[] }>({});

  // State for which cards are currently visible (empty set = show all)
  const [visibleCardKeys, setVisibleCardKeys] = useState<Set<string>>(new Set());
  // Store visible card keys per workspace so they persist when switching tabs
  const [workspaceVisibleCards, setWorkspaceVisibleCards] = useState<{ [workspaceId: string]: string[] }>({});

  // All card keys for comparison
  const allCardKeys = ['per-diem-not-dispatched', 'cutoff-not-dispatched', 'cutoff-not-terminated', 'drop-released-not-dispatched', 'dispatched-driver-oos'];

  // Toggle a card's visibility (only one stat can be selected at a time)
  const toggleCardVisibility = (cardKey: string) => {
    setVisibleCardKeys(prev => {
      // If this card is already selected, deselect it (show all)
      if (prev.has(cardKey) && prev.size === 1) {
        return new Set();
      }
      // Otherwise, select only this card
      return new Set([cardKey]);
    });
  };

  // Check if a card should be visible (show all if none selected or all selected)
  const isCardVisible = (cardKey: string): boolean => {
    if (visibleCardKeys.size === 0 || visibleCardKeys.size === allCardKeys.length) {
      return true; // Show all
    }
    return visibleCardKeys.has(cardKey);
  };

  // Check if exactly one card is selected (for full-screen single card mode)
  const isSingleCardSelected = visibleCardKeys.size === 1;

  // Main (default) workspace with no bucket selected: show only the counts picker
  // and load nothing. The user chooses a bucket to load and view.
  const isMainBoardUnselected = currentWorkspaceId === 'default' && visibleCardKeys.size === 0;

  // Pinned Driver Board tab: not a workspace, so it owns no cards and no layout.
  // Ships dark — the hook returns false until the group is granted (migration 407).
  const canSeeDriverBoard = useCanSeeDriverBoard();
  const isDriverBoardTab = currentWorkspaceId === DRIVER_BOARD_TAB_ID;

  // If the grant is revoked while the user is sitting on the tab, send them back
  // to Main rather than leaving them on a tab that no longer renders.
  useEffect(() => {
    if (isDriverBoardTab && !canSeeDriverBoard) {
      setCurrentWorkspaceId('default');
    }
  }, [isDriverBoardTab, canSeeDriverBoard]);

  // Global filters (shared across pages)
  const {
    selectedRegions,
    setSelectedRegions,
    selectedOrderDispatchers: selectedDispatchers,
    setSelectedOrderDispatchers: setSelectedDispatchers,
    selectedDriverDispatchers,
    setSelectedDriverDispatchers,
    isLoading: globalFiltersLoading,
  } = useGlobalFilters();

  // Local filters (page-specific, but still persisted)
  const [selectedCustomers, setSelectedCustomers] = useState<string[]>([]);
  const localFiltersLoaded = useRef(false);
  // State twin of the ref: gates the boot fetches so they wait for the saved
  // customer filter instead of fetching with defaults and refetching on restore.
  const [localFiltersReady, setLocalFiltersReady] = useState(false);

  // Load local filters from user settings
  useEffect(() => {
    if (localFiltersLoaded.current) return;
    const loadLocalFilters = async () => {
      try {
        const setting = await userSettingsService.getUserSetting('dispatch_dashboard_filters');
        if (setting?.setting_value) {
          const filters = setting.setting_value;
          if (filters.selectedCustomers) setSelectedCustomers(filters.selectedCustomers);
        }
      } catch (error) {
        // Silently fail
      }
      localFiltersLoaded.current = true;
      // Batched with the setter above (same tick) → gated effects run once.
      setLocalFiltersReady(true);
    };
    loadLocalFilters();
  }, []);

  // Auto-save local filters when they change (debounced)
  const saveLocalFiltersTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  useEffect(() => {
    if (!localFiltersLoaded.current) return;

    if (saveLocalFiltersTimeoutRef.current) clearTimeout(saveLocalFiltersTimeoutRef.current);
    saveLocalFiltersTimeoutRef.current = setTimeout(async () => {
      try {
        await userSettingsService.saveUserSetting('dispatch_dashboard_filters', { selectedCustomers });
      } catch (error) {
        console.error('Failed to save local filters:', error);
      }
    }, 500);

    return () => {
      if (saveLocalFiltersTimeoutRef.current) clearTimeout(saveLocalFiltersTimeoutRef.current);
    };
  }, [selectedCustomers]);

  // Stats grid time period (defaults to today, resets on page load)
  const [statsTimePeriod, setStatsTimePeriod] = useState<TimePeriod>('today');
  const [bucketCounts, setBucketCounts] = useState<{ [key: string]: number }>({});
  const [bucketCountsLoading, setBucketCountsLoading] = useState(false);
  const [statsCollapsed, setStatsCollapsed] = useState(false);
  const [customerOptions, setCustomerOptions] = useState<MultiSelectOption[]>([]);
  const [regionOptions, setRegionOptions] = useState<MultiSelectOption[]>([]);
  const homeRegionInitialized = useRef(false);

  // Auto-select user's home region when available (only if no global region is set)
  useEffect(() => {
    if (
      !homeRegionInitialized.current &&
      !globalFiltersLoading &&
      selectedRegions.length === 0 &&
      permissions?.home_region &&
      permissions?.home_region_as_default !== false &&
      regionOptions.length > 0 &&
      regionOptions.some(opt => opt.value === permissions.home_region)
    ) {
      setSelectedRegions([permissions.home_region]);
      homeRegionInitialized.current = true;
    }
  }, [permissions?.home_region, permissions?.home_region_as_default, regionOptions, globalFiltersLoading, selectedRegions.length, setSelectedRegions]);

  // Close global controls menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const clickedInButton = globalControlsRef.current?.contains(event.target as Node);
      const clickedInDropdown = globalControlsDropdownRef.current?.contains(event.target as Node);
      if (!clickedInButton && !clickedInDropdown) {
        setShowGlobalControls(false);
      }
    };

    if (showGlobalControls) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showGlobalControls]);

  // Fetch bucket counts function (extracted for reuse in auto-refresh)
  const fetchBucketCounts = useCallback(async (silent: boolean = false) => {
    // The StatsGrid these counts feed only renders on the default workspace
    // (see categorizedStats). Everywhere else this was 21 sequential COUNT(*)
    // scans every 30s for tiles nobody was looking at.
    if (currentWorkspaceId !== 'default') {
      return;
    }

    // If this is a silent background refresh and there's already a request in progress, skip it
    // This prevents canceling user-initiated requests with background refreshes
    if (silent && bucketCountsAbortControllerRef.current) {
      return;
    }

    // Cancel any existing bucket counts request (only for non-silent/user-initiated requests)
    if (bucketCountsAbortControllerRef.current) {
      bucketCountsAbortControllerRef.current.abort();
    }

    // Create new abort controller for this request
    const abortController = new AbortController();
    bucketCountsAbortControllerRef.current = abortController;

    if (!silent) {
      setBucketCountsLoading(true);
    }
    try {
      const buckets = [
        'per-diem-not-dispatched',
        'cutoff-not-dispatched',
        'cutoff-not-terminated',
        'drop-released-not-dispatched',
        'terminated-not-complete',
        'no-outgate',
        ...(statsTimePeriod === 'today' ? ['past-due-no-outgate'] : []),
        'past-due',
        'live-load-unload-no-terminate',
        'dispatched-driver-oos',
        'dispatches-not-accepted',
        'trace-terminate-no-driver',
        'pod-bol-mr-pl-eir-required',
        'dispatched-not-yet-arrived',
        'invalid-container-numbers',
        'missing-waiting-time',
        'scheduled-not-dispatched',
        'dispatch-started-not-completed',
        'dispatch-not-started',
        'mismatched-drivers',
        'not-validated-chassis',
        'declined-cancelled-legs',
        'declined-last-24h',
        'lfd-today-or-past'
      ];
      const response = await api.post('/dispatch-dashboard/bucket-counts', {
        time_period: statsTimePeriod,
        buckets,
        dispatchers: selectedDispatchers,
        customers: selectedCustomers,
        regions: selectedRegions
      }, {
        signal: abortController.signal
      });
      // API returns {time_period: string, counts: {bucket: count}}
      setBucketCounts(response.data.counts || {});
    } catch (err: any) {
      // Don't log cancelled requests as errors
      if (err.name === 'CanceledError' || err.code === 'ERR_CANCELED') {
        return;
      }
      console.error('Failed to fetch bucket counts:', err);
    } finally {
      // Clean up the abort controller
      bucketCountsAbortControllerRef.current = null;

      if (!silent) {
        setBucketCountsLoading(false);
      }
    }
  }, [currentWorkspaceId, statsTimePeriod, selectedDispatchers, selectedCustomers, selectedRegions]);

  // Fetch bucket counts when time period changes. Gated on both filter
  // restores — without the gate this fired 2-4x per page load as the staged
  // filter writes each re-ran the effect (2026-08-25 sweep).
  useEffect(() => {
    if (globalFiltersLoading || !localFiltersReady) return;
    fetchBucketCounts();
  }, [fetchBucketCounts, globalFiltersLoading, localFiltersReady]);

  // Fetch filter dropdown options (customers and regions - dispatchers handled by filter component)
  useEffect(() => {
    const fetchFilterOptions = async () => {
      try {
        const response = await ordersService.getDispatchFilterOptions();

        // Set customers - use recno as value for uniqueness, name as label
        if (response.customers?.length) {
          setCustomerOptions(
            response.customers.map((c: any) => ({
              value: String(c.recno),
              label: c.name
            }))
          );
        }

        // Set regions
        if (response.regions?.revenueRegions?.length) {
          setRegionOptions(
            response.regions.revenueRegions.map((r: string) => ({
              value: r,
              label: r
            }))
          );
        }
      } catch (err: any) {
        console.error('Failed to fetch filter options:', err);
      }
    };
    fetchFilterOptions();
  }, []);

  // Fetch tab counts for a card (lightweight - only gets counts per tab)
  const fetchCardTabCounts = useCallback(async (
    cardKey: string,
    bucket: string,
    tabField: string,
    filterConditions: FilterCondition[] = [],
    silent: boolean = false
  ) => {
    try {
      const response = await api.post(`/dispatch-dashboard/${bucket}`, {
        conditions: filterConditions,
        dispatchers: selectedDispatchers,
        customers: selectedCustomers,
        regions: selectedRegions,
        tab_field: tabField,
        counts_only: true
      });

      if (response.data.tab_counts) {
        setCardTabCounts(prev => ({ ...prev, [cardKey]: response.data.tab_counts }));

        // If no tab is selected yet, default to 'today' if available, otherwise first tab
        // Use functional update to check current state without needing it as dependency
        setCardSelectedTabs(prev => {
          if (prev[cardKey]) return prev; // Already has a selection, don't change

          const todayTab = response.data.tab_counts.find((t: any) => {
            const today = new Date();
            const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
            return t.tab_value === todayStr;
          });
          const defaultTab = todayTab ? todayTab.tab_value : response.data.tab_counts[0].tab_value;
          return { ...prev, [cardKey]: defaultTab };
        });
      }
    } catch (error: any) {
      if (error.name !== 'CanceledError' && error.code !== 'ERR_CANCELED') {
        console.error('Failed to fetch tab counts:', error);
      }
    }
  }, [selectedDispatchers, selectedCustomers, selectedRegions]);

  // Fetch data for a card based on its filter configuration
  const fetchCardData = useCallback(async (
    cardKey: string,
    filterConditions: FilterCondition[],
    filterView: string,
    bucket?: string,
    silent: boolean = false,
    additionalFields?: string[],
    tabField?: string,  // Tab optimization: field to filter by
    tabValue?: string,  // Tab optimization: specific tab value to fetch
    // Scope override for the card just created — cardConfigsRef has not caught up to
    // setCardConfigs yet on that path, so the ref lookup below would read undefined
    // and silently fetch current-scope for a card the user marked closed-inclusive.
    includeClosedOverride?: boolean
  ) => {
    // If this is a silent background refresh and there's already a request in progress, skip it
    // This prevents canceling user-initiated requests with background refreshes
    if (silent && abortControllersRef.current[cardKey]) {
      return;
    }

    // Cancel any existing request for this card (only for non-silent/user-initiated requests)
    if (abortControllersRef.current[cardKey]) {
      abortControllersRef.current[cardKey].abort();
    }

    // Create new abort controller for this request
    const abortController = new AbortController();
    abortControllersRef.current[cardKey] = abortController;

    // Read from the ref rather than taking a parameter: fetchCardData is intentionally
    // NOT dependent on cardConfigs (that would rebuild it on every card edit and churn
    // the polling effects), and the ref is how the rest of this file reads live config.
    const includeClosed = includeClosedOverride
      ?? cardConfigsRef.current.find(c => c.key === cardKey)?.includeClosed
      ?? false;

    if (!silent) {
      setCardDataLoading(prev => ({ ...prev, [cardKey]: true }));
    }

    try {
      let response: any;

      // If bucket is specified, use the batch-cards endpoint (with a single card)
      if (bucket) {
        // Get the base fields for this bucket (frontend is the source of truth)
        const baseFields = getBucketFields(bucket);

        // Build request for batch endpoint with a single card
        const requestBody: any = {
          cards: [{
            card_key: cardKey,
            bucket: bucket,
            base_fields: baseFields,
            additional_fields: expandAdditionalFields(additionalFields || []),
            conditions: filterConditions || [],
            // Add tab filtering if specified
            ...(tabField && tabValue ? { tab_field: tabField, tab_value: tabValue } : {})
          }],
          dispatchers: selectedDispatchers,
          customers: selectedCustomers,
          regions: selectedRegions,
        };

        response = await api.post('/dispatch-dashboard/batch-cards', requestBody, {
          signal: abortController.signal
        });

        // Extract the single card result from the batch response
        const cardResult = response.data.results?.[cardKey];
        if (cardResult) {
          response.data.data = cardResult.data;
          response.data.total = cardResult.total;
          response.data.error = cardResult.error;
        }
      } else {
        // Otherwise, use the dispatch-dashboard orderscurrent filter endpoint
        if (!filterConditions || filterConditions.length === 0) {
          if (!silent) {
            setCardDataLoading(prev => ({ ...prev, [cardKey]: false }));
          }
          delete abortControllersRef.current[cardKey];
          return;
        }

        response = await api.post('/dispatch-dashboard/orderscurrent/filter', {
          conditions: filterConditions,
          view: filterView || 'Orders',
          additional_fields: expandAdditionalFields(additionalFields || []),
          include_closed: includeClosed
        }, {
          signal: abortController.signal
        });
      }

      setCardData(prev => ({ ...prev, [cardKey]: response.data.data || [] }));
      setCardErrors(prev => ({ ...prev, [cardKey]: response.data.error || null }));
      // Derived from the RESPONSE, not the card config, so the header pill always
      // describes the rows on screen rather than a filter edit that hasn't refetched.
      setCardScopeMeta(prev => ({
        ...prev,
        [cardKey]: {
          includeClosed: Boolean(response.data.include_closed),
          truncated: Boolean(response.data.truncated),
          limit: response.data.limit,
        },
      }));
    } catch (error: any) {
      // Don't log or update state if request was cancelled
      if (error.name === 'CanceledError' || error.code === 'ERR_CANCELED') {
        return;
      }
      console.error('Failed to fetch card data:', error);
      setCardData(prev => ({ ...prev, [cardKey]: [] }));
      setCardErrors(prev => ({ ...prev, [cardKey]: 'Couldn’t load this card.' }));
    } finally {
      // Clean up the abort controller
      delete abortControllersRef.current[cardKey];

      if (!silent) {
        setCardDataLoading(prev => ({ ...prev, [cardKey]: false }));
      }
    }
  }, [selectedDispatchers, selectedCustomers, selectedRegions]);

  // Helper to convert tab ID (TanTable format) to tab_value (backend format) for API
  const tabIdToTabValue = useCallback((tabId: string): string => {
    if (tabId === 'past-due') return 'past_due';
    if (tabId === 'today') {
      const today = new Date();
      return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    }
    if (tabId.startsWith('date-')) return tabId.replace('date-', '');
    return tabId;
  }, []);

  // Helper to convert tab_value (backend format) to tab ID (TanTable format)
  const tabValueToTabId = useCallback((tabValue: string): string => {
    if (tabValue === 'past_due') return 'past-due';
    // Check if this is today's date
    const today = new Date();
    const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    if (tabValue === todayStr) return 'today';
    // If it looks like a date, prefix with 'date-'
    if (/^\d{4}-\d{2}-\d{2}$/.test(tabValue)) return `date-${tabValue}`;
    return tabValue;
  }, []);

  // Batch fetch all card data in a single HTTP request
  // This replaces fetchCardsStaggered for bucket-based cards to reduce HTTP connections
  const fetchAllCardData = useCallback(async (
    cards: CardConfig[],
    silent: boolean = false,
    onlyVisible: boolean = true  // Only fetch visible cards by default
  ) => {
    // Filter to only visible cards if requested
    const cardsToFetch = onlyVisible
      ? cards.filter(card => {
          if (visibleCardKeys.size === 0) {
            // Main (default) workspace defaults to no bucket selected: load nothing
            // up front. Counts come from /bucket-counts, so card data isn't needed
            // until the user picks a bucket. Other workspaces have no bucket
            // selector, so an empty selection means show all (unchanged behavior).
            return currentWorkspaceId !== 'default';
          }
          // All buckets selected = show all
          if (visibleCardKeys.size === allCardKeys.length) {
            return true;
          }
          return visibleCardKeys.has(card.key);
        })
      : cards;

    // All cards with a bucket or filter conditions can be batch loaded
    // Non-bucket cards with filters use 'orderscurrent-filter' as a generic bucket
    const batchableCards = cardsToFetch.filter(card =>
      card.bucket || (card.filterConditions && card.filterConditions.length > 0)
    );

    // Skip if no cards to fetch
    if (batchableCards.length === 0) return;

    // If a request is already in progress...
    if (batchCardsAbortControllerRef.current) {
      if (silent) {
        // For silent refreshes (auto-refresh), skip instead of canceling
        // This prevents the 30-second refresh from interrupting a slow load
        return;
      }
      // For user-initiated actions (visibility change, filter change), cancel the old request
      batchCardsAbortControllerRef.current.abort();
    }

    // Create new abort controller for this request
    const abortController = new AbortController();
    batchCardsAbortControllerRef.current = abortController;

    // Set loading state for all batchable cards
    if (!silent) {
      const loadingState = batchableCards.reduce((acc: Record<string, boolean>, c: CardConfig) => ({ ...acc, [c.key]: true }), {});
      setCardDataLoading(prev => ({ ...prev, ...loadingState }));
    }

    try {
      // Build batch request - use 'orderscurrent-filter' for cards without a predefined bucket
      const cardRequests = batchableCards.map((card: CardConfig) => {
        const effectiveBucket = card.bucket || 'orderscurrent-filter';
        return {
          card_key: card.key,
          bucket: effectiveBucket,
          base_fields: getBucketFields(effectiveBucket),
          additional_fields: expandAdditionalFields(card.additionalFields || []),
          conditions: card.filterConditions || [],
          // Custom-filter cards are batched under the synthetic 'orderscurrent-filter'
          // bucket, so this is the path a SAVED closed-inclusive card loads through.
          // Omitting it here would silently reset the card to current scope on load.
          include_closed: Boolean(card.includeClosed)
        };
      });

      const response = await api.post('/dispatch-dashboard/batch-cards', {
        cards: cardRequests,
        dispatchers: selectedDispatchers,
        customers: selectedCustomers,
        regions: selectedRegions
      }, {
        signal: abortController.signal
      });

      // Update state for all cards at once
      if (response.data.results) {
        setCardData(prev => {
          const updated = { ...prev };
          for (const [cardKey, result] of Object.entries(response.data.results)) {
            const typedResult = result as { data?: any[]; total?: number };
            updated[cardKey] = typedResult.data || [];
          }
          return updated;
        });
        setCardErrors(prev => {
          const updated = { ...prev };
          for (const [cardKey, result] of Object.entries(response.data.results)) {
            updated[cardKey] = (result as { error?: string }).error || null;
          }
          return updated;
        });
        // Scope/cap metadata per card, straight from each card's own result. The batch
        // mixes bucket cards (never closed-inclusive) with custom-filter cards, so this
        // has to be per-card rather than per-response.
        setCardScopeMeta(prev => {
          const updated = { ...prev };
          for (const [cardKey, result] of Object.entries(response.data.results)) {
            const r = result as { include_closed?: boolean; truncated?: boolean; limit?: number };
            updated[cardKey] = {
              includeClosed: Boolean(r.include_closed),
              truncated: Boolean(r.truncated),
              limit: r.limit,
            };
          }
          return updated;
        });
      }
    } catch (error: any) {
      if (error.name !== 'CanceledError' && error.code !== 'ERR_CANCELED') {
        console.error('Failed to fetch batch card data:', error);
        // Set empty data + error for all cards on whole-batch failure
        setCardData(prev => {
          const updated = { ...prev };
          batchableCards.forEach((card: CardConfig) => {
            updated[card.key] = [];
          });
          return updated;
        });
        setCardErrors(prev => {
          const updated = { ...prev };
          batchableCards.forEach((card: CardConfig) => {
            updated[card.key] = 'Couldn’t load this card.';
          });
          return updated;
        });
      }
    } finally {
      // Clear abort controller ref if it's still ours (not replaced by a new request)
      if (batchCardsAbortControllerRef.current === abortController) {
        batchCardsAbortControllerRef.current = null;
      }
      if (!silent) {
        const loadingOff = batchableCards.reduce((acc: Record<string, boolean>, c: CardConfig) => ({ ...acc, [c.key]: false }), {});
        setCardDataLoading(prev => ({ ...prev, ...loadingOff }));
      }
    }
  }, [selectedDispatchers, selectedCustomers, selectedRegions, visibleCardKeys, allCardKeys, currentWorkspaceId]);

  // Fetch all table settings in a single batch request
  // Called once during initial load to avoid individual API calls per TanTable
  const fetchAllTableSettings = useCallback(async () => {
    try {
      const [settings, defaults] = await Promise.all([
        userSettingsService.getUserSettingsByPrefix('table_dispatch-card-'),
        // Defaults are best-effort: their absence just means code defaults apply.
        userSettingsService.getDefaultSettingsByPrefix('dispatch-card-').catch(() => ({})),
      ]);
      setTableSettings(settings);
      setCardDefaults(defaults);

      // Seed a published default's additionalFields + grouping into shared cards
      // the user hasn't customized, BEFORE flipping the ready flag — so those
      // columns exist and the grouping applies when TanTable first renders.
      // Existing users (own table settings, or own additional fields) are left
      // untouched; their settings always win. Tab grouping is code-driven and is
      // intentionally NOT seeded here (its past-due/today conventions live in code).
      setCardConfigs(prev => prev.map(card => {
        if (!systemBucketDefaults.has(card.key)) return card;
        const key = `dispatch-card-${card.key}`;
        const hasUserTable = !!settings[`table_${key}`];
        const hasUserAdditional = Array.isArray(card.additionalFields) && card.additionalFields.length > 0;
        const def = (defaults as Record<string, any>)[key];
        if (!def || hasUserTable || hasUserAdditional) return card;
        const patch: Partial<CardConfig> = {};
        if (Array.isArray(def.additionalFields) && def.additionalFields.length > 0) patch.additionalFields = def.additionalFields;
        if (Array.isArray(def.grouping)) patch.grouping = def.grouping;
        return Object.keys(patch).length ? { ...card, ...patch } : card;
      }));

      setTableSettingsLoadFailed(false); // success (incl. empty) clears protected mode
    } catch (error) {
      console.error('Failed to fetch batch table settings:', error);
      setTableSettingsLoadFailed(true); // DB/network failure -> protect & retry
    } finally {
      setTableSettingsLoaded(true);
    }
  }, [systemBucketDefaults]);

  // Keep tableSettings cache in sync when TanTable saves settings
  const handleTableSettingsSaved = useCallback((settingsKey: string, settings: any) => {
    setTableSettings(prev => ({ ...prev, [settingsKey]: settings }));
  }, []);

  // Publish an admin-edited default layout for a shared bucket. TanTable hands us
  // a draft keyed by column id; we map the field list back to additionalFields
  // (db field names), persist the stored shape, and update the local cache so the
  // change is reflected without a refetch.
  // TanTable composes the full default blob; we just persist + cache it.
  const handleSaveDefaults = useCallback(async (settingsKey: string, stored: any) => {
    await userSettingsService.saveDefaultSetting(settingsKey, stored);
    setCardDefaults(prev => ({ ...prev, [settingsKey]: stored }));
  }, []);

  // Grouping field(s) a bucket requires — these can't be removed from the default
  // (admins may layer more grouping on top). Mirrors the code-level default tab
  // grouping applied in the card renderer.
  const getMandatoryGroupingFields = useCallback((bucket?: string): string[] => {
    switch (bucket) {
      case 'per-diem-not-dispatched': return ['perdiemstartdate'];
      case 'cutoff-not-dispatched':
      case 'cutoff-not-terminated': return ['cutoffdatetime'];
      case 'dispatches-not-accepted': return ['leg_destapptdate'];
      case 'scheduled-not-dispatched': return ['d_legmiles_band'];
      default: return [];
    }
  }, []);

  // Refresh all cards that have filters or buckets (silent background refresh)
  // Only refresh cards in the current workspace, skip collapsed cards
  const refreshAllCards = useCallback(async () => {
    const cardsToRefresh = cardConfigsRef.current.filter((card: CardConfig) => {
      if (collapsedRef.current[card.key]) return false;
      return card.workspaceId === currentWorkspaceId &&
             (card.bucket || (card.filterConditions && card.filterConditions.length > 0));
    });

    await fetchAllCardData(cardsToRefresh, true); // silent=true, single batch request
  }, [currentWorkspaceId, fetchAllCardData]);

  // Trigger re-fetch of all cards when global filters change
  useEffect(() => {
    // Skip initial mount
    if (!configsLoaded) return;
    // Boot-time staged filter restores are not user changes — the workspace
    // effect owns the (gated) initial load. Not in deps on purpose: gate-open
    // must not fire this one too.
    if (globalFiltersLoading || !localFiltersReady) return;

    // Fetch all cards with loading indicators (not silent)
    const cardsToRefresh = cardConfigsRef.current.filter((card: CardConfig) => {
      if (collapsedRef.current[card.key]) return false;
      return card.workspaceId === currentWorkspaceId &&
             (card.bucket || (card.filterConditions && card.filterConditions.length > 0));
    });
    fetchAllCardData(cardsToRefresh, false); // silent=false to show loading, single batch request
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDispatchers, selectedCustomers, selectedRegions]);

  // Trigger re-fetch when card visibility changes (user selects/deselects a bucket)
  useEffect(() => {
    // Skip if configs not loaded yet
    if (!configsLoaded) return;

    // Fetch only the visible cards
    const cardsToRefresh = cardConfigsRef.current.filter((card: CardConfig) => {
      if (collapsedRef.current[card.key]) return false;
      return card.workspaceId === currentWorkspaceId &&
             (card.bucket || (card.filterConditions && card.filterConditions.length > 0));
    });
    fetchAllCardData(cardsToRefresh, false, true); // silent=false, onlyVisible=true
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleCardKeys]);

  // Prune pending Not Validated Chassis edits down to rows still present in the
  // fetched data. A page-level (global) filter is applied server-side and
  // replaces cardData, so edits for filtered-out rows get dropped here — they
  // leave the "to validate" / "missing comment" tallies and stop blocking Save
  // for rows the user can no longer see or act on. Table-level column filters
  // are client-side and don't change cardData, so they intentionally leave
  // pending edits alone (the user can adjust those to find and fix anything).
  const chassisCardData = cardData['not-validated-chassis'];
  useEffect(() => {
    if (!chassisCardData) return;
    setChassisEdits((prev) => {
      const keys = Object.keys(prev);
      if (keys.length === 0) return prev;
      const present = new Set(chassisCardData.map((r: any) => String(r?.pronumber ?? '')));
      const next: Record<string, ChassisRowEdit> = {};
      let changed = false;
      for (const k of keys) {
        if (present.has(k)) next[k] = prev[k];
        else changed = true;
      }
      return changed ? next : prev;
    });
  }, [chassisCardData]);

  // Same pruning for the Declined/Cancelled Legs card (keyed by dispatch_recno).
  const declinedCardData = cardData['declined-cancelled-legs'];
  useEffect(() => {
    if (!declinedCardData) return;
    setDeclinedEdits((prev) => pruneDeclinedEdits(prev, declinedCardData));
  }, [declinedCardData]);

  // Trigger re-fetch when collapse state changes (user expands/collapses cards)
  // This ensures the batch-cards request includes the correct set of visible cards
  const prevCollapsedRef = useRef<{ [key: string]: boolean }>({});
  useEffect(() => {
    // Skip if configs not loaded yet
    if (!configsLoaded) return;

    // Check if any cards were expanded (collapsed -> not collapsed)
    const expandedCards: string[] = [];
    Object.keys(collapsed).forEach(key => {
      if (prevCollapsedRef.current[key] === true && collapsed[key] === false) {
        expandedCards.push(key);
      }
    });

    // Update the previous collapsed state
    prevCollapsedRef.current = { ...collapsed };

    // If no cards were expanded, don't re-fetch (collapsing doesn't need a re-fetch)
    if (expandedCards.length === 0) return;

    // Cancel any in-progress batch request and fetch the updated set of visible cards
    if (batchCardsAbortControllerRef.current) {
      batchCardsAbortControllerRef.current.abort();
      batchCardsAbortControllerRef.current = null;
    }

    // Fetch data for all non-collapsed cards in the current workspace
    const cardsToRefresh = cardConfigsRef.current.filter((card: CardConfig) => {
      if (collapsed[card.key]) return false;
      return card.workspaceId === currentWorkspaceId &&
             (card.bucket || (card.filterConditions && card.filterConditions.length > 0));
    });
    fetchAllCardData(cardsToRefresh, false, true); // silent=false, onlyVisible=true
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collapsed]);

  // Tab optimization disabled - loading full card data instead
  // const prevSelectedTabsRef = useRef<{ [cardKey: string]: string }>({});

  // Trigger immediate refresh when order details closes
  const triggerImmediateRefresh = useCallback(() => {
    refreshAllCards();
    fetchBucketCounts(true); // silent = true

    // Reset the countdown timer to 30 seconds
    countdownRef.current = REFRESH_INTERVAL;
  }, [refreshAllCards, fetchBucketCounts]);

  // Memoized callbacks for TanTable props - prevents unnecessary re-renders
  const handleEditFilters = useCallback((cardKey: string) => {
    // System buckets are read-only — users duplicate them to customize, they don't edit
    // them in place. The cog is hidden for these, but guard here too so there's no path in.
    if (systemBucketDefaults.has(cardKey)) return;
    setEditingCardFiltersKey(cardKey);
  }, [systemBucketDefaults]);

  const handleGroupingChange = useCallback((cardKey: string, grouping: string[]) => {
    setCardConfigs(prev => prev.map(card =>
      card.key === cardKey ? { ...card, grouping } : card
    ));
  }, []);

  const handleTabGroupingChange = useCallback((cardKey: string, tabGrouping: string) => {
    setCardConfigs(prev => prev.map(card =>
      card.key === cardKey ? { ...card, tabGrouping } : card
    ));
  }, []);

  const handleVisibleColumnsChange = useCallback((cardKey: string, visibleColumns: string[]) => {
    setCardConfigs(prev => prev.map(card =>
      card.key === cardKey ? { ...card, visibleColumns } : card
    ));
  }, []);

  // Handle additional fields change - updates card config and triggers refetch
  const handleAdditionalFieldsChange = useCallback((cardKey: string, additionalFields: string[]) => {
    // Update the card config with new additional fields, and PERSIST it. Column
    // show/hide/reorder + grouping are saved by TanTable itself (via its
    // settingsKey), but additionalFields is not part of TanTable's settings
    // payload (buildSettingsData) — so a user-added column only survives reload
    // if we save it into the dashboard layout ourselves. Without this, added
    // columns on custom cards were lost on refresh (same class of gap fixed for
    // the LC accounting drills; here the card layout already carries the field,
    // it just wasn't being saved on change). Debounced.
    setCardConfigs(prev => {
      const updated = prev.map(card =>
        card.key === cardKey ? { ...card, additionalFields } : card
      );
      saveLayoutRef.current?.(updated, false);
      return updated;
    });

    // Find the card and trigger a refetch with the new additional fields
    const card = cardConfigs.find(c => c.key === cardKey);
    if (card && (card.bucket || (card.filterConditions && card.filterConditions.length > 0))) {
      fetchCardData(
        cardKey,
        card.filterConditions || [],
        card.filterView || 'Orders',
        card.bucket,
        false,
        additionalFields
      );
    }
  }, [cardConfigs, fetchCardData]);

  const handleDuplicate = useCallback((cardConfig: CardConfig) => {
    setDuplicateCardConfig(cardConfig);
  }, []);

  // Save handler for the Not Validated Chassis bucket. Posts only rows the
  // user has changed away from the implicit 'N' default. After save, clears
  // local edits and refetches the card data + bucket counts.
  const handleSaveChassisValidations = useCallback(async () => {
    const cardKey = 'not-validated-chassis';
    const card = cardConfigs.find((c) => c.key === cardKey);
    if (!card) return;

    const rows = Object.entries(chassisEdits)
      .filter(([, edit]) => edit.value !== 'N')
      .map(([pronumber, edit]) => ({
        pronumber: Number(pronumber),
        isvalidchassis: edit.value,
        comment: edit.comment,
      }));

    if (rows.length === 0) return;

    setChassisSaving(true);
    try {
      const res = await api.post('/dispatch-dashboard/validate-chassis', { rows });
      const { updated_y = 0, updated_blank = 0, errors = [] } = res.data || {};
      if (errors.length > 0) {
        showToast({
          type: 'warning',
          message: `Saved with ${errors.length} error(s). ${updated_y} validated, ${updated_blank} cleared.`,
        });
      } else {
        showToast({
          type: 'success',
          message: `${updated_y} validated, ${updated_blank} cleared.`,
        });
      }
      setChassisEdits({});
      fetchCardData(cardKey, card.filterConditions || [], card.filterView || 'Orders', card.bucket, false);
      fetchBucketCounts(true);
    } catch (err: any) {
      console.error('validate-chassis save failed:', err);
      showToast({
        type: 'error',
        message: err?.response?.data?.detail || 'Failed to save chassis validations.',
      });
    } finally {
      setChassisSaving(false);
    }
  }, [cardConfigs, chassisEdits, fetchCardData, fetchBucketCounts, showToast]);

  // Save handler for the Declined/Cancelled Legs bucket. Posts only ticked rows
  // (comment optional). After save, clears local edits and refetches the card
  // data + bucket counts.
  const handleSaveDeclinedResolves = useCallback(async () => {
    const cardKey = 'declined-cancelled-legs';
    const card = cardConfigs.find((c) => c.key === cardKey);
    if (!card) return;

    const rows = declinedRowsToSave(declinedEdits, cardData[cardKey] || []);
    if (rows.length === 0) return;

    setDeclinedSaving(true);
    try {
      const res = await api.post('/dispatch-dashboard/resolve-declined', { rows });
      const { resolved = 0, skipped = 0 } = res.data || {};
      showToast({
        type: skipped ? 'warning' : 'success',
        message: `${resolved} resolved${skipped ? `, ${skipped} already resolved` : ''}.`,
      });
      setDeclinedEdits({});
      fetchCardData(cardKey, card.filterConditions || [], card.filterView || 'Orders', card.bucket, false);
      fetchBucketCounts(true);
    } catch (err: any) {
      console.error('resolve-declined save failed:', err);
      showToast({
        type: 'error',
        message: err?.response?.data?.detail || 'Failed to resolve declined legs.',
      });
    } finally {
      setDeclinedSaving(false);
    }
  }, [cardConfigs, declinedEdits, cardData, fetchCardData, fetchBucketCounts, showToast]);

  const handleMultiTabChange = useCallback((cardKey: string, tabIds: string[]) => {
    setCardActiveTabsMulti(prev => ({ ...prev, [cardKey]: tabIds }));
  }, []);

  // Check if any modal is open (to pause auto-refresh)
  const isAnyModalOpen = isFirstCardModalOpen || !!editingCardFiltersKey || !!duplicateCardConfig || showNewWorkspaceModal || !!selectedProNumber || !!selectedTicket || ticketLoading || !!driverPayAddition;

  // Set up auto-refresh interval (every 30 seconds) - pause when modals are open
  // Refreshes both card data (for expanded cards) and bucket counts (for StatsGrid)
  const REFRESH_INTERVAL = 30; // seconds
  const countdownRef = React.useRef(REFRESH_INTERVAL);

  useVisibilityAwareInterval(
    () => { refreshAllCards(); fetchBucketCounts(true); },
    initialLoadComplete && !isAnyModalOpen ? REFRESH_INTERVAL * 1000 : null
  );

  // Push: when another user dispatches/terminates, refresh without waiting out
  // the 30s tick. The interval above stays on — V1 CMS, EDI, the trace webhooks
  // and the ordersall -> orderscurrent sync change bucket membership too and
  // emit nothing, so the poll remains the only path that catches those.
  // Timestamp of the last push-driven refresh, for the header chip. Lets you
  // tell "push is quiet because nothing changed" from "push is broken".
  const [lastPushAt, setLastPushAt] = useState<number | null>(null);
  const [pushTick, setPushTick] = useState(0);

  useDispatchBoardPush({
    enabled: initialLoadComplete,
    suppressed: isAnyModalOpen,
    onUpdate: () => {
      setLastPushAt(Date.now());
      triggerImmediateRefresh();
    },
  });

  // Re-render once a minute so the chip's relative time doesn't go stale.
  useVisibilityAwareInterval(() => setPushTick(t => t + 1), lastPushAt ? 60000 : null, false);

  const lastPushLabel = useMemo(() => {
    void pushTick; // recomputed on tick
    if (lastPushAt === null) return null;
    const secs = Math.round((Date.now() - lastPushAt) / 1000);
    if (secs < 60) return `${secs}s ago`;
    const mins = Math.round(secs / 60);
    return mins < 60 ? `${mins}m ago` : `${Math.round(mins / 60)}h ago`;
  }, [lastPushAt, pushTick]);

  // Load card configurations from database on mount
  useEffect(() => {
    const loadCardConfigs = async () => {
      try {
        const layout = await userSettingsService.getDashboardLayoutNew('dispatch');
        const defaultCards = getDefaultCardConfigs();

        // Note: We no longer reset on version mismatch to preserve user data.
        // If migration is needed for a new version, it should be done incrementally below.
        if (layout && layout.cardConfigs && layout.cardConfigs.length > 0) {
          // Get the set of default locked card keys for comparison
          const defaultLockedKeys = new Set(defaultCards.filter(dc => dc.locked).map(dc => dc.key));

          // Buckets that used to live on the Dispatch Dashboard but moved to
          // the Yard Reports page. We prune these out of saved layouts so
          // both the original locked cards AND any user-duplicated cards
          // built on top of them disappear cleanly. Idempotent — old layouts
          // get cleaned on first load and the next save persists the prune.
          const REMOVED_BUCKETS = new Set([
            'terminated-on-yard',
            'yard-chassis-without-equipment',
            'leg-yard-mismatch',
            'missing-containers',
          ]);

          // Migrate and validate loaded configs (including locked cards)
          const migrated = layout.cardConfigs
            .filter((card: any) => card.key !== 'testCard')  // Only exclude testCard
            .filter((card: any) => !card.bucket || !REMOVED_BUCKETS.has(card.bucket))
            .map((card: any) => {
              const migratedCard: any = { ...card };
              if (card.key === 'past-due-no-outgate' && card.order === 5.5) {
                migratedCard.order = 6;
              }
              if (typeof card.column === 'string') {
                migratedCard.column = parseInt(card.column) || 0;
              }
              if (!card.span) {
                migratedCard.span = 1;
              }
              // Ensure workspaceId is set (default to 'default' for old configs)
              if (!card.workspaceId) {
                migratedCard.workspaceId = 'default';
              }
              // Preserve the saved locked state (user may have toggled it).
              // For cards with no saved locked field, default to true if it's a default card, false otherwise.
              if (typeof card.locked === 'boolean') {
                migratedCard.locked = card.locked;
              } else {
                migratedCard.locked = defaultLockedKeys.has(card.key);
              }
              // Heal legacy data: older builds persisted the collapsed visual
              // height (h=1) into gsH, which left cards tiny after returning to
              // the page. Restore the expanded height when we find it.
              if (typeof card.gsH === 'number' && card.gsH < 2) {
                if (typeof card.expandedGsH === 'number' && card.expandedGsH >= 2) {
                  migratedCard.gsH = card.expandedGsH;
                } else {
                  migratedCard.gsH = 6;
                }
              }
              // Scrub overrides on system buckets. Before in-place editing was
              // removed, users could open the filter cog and change a locked
              // bucket's query, title, and stat button; those edits got persisted
              // into their saved layout. Force every field the cog could touch back
              // to the canonical code definition so these buckets are identical for
              // everyone — same query (bucket / conditions / view), same title, same
              // stat button. Presentation the user legitimately controls (visible
              // columns, grouping, height, grid position) is left intact.
              // Idempotent: clean layouts are unchanged, the next save persists this.
              const systemDefault = systemBucketDefaults.get(card.key);
              if (systemDefault) {
                migratedCard.bucket = systemDefault.bucket;
                migratedCard.filterConditions = systemDefault.filterConditions ?? [];
                migratedCard.filterView = systemDefault.filterView;
                migratedCard.title = systemDefault.title;
                migratedCard.showStatButton = systemDefault.showStatButton;
                migratedCard.statButtonLabel = systemDefault.statButtonLabel;
                migratedCard.statButtonCountField = systemDefault.statButtonCountField;
                migratedCard.isShared = systemDefault.isShared;
              }
              return migratedCard;
            });

          // Check for missing locked cards and add them with default positioning
          // (defaultCards is already defined above)

          // Create a set of ALL saved card keys (not just locked ones)
          const savedCardKeys = new Set(migrated.map((c: any) => c.key));

          // Only add locked cards that don't exist at all in the saved layout
          const missingLockedCards = defaultCards.filter(dc => dc.locked && !savedCardKeys.has(dc.key));

          // Combine all cards (missing locked cards + existing saved cards)
          const allCards = missingLockedCards.length > 0
            ? [...missingLockedCards, ...migrated]
            : migrated;

          setCardConfigs(allCards);

          // Load workspaces if saved
          if (layout.workspaces && layout.workspaces.length > 0) {
            // Ensure all workspaces have a columnCount (default to 2 if missing for backwards compatibility)
            // Also ensure default workspace is named "Main"
            const workspacesWithColumnCount = layout.workspaces.map((ws: Workspace) => ({
              ...ws,
              columnCount: ws.columnCount || 2,
              name: ws.id === 'default' ? 'Main' : ws.name
            }));

            // Add any missing default workspaces (e.g., new workspaces added in code updates)
            const defaultWorkspaces = getDefaultWorkspaces();
            const savedWorkspaceIds = new Set(workspacesWithColumnCount.map((ws: Workspace) => ws.id));
            const missingWorkspaces = defaultWorkspaces.filter(ws => !savedWorkspaceIds.has(ws.id));
            const mergedWorkspaces = [...workspacesWithColumnCount, ...missingWorkspaces];

            setWorkspaces(mergedWorkspaces);

            // Restore the active board: this tab's own choice first, then the
            // server-saved one for a brand-new tab, then 'default'. The saved
            // layout is one shared per-user record, so without the per-tab value
            // a second app tab's board became this tab's board on every mount.
            // The Driver Board is a pinned code-defined tab, so it never appears in
            // the saved workspaces array — accept it explicitly or a reload would
            // always dump the user back on Main. A revoked grant is handled by the
            // permission guard effect, which bounces them to 'default'.
            const restoredWorkspaceId = resolveWorkspaceId({
              tabId: readTabWorkspaceId('dispatch'),
              savedId: layout.currentWorkspaceId,
              available: [...mergedWorkspaces.map((ws: Workspace) => ws.id), DRIVER_BOARD_TAB_ID],
            });
            setCurrentWorkspaceId(restoredWorkspaceId);

            // Set initial columnCount from the restored active workspace
            const activeWorkspace = workspacesWithColumnCount.find((ws: Workspace) => ws.id === restoredWorkspaceId) || workspacesWithColumnCount[0];
            if (activeWorkspace) {
              setColumnCount(activeWorkspace.columnCount);
            }
          } else {
            const defaults = getDefaultWorkspaces();
            setWorkspaces(defaults);
            setCurrentWorkspaceId(resolveWorkspaceId({
              tabId: readTabWorkspaceId('dispatch'),
              savedId: layout.currentWorkspaceId,
              available: [...defaults.map(ws => ws.id), DRIVER_BOARD_TAB_ID],
            }));
            // Load global column count as fallback for backwards compatibility
            if (layout.columnCount) {
              setColumnCount(layout.columnCount);
            }
          }

          // Load collapsed state if saved
          if (layout.collapsed) {
            setCollapsed(layout.collapsed);
          }
          // Load visible card keys (selected bucket) if saved
          if (layout.visibleCardKeys && Array.isArray(layout.visibleCardKeys)) {
            setVisibleCardKeys(new Set(layout.visibleCardKeys));
          }
          // Load global filters if saved
          if (layout.selectedDispatchers && Array.isArray(layout.selectedDispatchers)) {
            setSelectedDispatchers(layout.selectedDispatchers);
          }
          if (layout.selectedCustomers && Array.isArray(layout.selectedCustomers)) {
            setSelectedCustomers(layout.selectedCustomers);
          }
          if (layout.selectedRegions && Array.isArray(layout.selectedRegions)) {
            setSelectedRegions(layout.selectedRegions);
          }
          // Load workspace-specific visible cards if saved
          if (layout.workspaceVisibleCards && typeof layout.workspaceVisibleCards === 'object') {
            setWorkspaceVisibleCards(layout.workspaceVisibleCards);
          }
          // Note: Data fetching is handled by the workspace switch effect when configsLoaded becomes true
        } else {
          // No saved layout, use defaults (which includes locked cards)
          setCardConfigs(defaultCards);
          setWorkspaces(getDefaultWorkspaces());
          // Note: Data fetching is handled by the workspace switch effect when configsLoaded becomes true
        }
      } catch (error) {
        console.error('Failed to load dashboard layout from database:', error);
        // Keep default configs on error
        const defaultCards = getDefaultCardConfigs();
        setCardConfigs(defaultCards);
        setWorkspaces(getDefaultWorkspaces());
        // Note: Data fetching is handled by the workspace switch effect when configsLoaded becomes true
      } finally {
        setConfigsLoaded(true);
      }
    };

    loadCardConfigs();
    // Fetch all table settings in parallel (batch request instead of per-table calls)
    fetchAllTableSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Retry the batch settings fetch on backoff while it is in a failed state.
  // On success, fetchAllTableSettings clears tableSettingsLoadFailed, which flows down
  // to each TanTable (settingsLoadFailed=false + fresh initialSettings) to re-apply + unblock saving.
  useEffect(() => {
    if (!tableSettingsLoadFailed) return;
    let cancelled = false;
    let attempt = 0;
    let timer: ReturnType<typeof setTimeout>;
    const schedule = () => {
      const delay = Math.min(15000 * 2 ** attempt, 60000);
      timer = setTimeout(() => {
        attempt += 1;
        if (!cancelled) fetchAllTableSettings(); // clears the flag on success
      }, delay);
    };
    schedule();
    return () => { cancelled = true; clearTimeout(timer); };
  }, [tableSettingsLoadFailed, fetchAllTableSettings]);

  // Mark initial load as complete after configs are loaded and a short delay for data fetching
  useEffect(() => {
    if (!configsLoaded) return;

    // Wait a bit for initial card data to start loading, then enable refresh
    const timer = setTimeout(() => {
      setInitialLoadComplete(true);
    }, 2000); // 2 second delay after configs load

    return () => clearTimeout(timer);
  }, [configsLoaded]);

  // Remember this tab's board. The layout record (which carries currentWorkspaceId)
  // is shared by every open app tab and rewritten in full on any change, so without
  // a per-tab copy another tab's board wins the next time this page mounts.
  useEffect(() => {
    if (!configsLoaded) return;
    writeTabWorkspaceId('dispatch', currentWorkspaceId);
  }, [currentWorkspaceId, configsLoaded]);

  // Sync columnCount with current workspace when switching workspaces
  useEffect(() => {
    if (!configsLoaded) return;

    const currentWorkspace = workspaces.find(ws => ws.id === currentWorkspaceId);
    if (currentWorkspace) {
      // Set flag to prevent the save effect from overwriting the workspace's stored columnCount
      isRestoringColumnCountRef.current = true;
      setColumnCount(currentWorkspace.columnCount);
      // Clear flag after React has processed the state update
      setTimeout(() => {
        isRestoringColumnCountRef.current = false;
      }, 0);
    }
    // Only fire when workspace changes, not when columnCount changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentWorkspaceId, configsLoaded]);

  // Check if all loading is complete (matches the loading condition in render)
  const isFullyLoaded = configsLoaded && !fieldDefinitionsLoading && (fieldDefinitions.length > 0 || fieldDefinitionsError);

  // Initialize/reinitialize GridStack when workspace changes or all loading is complete
  useEffect(() => {
    // Don't initialize until everything is loaded (grid-stack div won't exist during loading screen)
    if (!isFullyLoaded) return;

    // Destroy existing grid
    if (gridInstanceRef.current) {
      gridInstanceRef.current.destroy(false);
      gridInstanceRef.current = null;
    }

    // Mark that we're initializing (to skip change events during init)
    isGridInitializing.current = true;

    // Delay to ensure DOM is ready - ref check moved inside timeout because
    // React may not have rendered the grid-stack div yet when loading completes
    const timer = setTimeout(() => {
      if (!gridRef.current) return;

      const grid = GridStack.init({
        column: 12,
        cellHeight: 80,
        margin: 4,
        float: false,
        animate: true,
        disableDrag: false,
        disableResize: false,
        resizable: {
          handles: 'se',
        },
      }, gridRef.current);

      gridInstanceRef.current = grid;

      // Listen for changes and update cardConfigs with new positions
      grid.on('change', () => {
        // Skip change events during initialization (GridStack fires change on init)
        if (isGridInitializing.current) {
          return;
        }

        const nodes = grid.getGridItems();

        // Build a map of card positions from GridStack
        const positionMap: { [id: string]: { x: number; y: number; w: number; h: number } } = {};
        nodes.forEach((el) => {
          const node = el.gridstackNode;
          // Also read from DOM attributes as fallback
          const domId = el.getAttribute('gs-id');
          const domX = parseInt(el.getAttribute('gs-x') || '0', 10);
          const domY = parseInt(el.getAttribute('gs-y') || '0', 10);
          const domW = parseInt(el.getAttribute('gs-w') || '4', 10);
          const domH = parseInt(el.getAttribute('gs-h') || '5', 10);

          const id = node?.id || domId;
          if (id) {
            // Prefer gridstackNode values if they look valid (w >= 1), otherwise use DOM
            const useNode = node && node.w && node.w >= 1;
            const w = useNode ? (node.w ?? domW) : domW;
            const h = useNode ? (node.h ?? domH) : domH;

            // Skip saving if w or h are invalid (less than 1)
            if (w < 1 || h < 1) {
              return; // Don't add to positionMap - will keep existing valid values
            }

            positionMap[id] = {
              x: useNode ? (node.x ?? domX) : domX,
              y: useNode ? (node.y ?? domY) : domY,
              w,
              h,
            };
          }
        });

        // Update cardConfigs with GridStack positions directly
        // Only update cards that are in the positionMap (visible in current grid)
        setCardConfigs(prev => {
          const updated = prev.map(card => {
            const pos = positionMap[card.key];
            if (!pos) return card; // Card not in current grid, keep unchanged

            // h === 1 is the collapsed visual state — don't persist it as the
            // card's expanded height. Keep the previous gsH so the next expand
            // restores the user's chosen size.
            const nextH = pos.h === 1 ? (card.gsH ?? pos.h) : pos.h;

            // Store GridStack positions directly for exact persistence
            return {
              ...card,
              gsX: pos.x,
              gsY: pos.y,
              gsW: pos.w,
              gsH: nextH,
            };
          });

          // Persist the new positions/sizes (debounced) so a bare drag/resize
          // sticks across reloads — not only when another action later triggers
          // a save. Matches the LC dashboard. Routed through saveLayoutRef since
          // saveLayout is defined below this grid-init effect (TDZ).
          saveLayoutRef.current?.(updated);
          return updated;
        });
      });

      // Apply lock state to widgets (locked cards can't be moved or resized)
      cardConfigsRef.current.forEach(card => {
        if (card.locked && gridRef.current) {
          const el = gridRef.current.querySelector(`[gs-id="${card.key}"]`);
          if (el) {
            grid.update(el as HTMLElement, { noMove: true, noResize: true });
          }
        }
      });

      // gsH always holds the expanded height. Visually collapse widgets whose
      // saved state is collapsed by shrinking them to h=1 (header only).
      cardConfigsRef.current.forEach(card => {
        if (collapsedRef.current[card.key] && gridRef.current) {
          const el = gridRef.current.querySelector(`[gs-id="${card.key}"]`);
          if (el) {
            grid.update(el as HTMLElement, { h: 1 });
          }
        }
      });

      // After a short delay, allow change events (initialization is complete)
      setTimeout(() => {
        isGridInitializing.current = false;
      }, 200);
    }, 100);

    return () => {
      clearTimeout(timer);
      // Clear any pending save to prevent stale saves when switching workspaces
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
        saveTimeoutRef.current = null;
      }
      if (gridInstanceRef.current) {
        gridInstanceRef.current.destroy(false);
        gridInstanceRef.current = null;
      }
    };
  }, [currentWorkspaceId, isFullyLoaded]);

  // Track card keys to detect when new cards are added
  const prevCardKeysRef = useRef<Set<string>>(new Set());

  // Handle new cards being added - use GridStack's makeWidget API
  useEffect(() => {
    if (!gridInstanceRef.current || !isFullyLoaded) return;

    const currentKeys = new Set(currentGridItems.map(item => item.id));
    const prevKeys = prevCardKeysRef.current;

    // Find newly added cards
    const newKeys = [...currentKeys].filter(key => !prevKeys.has(key));

    if (newKeys.length > 0) {
      // Give React time to render the new DOM elements, then make them widgets
      setTimeout(() => {
        if (!gridInstanceRef.current) return;

        newKeys.forEach(key => {
          const element = document.querySelector(`[gs-id="${key}"]`) as HTMLElement;
          if (element && !element.classList.contains('ui-draggable')) {
            gridInstanceRef.current?.makeWidget(element);
          }
        });
      }, 50);
    }

    prevCardKeysRef.current = currentKeys;
  }, [currentGridItems, isFullyLoaded]);

  // Reset initial load state and fetch fresh data when switching workspaces
  // Track previous workspace to detect actual workspace changes (not initial mount)
  const prevWorkspaceIdRef = React.useRef<string | null>(null);

  useEffect(() => {
    if (!configsLoaded) return;
    // Initial mount also waits for the filter restores so the first batch-cards
    // request carries the user's real filters (prevWorkspaceIdRef stays null
    // until we proceed, so this still counts as the initial mount later).
    if (globalFiltersLoading || !localFiltersReady) return;

    // Track if this is initial mount
    const isInitialMount = prevWorkspaceIdRef.current === null;

    // Only proceed if workspace changed OR this is initial mount
    if (!isInitialMount && prevWorkspaceIdRef.current === currentWorkspaceId) {
      return;
    }

    const previousWorkspaceId = prevWorkspaceIdRef.current;

    // Only cancel existing requests on workspace SWITCH, not initial mount
    if (!isInitialMount && previousWorkspaceId) {
      // Save current layout immediately before switching (preserves grouping, filters, etc.)
      const currentCards = cardConfigsRef.current;
      userSettingsService.saveDashboardLayoutNew('dispatch', {
        cardConfigs: currentCards,
        columnCount: saveStateRef.current.columnCount,
        workspaces: saveStateRef.current.workspaces,
        collapsed: saveStateRef.current.collapsed,
        currentWorkspaceId: saveStateRef.current.currentWorkspaceId,
        visibleCardKeys: saveStateRef.current.visibleCardKeys,
        workspaceVisibleCards: {
          ...saveStateRef.current.workspaceVisibleCards,
          [previousWorkspaceId]: Array.from(visibleCardKeys)
        },
        selectedDispatchers: saveStateRef.current.selectedDispatchers,
        selectedCustomers: saveStateRef.current.selectedCustomers,
        selectedRegions: saveStateRef.current.selectedRegions,
        version: CURRENT_VERSION
      }).catch(err => console.error('Failed to save layout on workspace switch:', err));

      // Save current visible card keys for the previous workspace before switching
      setWorkspaceVisibleCards(prev => ({
        ...prev,
        [previousWorkspaceId]: Array.from(visibleCardKeys)
      }));

      // Cancel card data requests
      Object.keys(abortControllersRef.current).forEach(cardKey => {
        abortControllersRef.current[cardKey]?.abort();
        delete abortControllersRef.current[cardKey];
      });

      // Cancel bucket counts request
      if (bucketCountsAbortControllerRef.current) {
        bucketCountsAbortControllerRef.current.abort();
        bucketCountsAbortControllerRef.current = null;
      }

      // Restore visible card keys for the new workspace (or clear if none saved)
      setWorkspaceVisibleCards(prev => {
        const savedKeys = prev[currentWorkspaceId];
        if (savedKeys && savedKeys.length > 0) {
          setVisibleCardKeys(new Set(savedKeys));
        } else {
          setVisibleCardKeys(new Set());
        }
        return prev;
      });
    }

    // Update the ref for next comparison
    prevWorkspaceIdRef.current = currentWorkspaceId;

    // Reset initial load state to prevent background refresh until fresh data is loaded
    setInitialLoadComplete(false);

    // Use cardConfigsRef to get the latest configs (handles race condition when card is duplicated just before workspace switch)
    const latestCardConfigs = cardConfigsRef.current;

    // Get all card keys for the current workspace
    const workspaceCardKeys = latestCardConfigs
      .filter(card => card.workspaceId === currentWorkspaceId)
      .map(card => card.key);

    // Get card keys that will actually be loaded (non-collapsed)
    const cardsToLoadKeys = latestCardConfigs
      .filter(card => card.workspaceId === currentWorkspaceId && !collapsedRef.current[card.key])
      .map(card => card.key);

    // MEMORY OPTIMIZATION: Clear data from cards not in the current workspace
    // This prevents holding onto massive datasets from other workspaces
    setCardData(prev => {
      const newData: { [key: string]: any[] } = {};
      workspaceCardKeys.forEach(key => {
        if (prev[key]) {
          newData[key] = prev[key];
        }
      });
      return newData;
    });

    // The Main (default) workspace loads data only for the bucket the user selects.
    // Skip pre-marking cards as loading there; fetchAllCardData sets/clears loading
    // for the selected card itself, so doing it here would leave stuck spinners on
    // the cards that are never fetched while no bucket is selected.
    const isDefaultWorkspace = currentWorkspaceId === 'default';

    // Only set loading state for non-collapsed cards that will be fetched
    if (!isDefaultWorkspace) {
      setCardDataLoading(prev => {
        const updated = { ...prev };
        cardsToLoadKeys.forEach(key => {
          updated[key] = true;
        });
        return updated;
      });
    }

    // Fetch fresh data for all cards in the current workspace (skip collapsed cards).
    // On the Main workspace this is filtered down by fetchAllCardData to just the
    // selected bucket (nothing when no bucket is selected).
    const cardsToLoad = latestCardConfigs.filter((card: CardConfig) =>
      card.workspaceId === currentWorkspaceId &&
      !collapsedRef.current[card.key] &&
      (card.bucket || (card.filterConditions && card.filterConditions.length > 0))
    );
    fetchAllCardData(cardsToLoad, false); // single batch request
    // Set initial load complete after a brief delay for the batch request to complete
    if (cardsToLoad.length === 0) {
      setInitialLoadComplete(true);
    } else {
      // Give batch request time to complete (single request is much faster than staggered)
      setTimeout(() => setInitialLoadComplete(true), 500);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentWorkspaceId, configsLoaded, globalFiltersLoading, localFiltersReady]);

  // Cleanup: Cancel all pending requests on unmount
  useEffect(() => {
    return () => {
      // Cancel card data requests
      Object.keys(abortControllersRef.current).forEach(cardKey => {
        abortControllersRef.current[cardKey]?.abort();
      });

      // Cancel bucket counts request
      if (bucketCountsAbortControllerRef.current) {
        bucketCountsAbortControllerRef.current.abort();
      }
    };
  }, []);

  // Update workspace columnCount when layout mode changes (user manually changes columns)
  useEffect(() => {
    if (!configsLoaded) return;

    // Skip if we're restoring columnCount from a workspace switch
    // This prevents overwriting the workspace's stored columnCount with the previous workspace's value
    if (isRestoringColumnCountRef.current) return;

    setWorkspaces(prev => {
      const updated = prev.map(ws =>
        ws.id === currentWorkspaceId
          ? { ...ws, columnCount }
          : ws
      );
      return updated;
    });

    // Wrap cards that are now out of bounds to available columns
    setCardConfigs(prev => prev.map(card => {
      if (card.workspaceId === currentWorkspaceId && card.column >= columnCount) {
        return { ...card, column: card.column % columnCount };
      }
      return card;
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnCount, configsLoaded, currentWorkspaceId]);

  // Drag state
  const [draggedCard, setDraggedCard] = useState<string | null>(null);
  const [dragOverCard, setDragOverCard] = useState<string | null>(null);
  const [dropPosition, setDropPosition] = useState<'above' | 'below'>('below');
  const [dragOverColumn, setDragOverColumn] = useState<number | null>(null);

  // Debounced save function - only called explicitly when cards are moved/resized/added/deleted
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Track workspace IDs that have been explicitly deleted by the user
  const deletedWorkspaceIdsRef = useRef<string[]>([]);

  // Use ref to always have access to latest state in callbacks
  const saveStateRef = useRef({
    columnCount, workspaces, collapsed, currentWorkspaceId, cardConfigs,
    visibleCardKeys: Array.from(visibleCardKeys),
    workspaceVisibleCards,
    selectedDispatchers, selectedCustomers, selectedRegions
  });
  useEffect(() => {
    saveStateRef.current = {
      columnCount, workspaces, collapsed, currentWorkspaceId, cardConfigs,
      visibleCardKeys: Array.from(visibleCardKeys),
      workspaceVisibleCards,
      selectedDispatchers, selectedCustomers, selectedRegions
    };
  }, [columnCount, workspaces, collapsed, currentWorkspaceId, cardConfigs, visibleCardKeys, workspaceVisibleCards, selectedDispatchers, selectedCustomers, selectedRegions]);

  const saveLayout = useCallback((updatedCardConfigs: CardConfig[], immediate = false, onComplete?: () => void) => {
    // Clear any pending save
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    const doSave = async () => {
      try {
        const {
          columnCount: cols, workspaces: ws, collapsed: coll, currentWorkspaceId: wsId,
          visibleCardKeys: visibleKeys, workspaceVisibleCards: wsVisibleCards,
          selectedDispatchers: dispatchers, selectedCustomers: customers, selectedRegions: regions
        } = saveStateRef.current;
        const deletedIds = deletedWorkspaceIdsRef.current;
        deletedWorkspaceIdsRef.current = [];
        await userSettingsService.saveDashboardLayoutNew('dispatch', {
          cardConfigs: updatedCardConfigs,
          columnCount: cols,
          workspaces: ws,
          collapsed: coll,
          currentWorkspaceId: wsId,
          visibleCardKeys: visibleKeys,
          workspaceVisibleCards: wsVisibleCards,
          selectedDispatchers: dispatchers,
          selectedCustomers: customers,
          selectedRegions: regions,
          deletedWorkspaceIds: deletedIds.length > 0 ? deletedIds : undefined,
          version: CURRENT_VERSION
        });
      } catch (error) {
        console.error('Failed to save dashboard layout to database:', error);
      } finally {
        if (onComplete) onComplete();
      }
    };

    if (immediate) {
      // Save immediately (for edit mode exit)
      doSave();
    } else {
      // Debounce save by 1 second
      saveTimeoutRef.current = setTimeout(doSave, 1000);
    }
  }, []); // No deps - uses refs for latest state

  // Expose saveLayout to the column handlers defined above it (see saveLayoutRef).
  useEffect(() => {
    saveLayoutRef.current = saveLayout;
  }, [saveLayout]);

  const handleToggleCardLock = useCallback((cardKey: string, locked: boolean) => {
    setCardConfigs(prev => {
      const updated = prev.map(card =>
        card.key === cardKey ? { ...card, locked } : card
      );
      // Save layout with the updated configs
      saveLayout(updated, true);
      return updated;
    });

    // Update GridStack widget noMove/noResize
    const grid = gridInstanceRef.current;
    if (grid && gridRef.current) {
      const el = gridRef.current.querySelector(`[gs-id="${cardKey}"]`);
      if (el) {
        grid.update(el as HTMLElement, { noMove: locked, noResize: locked });
      }
    }
  }, [saveLayout]);

  // Save layout when filters or selected card changes (debounced)
  const filterSaveInitialized = useRef(false);
  useEffect(() => {
    if (!configsLoaded) return;

    // Skip the initial render to avoid saving on page load
    if (!filterSaveInitialized.current) {
      filterSaveInitialized.current = true;
      return;
    }

    // Debounced save when filters or visible card selection changes
    const cardsToSave = saveStateRef.current.cardConfigs;
    saveLayout(cardsToSave, false); // debounced save
  }, [visibleCardKeys, workspaceVisibleCards, selectedDispatchers, selectedCustomers, selectedRegions, configsLoaded, saveLayout]);

  // Keyboard shortcuts for workspace switching (Alt/Option + 1-9)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Check if Alt/Option key is pressed along with a number 1-9
      // Use e.code to handle Mac's Alt key which produces special characters
      if (e.altKey) {
        const codeMatch = e.code.match(/^Digit([1-9])$/);
        if (codeMatch) {
          const workspaceIndex = parseInt(codeMatch[1]) - 1;
          const sortedWorkspaces = [...workspaces].sort((a, b) => a.order - b.order);
          if (workspaceIndex < sortedWorkspaces.length) {
            e.preventDefault();
            setCurrentWorkspaceId(sortedWorkspaces[workspaceIndex].id);
          }
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [workspaces]);

  // Sync columnCount changes to current workspace
  useEffect(() => {
    if (!configsLoaded) return;

    setWorkspaces(prev => prev.map(ws =>
      ws.id === currentWorkspaceId
        ? { ...ws, columnCount }
        : ws
    ));
  }, [columnCount, currentWorkspaceId, configsLoaded]);

  // Workspace management functions (no cap on the number of workspaces)
  const handleCreateWorkspace = () => {
    setNewWorkspaceName('');
    setShowNewWorkspaceModal(true);
  };

  const handleConfirmCreateWorkspace = () => {
    const name = newWorkspaceName.trim() || `Workspace ${workspaces.length + 1}`;
    const newId = `workspace-${Date.now()}`;
    const newOrder = workspaces.length;
    setWorkspaces([...workspaces, { id: newId, name, order: newOrder, columnCount: 2 }]);
    setCurrentWorkspaceId(newId);
    setShowNewWorkspaceModal(false);
    setNewWorkspaceName('');
  };

  // Reset layout to default positions for current workspace
  const handleResetLayout = () => {
    // Destroy current GridStack instance to force re-initialization
    if (gridInstanceRef.current) {
      gridInstanceRef.current.destroy(false);
      gridInstanceRef.current = null;
    }

    // Clear gsX/gsY/gsW/gsH values for cards in current workspace
    // This will cause currentGridItems to recompute positions from column/order/span defaults
    setCardConfigs(prev => prev.map(card => {
      if (card.workspaceId !== currentWorkspaceId) return card;

      // Remove GridStack position overrides
      const { gsX, gsY, gsW, gsH, ...rest } = card;
      return rest;
    }));

    setShowResetLayoutConfirm(false);
    showToast({ type: 'success', message: 'Layout reset to default' });
  };

  // Tab ids the user may neither rename nor delete. DRIVER_BOARD_TAB_ID is not a
  // workspace at all — it is pinned by code — so it belongs here too.
  const PROTECTED_WORKSPACE_IDS = ['default', 'critical', DRIVER_BOARD_TAB_ID];

  const handleDeleteWorkspace = (workspaceId: string) => {
    // Can't delete protected workspaces
    if (PROTECTED_WORKSPACE_IDS.includes(workspaceId)) return;

    // Track for explicit backend deletion
    deletedWorkspaceIdsRef.current = [...deletedWorkspaceIdsRef.current, workspaceId];

    // Remove workspace
    setWorkspaces(workspaces.filter(w => w.id !== workspaceId));

    // Remove all cards from this workspace
    const deletedCardKeys = cardConfigs.filter(c => c.workspaceId === workspaceId).map(c => c.key);
    setCardConfigs(cardConfigs.filter(c => c.workspaceId !== workspaceId));

    // MEMORY OPTIMIZATION: Clear data for all deleted cards
    setCardData(prev => {
      const newData = { ...prev };
      deletedCardKeys.forEach(key => delete newData[key]);
      return newData;
    });

    // Switch to default if deleting current workspace
    if (currentWorkspaceId === workspaceId) {
      setCurrentWorkspaceId('default');
    }
  };

  const loadTrashItems = async () => {
    setTrashLoading(true);
    try {
      const items = await userSettingsService.getDashboardTrash('dispatch');
      setTrashItems(items);
    } catch (error) {
      console.error('Failed to load trash items:', error);
    } finally {
      setTrashLoading(false);
    }
  };

  const handleOpenTrash = () => {
    setShowTrashModal(true);
    loadTrashItems();
  };

  const handleRestoreWorkspace = async (workspaceId: string) => {
    try {
      await userSettingsService.restoreFromTrash('dispatch', workspaceId);
      showToast({ type: 'success', message: 'Workspace restored. Refreshing...' });
      setShowTrashModal(false);
      // Reload the page to pick up restored data
      window.location.reload();
    } catch (error) {
      console.error('Failed to restore workspace:', error);
      showToast({ type: 'error', message: 'Failed to restore workspace' });
    }
  };

  const handleRenameWorkspace = (workspaceId: string, newName: string) => {
    setWorkspaces(workspaces.map(w =>
      w.id === workspaceId ? { ...w, name: newName } : w
    ));
  };

  const handleReorderWorkspaces = (reorderedTabs: Tab[]) => {
    // TabBar hands back every tab it renders, pinned ones included. Drop the
    // Driver Board tab first: it maps to no workspace, so the fallback below
    // would otherwise materialize a phantom workspace under its id.
    const reorderedWorkspaceTabs = reorderedTabs.filter(tab => tab.id !== DRIVER_BOARD_TAB_ID);
    // Update workspace order based on the new tab positions
    setWorkspaces(prevWorkspaces => {
      return reorderedWorkspaceTabs.map((tab, index) => {
        const workspace = prevWorkspaces.find(w => w.id === tab.id);
        if (workspace) {
          return { ...workspace, order: index };
        }
        // Fallback - shouldn't happen
        return { id: tab.id, name: tab.label, order: index, columnCount: 2 };
      });
    });
  };

  // Get card count per workspace
  const getWorkspaceCardCount = (workspaceId: string) => {
    return cardConfigs.filter(card => card.workspaceId === workspaceId && card.visible).length;
  };

  // Numbered workspace icon component
  const WorkspaceIcon = ({ number }: { number: number }) => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor">
      <path d="M19,24H5c-2.757,0-5-2.243-5-5V5C0,2.243,2.243,0,5,0h14c2.757,0,5,2.243,5,5v14c0,2.757-2.243,5-5,5ZM5,2c-1.654,0-3,1.346-3,3v14c0,1.654,1.346,3,3,3h14c1.654,0,3-1.346,3-3V5c0-1.654-1.346-3-3-3H5Z"/>
      <text x="12" y="17" textAnchor="middle" fontSize="12" fontWeight="600" fill="currentColor">{number}</text>
    </svg>
  );

  // Create workspace tabs for TabBar
  const workspaceTabs: Tab[] = useMemo(() => {
    const tabs: Tab[] = workspaces.sort((a, b) => a.order - b.order).map((workspace, index) => ({
      id: workspace.id,
      label: workspace.name,
      current: workspace.id === currentWorkspaceId,
      icon: <WorkspaceIcon number={index + 1} />
    }));
    // Pinned tab, appended last on purpose: prepending it would renumber every
    // WorkspaceIcon and move the user's workspaces out from under their Alt+digit
    // shortcuts.
    if (canSeeDriverBoard) {
      tabs.push({
        id: DRIVER_BOARD_TAB_ID,
        label: 'Driver Board',
        current: isDriverBoardTab,
        icon: <TruckIcon className="h-4 w-4" />
      });
    }
    return tabs;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaces, currentWorkspaceId, canSeeDriverBoard, isDriverBoardTab]);

  // Handle duplicating a card with modal
  const handleDuplicateCard = (newName: string, workspaceId: string, createNewWorkspace: boolean) => {
    if (!duplicateCardConfig) return;

    try {
      let targetWorkspaceId = workspaceId;

      // Create new workspace if requested
      if (createNewWorkspace) {
        const newId = `workspace-${Date.now()}`;
        const newOrder = workspaces.length;
        const newWorkspaceName = `Workspace ${workspaces.length}`;
        const updatedWorkspaces = [...workspaces, { id: newId, name: newWorkspaceName, order: newOrder, columnCount: 2 }];
        setWorkspaces(updatedWorkspaces);
        targetWorkspaceId = newId;
        setCurrentWorkspaceId(newId); // Switch to the new workspace
        // Update ref immediately so saveLayout sees the new workspace
        saveStateRef.current = { ...saveStateRef.current, workspaces: updatedWorkspaces, currentWorkspaceId: newId };
      } else {
        // Check if target workspace already has 8 visible cards (max limit)
        const visibleCardsInWorkspace = cardConfigs.filter(
          c => c.workspaceId === workspaceId && c.visible
        ).length;

        if (visibleCardsInWorkspace >= 8) {
          const workspaceName = workspaces.find(w => w.id === workspaceId)?.name || 'workspace';
          showToast({
            message: `Cannot duplicate to ${workspaceName} - workspace is full (8/8 cards). Choose a different workspace or create a new one.`,
            type: 'error',
            duration: 5000
          });
          return;
        }
      }

      // Create a unique key for the duplicated card
      const newKey = `card-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;

      // Check if a card with this key already exists (extremely unlikely but just in case)
      if (cardConfigs.some(c => c.key === newKey)) {
        showToast({
          message: `Cannot create new card - card key collision`,
          type: 'error',
          duration: 5000
        });
        return;
      }

      // Check if a card with the same title already exists
      if (cardConfigs.some(c => c.title === newName)) {
        showToast({
          message: `Cannot create new card - card "${newName}" already exists`,
          type: 'error',
          duration: 5000
        });
        return;
      }

      // Find the highest order in the target workspace (column 0 by default)
      const cardsInTargetWorkspace = cardConfigs.filter(
        c => c.workspaceId === targetWorkspaceId && c.column === 0
      );
      const maxOrder = Math.max(...cardsInTargetWorkspace.map(c => c.order), -1);

      // Calculate GridStack position for the new card in target workspace
      const targetWorkspaceCards = cardConfigs.filter(c => c.workspaceId === targetWorkspaceId && c.visible);
      let maxBottom = 0;
      targetWorkspaceCards.forEach(card => {
        // Only use gs* values if they're valid (w and h >= 1)
        if (card.gsY !== undefined && card.gsH !== undefined && card.gsW !== undefined && card.gsW >= 1 && card.gsH >= 1) {
          maxBottom = Math.max(maxBottom, card.gsY + card.gsH);
        } else {
          // Estimate from order if no valid gs* values
          const estimatedY = card.order * 6;
          maxBottom = Math.max(maxBottom, estimatedY + 6);
        }
      });

      // Create new card config with same settings but new key, title, workspace, and fresh grid position
      const newCard: CardConfig = {
        ...duplicateCardConfig,
        key: newKey,
        title: newName,
        workspaceId: targetWorkspaceId,
        column: 0, // Place in first column of target workspace
        order: maxOrder + 1,
        locked: true, // New cards start locked (clean view); unlock to arrange the tile
        // Fresh GridStack position (don't copy from original card)
        gsX: 0,
        gsY: maxBottom,
        gsW: 4,
        gsH: 6
      };

      // Add the new card to configs
      const updatedConfigs = [...cardConfigs, newCard];

      // IMPORTANT: Update refs BEFORE triggering state changes
      // The workspace switch effect uses cardConfigsRef.current, so it must be updated first
      cardConfigsRef.current = updatedConfigs;

      // Now trigger state updates (React will batch these)
      setCardConfigs(updatedConfigs);

      // Save the layout immediately so duplicate persists
      saveLayout(updatedConfigs, true);

      // Fetch data for the new card (same pattern as FirstCardModal)
      fetchCardData(
        newKey,
        newCard.filterConditions || [],
        newCard.filterView || 'Orders',
        newCard.bucket,
        false,
        newCard.additionalFields
      );

      // Close modal
      setDuplicateCardConfig(null);

      // Show success toast
      showToast({
        message: `Successfully duplicated "${duplicateCardConfig.title}" to ${createNewWorkspace ? 'new workspace' : workspaces.find(w => w.id === targetWorkspaceId)?.name}`,
        type: 'success',
        duration: 3000
      });
    } catch (error) {
      console.error('Failed to duplicate card:', error);
      showToast({
        message: `Failed to duplicate card "${duplicateCardConfig.title}"`,
        type: 'error',
        duration: 5000
      });
    }
  };

  // Handle creating a new card via modal
  const handleCreateBlankCard = useCallback(() => {
    setIsFirstCardModalOpen(true);
  }, []);

  // Clear custom actions for the toolbar (GridStack doesn't need Add Container)
  useEffect(() => {
    setCustomActions([]);
    return () => {
      setCustomActions([]);
    };
  }, [setCustomActions]);

  // Helper to calculate next available Y position for new cards in a workspace
  const getNextGridPosition = (workspaceId: string): { gsX: number; gsY: number; gsW: number; gsH: number } => {
    const workspaceCards = cardConfigs.filter(c => c.workspaceId === workspaceId && c.visible);

    // Find the maximum Y + H (bottom edge) of all existing cards
    let maxBottom = 0;
    workspaceCards.forEach(card => {
      // Only use gs* values if they're valid (w and h >= 1)
      if (card.gsY !== undefined && card.gsH !== undefined && card.gsW !== undefined && card.gsW >= 1 && card.gsH >= 1) {
        maxBottom = Math.max(maxBottom, card.gsY + card.gsH);
      } else {
        // Estimate from column/order if no valid gs* values
        // Use a default height of 6 for each card stacked vertically
        const estimatedY = card.order * 6;
        maxBottom = Math.max(maxBottom, estimatedY + 6);
      }
    });

    // Default: place at x=0, below all existing cards, width=4 (1/3 of 12-col grid), height=6
    return { gsX: 0, gsY: maxBottom, gsW: 4, gsH: 6 };
  };

  // Handle creating a new card from modal
  const handleCreateCard = () => {
    if (!newCardTitle.trim()) {
      alert('Please enter a card title');
      return;
    }

    // Generate a unique key for the new card
    const newKey = `card-${Date.now()}`;

    // Get the highest order number in the target column
    const columnCards = cardConfigs.filter(c => c.column === newCardColumn && c.workspaceId === currentWorkspaceId);
    const maxOrder = columnCards.length > 0 ? Math.max(...columnCards.map(c => c.order)) : -1;

    // Calculate GridStack position for new card
    const gridPos = getNextGridPosition(currentWorkspaceId);

    // Create the new card config with GridStack position
    const newCard: CardConfig = {
      key: newKey,
      title: newCardTitle,
      column: newCardColumn,
      order: maxOrder + 1,
      visible: true,
      span: 1,
      workspaceId: currentWorkspaceId,
      gsX: gridPos.gsX,
      gsY: gridPos.gsY,
      gsW: gridPos.gsW,
      gsH: gridPos.gsH
    };

    // Add the card to the config
    setCardConfigs([...cardConfigs, newCard]);

    // Reset the form and close the modal
    setNewCardTitle('');
    setNewCardColumn(0);
    setIsAddCardModalOpen(false);
  };

  // Check if current workspace has reached the 8 card limit
  const isCurrentWorkspaceFull = useMemo(() => {
    return getWorkspaceCardCount(currentWorkspaceId) >= 8;
  }, [currentWorkspaceId, cardConfigs]);

  // Collect all component data for bulk permissions
  const allComponents = useMemo(() => {
    return cardConfigs
      .map(card => ({
        id: `dispatch-${card.key}`,
        name: card.title,
      }));
  }, [cardConfigs]);

  useEffect(() => {
    if (permissionsMode && setPageComponents) {
      setPageComponents(allComponents);
    }
  }, [permissionsMode, allComponents, setPageComponents]);

  // Register Create Card button in footer
  useEffect(() => {
    const currentCardCount = getWorkspaceCardCount(currentWorkspaceId);
    const cardsRemaining = 8 - currentCardCount;

    // The Driver Board tab is not a workspace — a card created there would be
    // stamped with its id and then never render anywhere.
    if (!isCurrentWorkspaceFull && !isDriverBoardTab) {
      setDispatchButton(
        <div className="inline-flex items-center gap-3">
          <span className="text-sm text-gray-600 dark:text-gray-400">
            Cards Remaining: <span className="font-semibold text-heading">{cardsRemaining}</span>
          </span>
          <button
            onClick={handleCreateBlankCard}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-accent-fg bg-accent rounded-lg shadow-sm hover:bg-accent-hover transition-colors"
          >
            <PlusIcon className="h-5 w-5" />
            Create Card
          </button>
        </div>
      );
    } else {
      setDispatchButton(null);
    }

    // Clear button when unmounting
    return () => {
      setDispatchButton(null);
    };
  }, [isCurrentWorkspaceFull, isDriverBoardTab, setDispatchButton, handleCreateBlankCard, currentWorkspaceId, cardConfigs]);


  // Toggle collapse state for a section
  // When expanding, the useEffect watching `collapsed` will trigger a batch fetch
  // When collapsing, clear data to free memory
  const toggleCollapse = (sectionKey: string) => {
    const isCurrentlyCollapsed = collapsed[sectionKey];
    const card = cardConfigs.find(c => c.key === sectionKey);

    // If we're expanding the card (currently collapsed -> becoming expanded)
    if (isCurrentlyCollapsed) {
      // Cancel any in-progress batch request - the useEffect will trigger a new one
      // that includes this newly expanded card
      if (batchCardsAbortControllerRef.current) {
        batchCardsAbortControllerRef.current.abort();
        batchCardsAbortControllerRef.current = null;
      }

      // Restore the GridStack height when expanding
      if (gridInstanceRef.current && card?.expandedGsH) {
        const element = document.querySelector(`[gs-id="${sectionKey}"]`) as HTMLElement;
        if (element) {
          gridInstanceRef.current.update(element, { h: card.expandedGsH });
        }
        // Clear the stored expanded height
        setCardConfigs(prev => prev.map(c =>
          c.key === sectionKey ? { ...c, expandedGsH: undefined } : c
        ));
      }
    } else {
      // MEMORY OPTIMIZATION: When collapsing, clear the card's data to free memory
      setCardData(prev => {
        const newData = { ...prev };
        delete newData[sectionKey];
        return newData;
      });

      // Collapse the GridStack item to just show the header (height = 1)
      if (gridInstanceRef.current) {
        const element = document.querySelector(`[gs-id="${sectionKey}"]`) as HTMLElement;
        if (element) {
          // Get current height from GridStack node or DOM
          const node = (element as any).gridstackNode;
          const currentHeight = node?.h || parseInt(element.getAttribute('gs-h') || '6', 10);

          // Store the current height before collapsing
          setCardConfigs(prev => prev.map(c =>
            c.key === sectionKey ? { ...c, expandedGsH: currentHeight } : c
          ));

          // Update GridStack to collapsed height (1 unit = just header)
          gridInstanceRef.current.update(element, { h: 1 });
        }
      }
    }

    setCollapsed(prev => ({
      ...prev,
      [sectionKey]: !prev[sectionKey]
    }));
  };

  // Collapse all cards in the current workspace
  const collapseAllCards = useCallback(() => {
    const currentWorkspaceCards = cardConfigs.filter(c => c.workspaceId === currentWorkspaceId && c.visible);
    const cardsToCollapse = currentWorkspaceCards.filter(card => !collapsed[card.key]);

    if (cardsToCollapse.length === 0) return;

    // Cancel any in-progress batch request since we're collapsing cards
    if (batchCardsAbortControllerRef.current) {
      batchCardsAbortControllerRef.current.abort();
      batchCardsAbortControllerRef.current = null;
    }

    // Clear data for all cards being collapsed
    setCardData(prev => {
      const newData = { ...prev };
      cardsToCollapse.forEach(card => {
        delete newData[card.key];
      });
      return newData;
    });

    // Store heights and collapse GridStack items
    if (gridInstanceRef.current) {
      const heightUpdates: { [key: string]: number } = {};

      cardsToCollapse.forEach(card => {
        const element = document.querySelector(`[gs-id="${card.key}"]`) as HTMLElement;
        if (element) {
          const node = (element as any).gridstackNode;
          const currentHeight = node?.h || parseInt(element.getAttribute('gs-h') || '6', 10);
          heightUpdates[card.key] = currentHeight;
          gridInstanceRef.current!.update(element, { h: 1 });
        }
      });

      // Store expanded heights in card configs
      setCardConfigs(prev => prev.map(c =>
        heightUpdates[c.key] ? { ...c, expandedGsH: heightUpdates[c.key] } : c
      ));
    }

    // Set all as collapsed
    setCollapsed(prev => {
      const newCollapsed = { ...prev };
      cardsToCollapse.forEach(card => {
        newCollapsed[card.key] = true;
      });
      return newCollapsed;
    });
  }, [cardConfigs, currentWorkspaceId, collapsed]);

  // Expand all cards in the current workspace
  const expandAllCards = useCallback(() => {
    const currentWorkspaceCards = cardConfigs.filter(c => c.workspaceId === currentWorkspaceId && c.visible);
    const cardsToExpand = currentWorkspaceCards.filter(card => collapsed[card.key]);

    if (cardsToExpand.length === 0) return;

    // Cancel any in-progress batch request - the useEffect will trigger a new one
    if (batchCardsAbortControllerRef.current) {
      batchCardsAbortControllerRef.current.abort();
      batchCardsAbortControllerRef.current = null;
    }

    // Restore GridStack heights
    if (gridInstanceRef.current) {
      const keysToRestore: string[] = [];

      cardsToExpand.forEach(card => {
        if (card.expandedGsH) {
          const element = document.querySelector(`[gs-id="${card.key}"]`) as HTMLElement;
          if (element) {
            gridInstanceRef.current!.update(element, { h: card.expandedGsH });
            keysToRestore.push(card.key);
          }
        }
      });

      // Clear expanded heights from card configs
      if (keysToRestore.length > 0) {
        setCardConfigs(prev => prev.map(c =>
          keysToRestore.includes(c.key) ? { ...c, expandedGsH: undefined } : c
        ));
      }
    }

    // Set all as expanded - the useEffect watching `collapsed` will trigger the batch fetch
    setCollapsed(prev => {
      const newCollapsed = { ...prev };
      cardsToExpand.forEach(card => {
        newCollapsed[card.key] = false;
      });
      return newCollapsed;
    });
  }, [cardConfigs, currentWorkspaceId, collapsed]);

  // Drag handlers
  const handleDragStart = (e: React.DragEvent, cardKey: string) => {
    setDraggedCard(cardKey);
    setDragOverCard(null);
  };

  const handleDragOver = (e: React.DragEvent, cardKey: string) => {
    if (!draggedCard || draggedCard === cardKey) return;
    e.preventDefault();

    const rect = e.currentTarget.getBoundingClientRect();
    const midpoint = rect.top + rect.height / 2;
    const position = e.clientY < midpoint ? 'above' : 'below';

    setDragOverCard(cardKey);
    setDropPosition(position);
  };

  const handleDrop = (e: React.DragEvent, targetCardKey: string) => {
    e.preventDefault();
    if (!draggedCard || draggedCard === targetCardKey) return;

    const draggedCardConfig = cardConfigs.find(c => c.key === draggedCard);
    const targetCardConfig = cardConfigs.find(c => c.key === targetCardKey);

    if (!draggedCardConfig || !targetCardConfig) return;

    setCardConfigs(prev => {
      const newConfigs = prev.map(card => {
        if (card.key === draggedCard) {
          return {
            ...card,
            column: targetCardConfig.column,
          };
        }
        return card;
      });

      const sameColumnCards = newConfigs.filter(c =>
        c.column === targetCardConfig.column
      );

      const targetIndex = sameColumnCards.findIndex(c => c.key === targetCardKey);
      const draggedIndex = sameColumnCards.findIndex(c => c.key === draggedCard);

      if (targetIndex === -1 || draggedIndex === -1) return newConfigs;

      const insertIndex = dropPosition === 'above' ? targetIndex : targetIndex + 1;
      const adjustedInsertIndex = draggedIndex < targetIndex && dropPosition === 'below'
        ? insertIndex - 1
        : draggedIndex > targetIndex && dropPosition === 'above'
          ? insertIndex + 1
          : insertIndex;

      sameColumnCards.splice(draggedIndex, 1);
      sameColumnCards.splice(adjustedInsertIndex, 0, newConfigs.find(c => c.key === draggedCard)!);

      sameColumnCards.forEach((card, index) => {
        const originalCard = newConfigs.find(c => c.key === card.key);
        if (originalCard) {
          originalCard.order = index;
        }
      });

      return newConfigs;
    });

    setDraggedCard(null);
    setDragOverCard(null);
  };

  // Get col-span class based on span value
  const getColSpanClass = (span: number) => {
    switch (span) {
      case 1: return 'lg:col-span-1';
      case 2: return 'lg:col-span-2';
      case 3: return 'lg:col-span-3';
      case 4: return 'lg:col-span-4';
      default: return 'lg:col-span-1';
    }
  };

  // Get grid column start class based on column number
  const getGridColumnStartClass = (column: number) => {
    return column + 1;
  };

  // Get table header background color based on urgency of data in the card
  // Red = has Past Due or Today items, Orange = has Tomorrow items (no red), Slate = default
  const getTableHeaderBgColor = (cardKey: string, cardData: any[]): string | undefined => {
    // Special case: Drop Released Not Dispatched - never show color
    if (cardKey === 'drop-released-not-dispatched') {
      return undefined;
    }

    // Past-due buckets are urgent whenever they contain records.
    if (cardKey === 'past-due' || cardKey === 'past-due-no-outgate') {
      return cardData.length > 0 ? 'bg-brand-red dark:bg-brand-red' : undefined;
    }

    // Special case: Live Load/Unload, No Terminate - red if has data
    if (cardKey === 'live-load-unload-no-terminate') {
      return cardData.length > 0 ? 'bg-brand-red dark:bg-brand-red' : undefined;
    }

    // Special case: Dispatched Driver OOS - red if has data
    if (cardKey === 'dispatched-driver-oos') {
      return cardData.length > 0 ? 'bg-brand-red dark:bg-brand-red' : undefined;
    }

    // Special case: Terminated Not Complete - red if has data
    if (cardKey === 'terminated-not-complete') {
      return cardData.length > 0 ? 'bg-brand-red dark:bg-brand-red' : undefined;
    }

    // Special case: No Outgate - red if any row meets time threshold for its region
    if (cardKey === 'no-outgate' && cardData.length > 0) {
      const now = new Date();
      const currentHour = now.getHours();

      // Check if any row meets the time threshold
      const hasUrgentRow = cardData.some((row: any) => {
        const region = row.region?.toLowerCase() || '';
        let thresholdHour = 14; // Default: 2pm

        if (region === 'mobile') {
          thresholdHour = 13; // 1pm for Mobile
        } else if (region === 'dallas' || region === 'memphis') {
          thresholdHour = 15; // 3pm for Dallas/Memphis
        }

        return currentHour >= thresholdHour;
      });

      if (hasUrgentRow) {
        return 'bg-brand-red dark:bg-brand-red';
      }
    }

    // Get the tab grouping field for this card
    const cardConfig = cardConfigs.find(c => c.key === cardKey);
    const tabGroupingField = cardConfig?.tabGrouping;

    if (!tabGroupingField || cardData.length === 0) return undefined;

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayStr = today.toISOString().split('T')[0];

    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const tomorrowStr = tomorrow.toISOString().split('T')[0];

    let hasPastDueOrToday = false;
    let hasTomorrow = false;

    // Check each row's date to determine urgency
    cardData.forEach((row: any) => {
      const value = row[tabGroupingField];
      if (!value) return;

      // Extract date string (handle datetime formats)
      let dateStr = String(value);
      if (dateStr.includes('T')) {
        dateStr = dateStr.split('T')[0];
      } else if (dateStr.includes(' ')) {
        dateStr = dateStr.split(' ')[0];
      }

      // Check if valid date format
      if (!/^\d{4}-\d{2}-\d{2}/.test(dateStr)) return;
      dateStr = dateStr.substring(0, 10);

      if (dateStr <= todayStr) {
        hasPastDueOrToday = true;
      } else if (dateStr === tomorrowStr) {
        hasTomorrow = true;
      }
    });

    // Priority: Red > Orange > Slate
    if (hasPastDueOrToday) {
      return 'bg-brand-red dark:bg-brand-red';
    }
    if (hasTomorrow) {
      return 'bg-orange-500 dark:bg-orange-600';
    }

    // Default slate
    return 'bg-slate-700 dark:bg-slate-800';
  };

  // Get count of "Today" items only for a card
  // Note: Backend now handles global filtering, so we use raw card data
  const getTodayCount = (cardKey: string): number => {
    const data = cardData[cardKey] || [];

    // Special case: Dispatched Driver OOS, No Outgate, Terminated Not Complete - count all items
    if (cardKey === 'dispatched-driver-oos' || cardKey === 'no-outgate' || cardKey === 'terminated-not-complete') {
      return data.length;
    }

    // Get the tab grouping field for this card
    const cardConfig = cardConfigs.find(c => c.key === cardKey);
    const tabGroupingField = cardConfig?.tabGrouping;

    if (!tabGroupingField || data.length === 0) return 0;

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayStr = today.toISOString().split('T')[0];

    let count = 0;

    // Count items that are exactly today
    data.forEach((row: any) => {
      const value = row[tabGroupingField];
      if (!value) return;

      // Extract date string (handle datetime formats)
      let dateStr = String(value);
      if (dateStr.includes('T')) {
        dateStr = dateStr.split('T')[0];
      } else if (dateStr.includes(' ')) {
        dateStr = dateStr.split(' ')[0];
      }

      // Check if valid date format
      if (!/^\d{4}-\d{2}-\d{2}/.test(dateStr)) return;
      dateStr = dateStr.substring(0, 10);

      if (dateStr === todayStr) {
        count++;
      }
    });

    return count;
  };

  // Get count for a card based on its statButtonCountField configuration and time period
  // Note: Backend now handles global filtering, so we use raw card data
  const getCardCount = (card: CardConfig, timePeriod: TimePeriod = 'today'): number => {
    const data = cardData[card.key] || [];

    // Determine which field to use for date filtering
    // Priority: statButtonCountField > tabGrouping > none (return total)
    const dateField = card.statButtonCountField || card.tabGrouping;

    // If no date field specified or TODAY_ALL, return total count
    if (!dateField || card.statButtonCountField === 'TODAY_ALL') {
      return data.length;
    }

    // Calculate date range based on time period
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayStr = today.toISOString().split('T')[0];

    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const tomorrowStr = tomorrow.toISOString().split('T')[0];

    // End of week (Sunday)
    const endOfWeek = new Date(today);
    const dayOfWeek = today.getDay();
    const daysUntilSunday = dayOfWeek === 0 ? 0 : 7 - dayOfWeek;
    endOfWeek.setDate(today.getDate() + daysUntilSunday);
    const endOfWeekStr = endOfWeek.toISOString().split('T')[0];

    let count = 0;

    data.forEach((row: any) => {
      const value = row[dateField];
      if (!value) return;

      // Extract date string (handle datetime formats)
      let dateStr = String(value);
      if (dateStr.includes('T')) {
        dateStr = dateStr.split('T')[0];
      } else if (dateStr.includes(' ')) {
        dateStr = dateStr.split(' ')[0];
      }

      // Check if valid date format
      if (!/^\d{4}-\d{2}-\d{2}/.test(dateStr)) return;
      dateStr = dateStr.substring(0, 10);

      // Check based on time period
      if (timePeriod === 'today' && dateStr === todayStr) {
        count++;
      } else if (timePeriod === 'tomorrow' && dateStr === tomorrowStr) {
        count++;
      } else if (timePeriod === 'this_week' && dateStr >= todayStr && dateStr <= endOfWeekStr) {
        count++;
      }
    });

    return count;
  };

  // Stats grid data for main workspace only — uses API bucket counts.
  // Twelve cards laid out as a 4×3 grid; cards render in their natural order
  // (no pinning since the yard buckets that used to pin to the right column
  // moved to the Yard Reports page).
  const categorizedStats = useMemo(() => {
    const empty = { critical: [] as StatItem[], important: [] as StatItem[], informational: [] as StatItem[] };
    if (currentWorkspaceId !== 'default') return empty;
    const result = { critical: [] as StatItem[], important: [] as StatItem[], informational: [] as StatItem[] };
    for (const card of cardConfigs) {
      if (card.workspaceId !== 'default' || card.showStatButton === false) continue;
      if (card.bucket === 'past-due-no-outgate' && statsTimePeriod !== 'today') continue;
      const category = card.bucket ? STAT_CATEGORY_BY_BUCKET[card.bucket] : undefined;
      if (!category) continue;
      result[category].push({
        id: card.key,
        label: card.statButtonLabel || card.title,
        value: card.bucket ? (bucketCounts[card.bucket] ?? 0) : 0,
        onClick: () => toggleCardVisibility(card.key),
        selected: visibleCardKeys.has(card.key) && visibleCardKeys.size < allCardKeys.length,
      });
    }
    // Keep the overdue pickup alert first, including in saved layouts.
    result.critical.sort((a, b) =>
      Number(b.id === 'past-due-no-outgate') - Number(a.id === 'past-due-no-outgate')
    );
    return result;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cardConfigs, currentWorkspaceId, visibleCardKeys, allCardKeys, bucketCounts, statsTimePeriod]);

  const hasAnyStats =
    categorizedStats.critical.length > 0 ||
    categorizedStats.important.length > 0 ||
    categorizedStats.informational.length > 0;

  const renderCategorizedStatsPanel = () => {
    const sections: Array<{
      key: StatCategory;
      label: string;
      labelColor: string;
      stats: StatItem[];
      columns: number;
      rows: number;
    }> = [
      {
        key: 'critical',
        label: 'Critical',
        labelColor: 'text-red-600 dark:text-red-400',
        stats: categorizedStats.critical,
        columns: statsTimePeriod === 'today' ? 7 : 6,
        rows: 1,
      },
      {
        key: 'important',
        label: 'Important',
        labelColor: 'text-amber-600 dark:text-amber-400',
        stats: categorizedStats.important,
        columns: 5,
        rows: 2,
      },
      {
        key: 'informational',
        label: 'Informational',
        labelColor: 'text-slate-600 dark:text-slate-400',
        stats: categorizedStats.informational,
        columns: 3,
        rows: 1,
      },
    ];

    return (
      <div className="relative">
        {/* Shared controls: time-period selector (left) + pin (right) */}
        <div className="flex items-center justify-between mb-2">
          <div className="relative inline-block">
            <select
              value={statsTimePeriod}
              onChange={(e) => setStatsTimePeriod(e.target.value as TimePeriod)}
              className="appearance-none text-base font-semibold text-heading bg-transparent pr-6 cursor-pointer focus:outline-none"
            >
              <option value="today">Today</option>
              <option value="tomorrow">Tomorrow</option>
              <option value="this_week">This Week</option>
            </select>
            <ChevronDownIcon className="absolute right-0 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500 pointer-events-none" />
          </div>
          {/* Live-push indicator. Green once a push has actually landed; grey
              until then, because a quiet board and a broken socket look
              identical without it. */}
          <div className="flex items-center gap-2 ml-auto mr-2">
            <span
              className={
                'inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[0.6875rem] font-medium ' +
                (lastPushLabel
                  ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300'
                  : 'bg-gray-100 text-muted dark:bg-gray-700')
              }
              title={
                lastPushLabel
                  ? 'Live updates are arriving. The board also refreshes every 30s regardless.'
                  : 'Connected, but no live update has arrived yet. The board still refreshes every 30s.'
              }
            >
              <span
                className={
                  'h-1.5 w-1.5 rounded-full ' + (lastPushLabel ? 'bg-green-500' : 'bg-gray-400')
                }
              />
              {lastPushLabel ? `Live · ${lastPushLabel}` : 'Live · waiting'}
            </span>
          </div>
          <button
            onClick={() => setStatsCollapsed(!statsCollapsed)}
            className="p-1.5 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
            title={statsCollapsed ? 'Expand bucket counts' : 'Collapse bucket counts'}
            aria-label={statsCollapsed ? 'Expand bucket counts' : 'Collapse bucket counts'}
          >
            <ChevronDownIcon className={`h-4 w-4 transition-transform ${statsCollapsed ? '' : 'rotate-180'}`} />
          </button>
        </div>

        {!statsCollapsed && sections.map((section) =>
          section.stats.length === 0 ? null : (
            <div key={section.key} className="mb-3 last:mb-0">
              <h4 className={`text-xs font-bold uppercase tracking-wide mb-1 ${section.labelColor}`}>
                {section.label}
              </h4>
              <StatsGrid
                stats={section.stats}
                size="sm"
                loading={bucketCountsLoading}
                columns={section.columns}
                rows={section.rows}
              />
            </div>
          )
        )}
      </div>
    );
  };


  // =============================================================================
  // DYNAMIC COLUMN GENERATION
  // =============================================================================

  // Shared PRO# column definition. Used by the dynamic bucket path (via
  // customColumnDefs.pronumber) and by every locked-down bucket whose row
  // shape forces a hand-rolled column array (mismatched-drivers,
  // invalid-container-numbers, missing-waiting-time). Coerces value via
  // String(...) so numeric pronumbers from buckets that select o.pronumber
  // as an int render the same as string-typed ones.
  const proNumberColumn = useMemo<ColumnDef<any>>(() => ({
    accessorKey: 'pronumber',
    header: 'PRO#',
    enableSorting: true,
    enableColumnFilter: true,
    filterFn: 'arrIncludes',
    cell: (info) => {
      const pronumber = String(info.getValue() ?? '');
      if (!pronumber) return '';
      const legacyCmsUrl = config.LEGACY_CMS_URL;
      const viewed = isViewed(pronumber);

      return (
        <div
          className={`flex items-center gap-2 px-1 rounded transition-colors duration-150 ${
            viewed ? 'bg-green-200 dark:bg-green-600/50' : ''
          }`}
          title={viewed ? 'Viewed' : undefined}
        >
          <button
            onClick={(e) => {
              e.stopPropagation();
              markViewed(pronumber);
              setSelectedProNumber(pronumber);
            }}
            className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 underline cursor-pointer"
          >
            {pronumber}
          </button>
          <a
            href={`${legacyCmsUrl}/index_cms.pl?action=savecustomsearch&contlist=${pronumber}&searchtype=pronumber`}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => {
              e.stopPropagation();
              markViewed(pronumber);
            }}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            title="Open in legacy CMS"
          >
            <ArrowTopRightOnSquareIcon className="h-4 w-4" />
          </a>
        </div>
      );
    },
    aggregatedCell: () => null,
  }), [isViewed, markViewed]);

  // Custom column definitions for fields that need special rendering
  // These are keyed by the API field name (after mapping from db field name)
  const customColumnDefs = useMemo<Record<string, ColumnDef<any>>>(() => ({
    pronumber: proNumberColumn,
    // Service Failure (SF) + Trouble Ticket (TT) computed columns — the TT badge
    // click opens the order drawer.
    ...buildComputedColumnDefs({ onProClick: setSelectedProNumber }),
    // Synthetic requirements column — composite icons rendered from the
    // underlying flag fields (fetched via expandAdditionalFields).
    [REQ_FIELD]: {
      id: REQ_FIELD,
      header: 'Req',
      size: 100,
      enableSorting: false,
      enableColumnFilter: false,
      cell: ({ row }) => <RequirementIcons row={row.original} />,
      aggregatedCell: () => null,
    },
    tt_recno: {
      accessorKey: 'tt_recno',
      header: 'Ticket #',
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      cell: (info) => {
        const value = info.getValue() as number;
        if (value === null || value === undefined) return '';
        return (
          <button
            onClick={async (e) => {
              e.stopPropagation();
              setTicketLoading(true);
              setSelectedTicket(null);
              try {
                const res = await api.get(`/trouble-tickets/by-recno/${value}`);
                setSelectedTicket(res.data.data);
              } catch (err) {
                console.error('Failed to fetch ticket:', err);
              } finally {
                setTicketLoading(false);
              }
            }}
            className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 underline cursor-pointer"
          >
            {String(value)}
          </button>
        );
      },
      aggregatedCell: () => null,
    },
    perdiemstartdate: {
      accessorKey: 'perdiemstartdate',
      header: 'Per Diem Start',
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      meta: { highlightDateProximity: true },
      cell: (info) => {
        const value = info.getValue() as string;
        if (!value || value === '0000-00-00') return '';
        const dateValue = new Date(value + 'T00:00:00');
        if (isNaN(dateValue.getTime())) return value;
        const month = String(dateValue.getMonth() + 1).padStart(2, '0');
        const day = String(dateValue.getDate()).padStart(2, '0');
        return `${month}/${day}`;
      },
    },
    lastfreedaydate: {
      accessorKey: 'lastfreedaydate',
      header: 'LFD',
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      meta: { highlightDateProximity: true },
      cell: (info) => {
        const value = info.getValue() as string;
        if (!value || value === '0000-00-00') return '';
        const dateValue = new Date(value + 'T00:00:00');
        if (isNaN(dateValue.getTime())) return value;
        const month = String(dateValue.getMonth() + 1).padStart(2, '0');
        const day = String(dateValue.getDate()).padStart(2, '0');
        return `${month}/${day}`;
      },
    },
    cutoffdatetime: {
      accessorKey: 'cutoffdatetime',
      header: 'Cutoff',
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      meta: { highlightDateProximity: true },
      cell: (info) => {
        const value = info.getValue() as string;
        if (!value || value === '0000-00-00 00:00:00' || value === '0000-00-00') return '';
        let dateObj: Date;
        if (value.includes('T')) {
          dateObj = new Date(value);
        } else if (value.includes(' ')) {
          const [datePart, timePart] = value.split(' ');
          dateObj = new Date(`${datePart}T${timePart}`);
        } else {
          dateObj = new Date(value + 'T00:00:00');
        }
        if (isNaN(dateObj.getTime())) return value;
        const month = String(dateObj.getMonth() + 1).padStart(2, '0');
        const day = String(dateObj.getDate()).padStart(2, '0');
        let hours = dateObj.getHours();
        const minutes = String(dateObj.getMinutes()).padStart(2, '0');
        const ampm = hours >= 12 ? 'PM' : 'AM';
        hours = hours % 12;
        hours = hours ? hours : 12;
        return `${month}/${day} ${hours}:${minutes} ${ampm}`;
      },
    },
    // OOS Driver column for dispatched-driver-oos bucket
    oos_driver: {
      accessorKey: 'oos_driver',
      header: 'OOS Driver',
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      cell: (info) => info.getValue() || '',
    },
    // Driver column for dispatches-not-accepted bucket (leg.driver, extra select)
    dispatched_driver: {
      accessorKey: 'dispatched_driver',
      header: 'Driver',
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      cell: (info) => info.getValue() || '',
    },
    // Leg dspdriverdatetime column for dispatches-not-accepted bucket (leg.dspdriverdatetime, extra select)
    dispatched_datetime: {
      accessorKey: 'dispatched_datetime',
      header: 'Dispatched',
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      cell: (info) => {
        const value = info.getValue() as string | null | undefined;
        if (!value || value === '0000-00-00 00:00:00' || value === '0000-00-00') return '';
        let dateObj: Date;
        if (value.includes('T')) {
          dateObj = new Date(value);
        } else if (value.includes(' ')) {
          const [datePart, timePart] = value.split(' ');
          dateObj = new Date(`${datePart}T${timePart}`);
        } else {
          dateObj = new Date(value + 'T00:00:00');
        }
        if (isNaN(dateObj.getTime())) return value;
        const currentYear = new Date().getFullYear();
        const month = String(dateObj.getMonth() + 1).padStart(2, '0');
        const day = String(dateObj.getDate()).padStart(2, '0');
        const hours = String(dateObj.getHours()).padStart(2, '0');
        const minutes = String(dateObj.getMinutes()).padStart(2, '0');
        if (dateObj.getFullYear() !== currentYear) {
          return `${month}/${day}/${String(dateObj.getFullYear()).slice(-2)} ${hours}:${minutes}`;
        }
        return `${month}/${day} ${hours}:${minutes}`;
      },
    },
    // Per diem days column for per-diem-not-dispatched and past-due buckets
    perdiemdays: {
      accessorKey: 'perdiemdays',
      header: 'PD Days',
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      cell: (info) => info.getValue() ?? '',
    },
    // Street turn opportunities count + clickable link to gwi360 multi-pro search
    streetturnopportunity: {
      accessorKey: 'streetturnopportunity',
      header: 'ST Opps',
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      cell: (info) => {
        const list = info.getValue() as string | null | undefined;
        if (!list) return null;
        const count = list.split(',').filter(Boolean).length;
        if (count === 0) return null;
        return (
          <button
            className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 underline cursor-pointer"
            onClick={(e) => {
              e.stopPropagation();
              window.open(`/searchresults?q=${encodeURIComponent(list)}&multi=true`, '_blank', 'noopener,noreferrer');
            }}
            title={list}
          >
            {count}
          </button>
        );
      },
      aggregatedCell: () => null,
    },
    // Computed columns for scheduled-not-dispatched bucket
    d_legmiles_band: {
      accessorKey: 'd_legmiles_band',
      header: 'Mile Band',
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      cell: (info) => info.getValue() || '',
    },
  }), [proNumberColumn]);

  /**
   * Generate column definitions for a bucket based on its field list.
   * Uses custom column definitions for special fields, falls back to generateColumnDef for others.
   */
  // Build column defs for an ordered list of field names. Prefers a custom
  // column def (special formatting/renderers), then a generated def from the
  // field definitions, then a basic fallback. Shared by the dynamic bucket
  // path and by custom buckets that append user-selected order-level fields.
  const generateColumnsForFields = useCallback((fieldnames: string[]): ColumnDef<any>[] => {
    const columns: ColumnDef<any>[] = [];

    for (const fieldname of fieldnames) {
      const apiFieldName = getApiFieldName(fieldname);

      // Check if we have a custom column definition
      if (customColumnDefs[apiFieldName]) {
        columns.push(decorateMuleDriverColumn(customColumnDefs[apiFieldName], apiFieldName));
        continue;
      }

      // Find the field definition and generate column
      const fieldDef = fieldDefinitions.find(f => f.fieldname === fieldname && f.tablename === 'orderscurrent')
        || fieldDefinitions.find(f => f.fieldname === fieldname);

      if (fieldDef) {
        // Location columns get an "ALIAS - Full Name" hover tooltip (shared with
        // the LC dashboards); non-location columns pass through unchanged.
        columns.push(decorateLocationColumn(generateColumnDef(fieldDef), fieldname));
      } else {
        // Fallback: create a basic column if no field definition found
        columns.push(decorateMuleDriverColumn({
          accessorKey: apiFieldName,
          header: fieldname,
          enableSorting: true,
          enableColumnFilter: true,
          filterFn: 'arrIncludes',
          cell: (info) => info.getValue() || '',
        }, apiFieldName));
      }
    }

    return columns;
  }, [customColumnDefs, fieldDefinitions]);

  const generateBucketColumns = useCallback((
    bucket: string,
    additionalFields: string[] = []
  ): ColumnDef<any>[] => {
    // Get the ordered field list for this bucket
    const bucketFields = getBucketFields(bucket);
    let allFields = [...bucketFields, ...additionalFields.filter(f => !bucketFields.includes(f))];

    // For scheduled-not-dispatched, exclude d_legmiles from display columns
    // (it's still fetched for computing the mile band, but not shown as a column)
    if (bucket === 'scheduled-not-dispatched') {
      allFields = allFields.filter(f => f !== 'd_legmiles');
    }

    const columns = generateColumnsForFields(allFields);

    // Add computed columns for scheduled-not-dispatched bucket (used for grouping)
    if (bucket === 'scheduled-not-dispatched') {
      if (customColumnDefs['d_legmiles_band']) {
        columns.push(customColumnDefs['d_legmiles_band']);
      }
    }

    return columns;
  }, [generateColumnsForFields, customColumnDefs]);


  // Mismatched drivers columns — consecutive legs with different drivers
  const mismatchedDriversColumns = useMemo<ColumnDef<any>[]>(() => [
    proNumberColumn,
    {
      accessorKey: 'legnum',
      header: 'Leg#',
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      cell: (info) => info.getValue() ?? '',
    },
    {
      accessorKey: 'next_legnum',
      header: 'Next Leg#',
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      cell: (info) => info.getValue() ?? '',
    },
    {
      accessorKey: 'destapptdate',
      header: 'Dest Appt',
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      cell: (info) => {
        const value = info.getValue() as string;
        if (!value || value === '0000-00-00') return '';
        return value;
      },
    },
    {
      accessorKey: 'next_destapptdate',
      header: 'Next Dest Appt',
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      cell: (info) => {
        const value = info.getValue() as string;
        if (!value || value === '0000-00-00') return '';
        return value;
      },
    },
    {
      accessorKey: 'driver',
      header: 'Driver',
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      cell: (info) => <span>{String(info.getValue() || '')}<MuleBadge isMule={info.row.original.driverIsMule} /></span>,
    },
    {
      accessorKey: 'next_driver',
      header: 'Next Driver',
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      cell: (info) => <span>{String(info.getValue() || '')}<MuleBadge isMule={info.row.original.next_driverIsMule} /></span>,
    },
  ], [proNumberColumn]);


  // Invalid Container Numbers columns — orders whose legdocumentinfo container
  // value fails the (loose) ISO 6346 check. Final invalid filter runs server-side.
  const invalidContainerNumbersColumns = useMemo<ColumnDef<any>[]>(() => [
    proNumberColumn,
    {
      accessorKey: 'container',
      header: 'Container',
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      cell: (info) => info.getValue() || '',
    },
    {
      accessorKey: 'dispatcher',
      header: 'Dispatcher',
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      cell: (info) => info.getValue() || '',
    },
    {
      accessorKey: 'enteredby',
      header: 'Entered By',
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      cell: (info) => info.getValue() || '',
    },
  ], [proNumberColumn]);


  // Fixed columns these custom cards always render (matching each bucket's
  // bespoke backend SELECT). User-selected additional fields are filtered
  // against these so we never render a duplicate column for a base field.
  const CHASSIS_BASE_FIELDS = [
    'pronumber', 'dispatcher', 'dispatchername',
    'chassisenteredby', 'chassisprovider', 'chassisid', 'isvalidchassis',
  ];
  const INVALID_CONTAINER_BASE_FIELDS = [
    'pronumber', 'container', 'dispatcher', 'enteredby',
  ];
  const MISSING_WAITING_TIME_BASE_FIELDS = [
    'pronumber', 'keyaccount', 'container', 'movetype', 'scac',
    'dpdriver', 'dpeventdate', 'dpintime', 'dpouttime', 'dplocation',
    'dpdescription', 'dprecno',
  ];

  // Not Validated Chassis columns. Editable Valid? dropdown + Comments field;
  // every other column is read-only. PRO# reuses the shared clickable column.
  // Rows default to 'N' (any pro in this bucket is currently flagged 'N' on the
  // server); only entries in `chassisEdits` represent user changes.
  const notValidatedChassisColumns = useMemo<ColumnDef<any>[]>(() => [
    proNumberColumn,
    {
      accessorKey: 'dispatchername',
      header: 'Dispatcher',
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      cell: (info) => info.getValue() || (info.row.original as any)?.dispatcher || '',
    },
    {
      accessorKey: 'chassisenteredby',
      header: 'Entered By',
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      cell: (info) => info.getValue() || '',
    },
    {
      accessorKey: 'chassisprovider',
      header: 'Pool',
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      cell: (info) => info.getValue() || '',
    },
    {
      accessorKey: 'chassisid',
      header: 'Chassis ID',
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      cell: (info) => info.getValue() || '',
    },
    {
      accessorKey: 'isvalidchassis',
      header: 'Valid?',
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      meta: {
        bulkEdit: {
          type: 'dropdown',
          options: [
            { value: 'N', label: 'No' },
            { value: 'Y', label: 'Yes' },
            { value: '', label: '(blank)' },
          ],
        } satisfies BulkEditMeta,
      },
      cell: (info) => {
        const pro = String((info.row.original as any)?.pronumber ?? '');
        const edit = chassisEditsRef.current[pro] ?? { value: 'N' as const, comment: '' };
        return (
          <select
            value={edit.value}
            disabled={chassisSavingRef.current}
            onChange={(e) => setChassisValid(pro, e.target.value as ChassisRowEdit['value'])}
            onClick={(e) => e.stopPropagation()}
            className="text-xs border border-gray-300 dark:border-gray-600 bg-panel rounded px-1 py-0.5 focus:outline-none focus:ring-1 focus:ring-blue-500"
          >
            <option value="N">No</option>
            <option value="Y">Yes</option>
            <option value="">(blank)</option>
          </select>
        );
      },
    },
    {
      id: 'chassis_comment',
      header: 'Comments',
      enableSorting: false,
      enableColumnFilter: false,
      meta: {
        bulkEdit: { type: 'text' } satisfies BulkEditMeta,
      },
      cell: (info) => {
        const pro = String((info.row.original as any)?.pronumber ?? '');
        const edit = chassisEditsRef.current[pro] ?? { value: 'N' as const, comment: '' };
        const isY = edit.value === 'Y';
        const missing = isY && !edit.comment.trim();
        return (
          <input
            type="text"
            value={edit.comment}
            disabled={!isY || chassisSavingRef.current}
            placeholder={isY ? 'Required when marking Yes' : ''}
            onChange={(e) => setChassisComment(pro, e.target.value)}
            onClick={(e) => e.stopPropagation()}
            className={`w-full text-xs border rounded px-2 py-0.5 focus:outline-none focus:ring-1 ${
              missing
                ? 'border-red-500 bg-red-50 dark:bg-red-900/20'
                : 'border-gray-300 dark:border-gray-600 bg-panel'
            } ${!isY ? 'opacity-50 cursor-not-allowed' : 'focus:ring-blue-500'}`}
          />
        );
      },
    },
  ], [proNumberColumn, setChassisValid, setChassisComment]);

  // Declined/Cancelled Legs columns — one row per declined leg (dispatch_recno).
  // Resolve checkbox + optional comment are editable; everything else is read-only.
  const DECLINED_BASE_FIELDS = [
    'dispatch_recno', 'pronumber', 'driver_name', 'unitnumber', 'muledriver',
    'driver_dispatcher', 'origin', 'destination', 'rejectdatetime', 'rejectreason', 'rejectcomment',
  ];
  const declinedLegsColumns = useMemo<ColumnDef<any>[]>(() => [
    proNumberColumn,
    {
      id: 'driver',
      header: 'Driver',
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      accessorFn: (r: any) => `${r.driver_name || ''} ${r.unitnumber || ''}`.trim(),
      cell: (info) => {
        const r = info.row.original as any;
        return (
          <span>
            {r.driver_name || '-'}
            <MuleBadge isMule={r.muledriver === 'Y'} />
            {' '}
            <span className="text-xs text-muted">{r.unitnumber}</span>
          </span>
        );
      },
    },
    {
      accessorKey: 'driver_dispatcher',
      header: 'Driver Dsp',
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      cell: (info) => info.getValue() || '',
    },
    {
      accessorKey: 'origin',
      header: 'Origin',
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      cell: (info) => info.getValue() || '',
    },
    {
      accessorKey: 'destination',
      header: 'Destination',
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      cell: (info) => info.getValue() || '',
    },
    {
      accessorKey: 'rejectdatetime',
      header: 'Reject Date',
      enableSorting: true,
      sortingFn: 'datetime',
      cell: (info) => formatDateTime(info.getValue() as string | null),
    },
    {
      accessorKey: 'rejectreason',
      header: 'Reject Reason',
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      cell: (info) => info.getValue() || '',
    },
    {
      accessorKey: 'rejectcomment',
      header: 'Reject Comment',
      enableSorting: false,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      cell: (info) => info.getValue() || '',
    },
    {
      id: 'resolve',
      header: 'Resolve',
      enableSorting: false,
      enableColumnFilter: false,
      meta: {
        bulkEdit: {
          type: 'dropdown',
          options: [
            { value: 'true', label: 'Resolve' },
            { value: '', label: 'Leave' },
          ],
        } satisfies BulkEditMeta,
      },
      cell: (info) => {
        const key = String((info.row.original as any)?.dispatch_recno ?? '');
        const edit = declinedEditsRef.current[key] ?? EMPTY_EDIT;
        return (
          <input
            type="checkbox"
            aria-label={`Resolve ${(info.row.original as any).pronumber}`}
            checked={edit.resolve}
            disabled={declinedSavingRef.current}
            onChange={(e) => setDeclinedResolve(key, e.target.checked)}
            onClick={(e) => e.stopPropagation()}
          />
        );
      },
    },
    {
      id: 'resolve_comment',
      header: 'Resolve Comment',
      enableSorting: false,
      enableColumnFilter: false,
      meta: {
        bulkEdit: { type: 'text' } satisfies BulkEditMeta,
      },
      cell: (info) => {
        const key = String((info.row.original as any)?.dispatch_recno ?? '');
        const edit = declinedEditsRef.current[key] ?? EMPTY_EDIT;
        return (
          <input
            type="text"
            aria-label={`Resolve comment ${(info.row.original as any).pronumber}`}
            value={edit.comment}
            maxLength={255}
            disabled={!edit.resolve || declinedSavingRef.current}
            onChange={(e) => setDeclinedComment(key, e.target.value)}
            onClick={(e) => e.stopPropagation()}
            className={`w-full text-xs border rounded px-2 py-0.5 border-gray-300 dark:border-gray-600 bg-panel focus:outline-none focus:ring-1 focus:ring-blue-500 ${!edit.resolve ? 'cursor-not-allowed' : ''}`}
          />
        );
      },
    },
  ], [proNumberColumn, setDeclinedResolve, setDeclinedComment]);

  // Declined Dispatches (24h) columns — a read-only copy of the Declined
  // Dispatches report grid (every decline in the last 24 hours, resolved or
  // not, newest first). One row per declined leg (dispatch_recno).
  const DECLINED_24H_BASE_FIELDS = [
    'dispatch_recno', 'pronumber', 'driver_name', 'unitnumber', 'muledriver',
    'driver_dispatcher', 'origin', 'destination', 'rejectdatetime', 'rejectreason', 'rejectcomment',
    'createdatetime', 'estimateddriverpay', 'resolvedatetime', 'resolvedby',
  ];
  const declinedLast24hColumns = useMemo<ColumnDef<any>[]>(() => [
    proNumberColumn,
    {
      id: 'driver',
      header: 'Driver',
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      accessorFn: (r: any) => `${r.driver_name || ''} ${r.unitnumber || ''}`.trim(),
      cell: (info) => {
        const r = info.row.original as any;
        return (
          <span>
            {r.driver_name || '-'}
            <MuleBadge isMule={r.muledriver === 'Y'} />
            {' '}
            <span className="text-xs text-muted">{r.unitnumber}</span>
          </span>
        );
      },
    },
    {
      accessorKey: 'origin',
      header: 'Origin',
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      cell: (info) => info.getValue() || '-',
    },
    {
      accessorKey: 'destination',
      header: 'Destination',
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      cell: (info) => info.getValue() || '-',
    },
    {
      accessorKey: 'rejectreason',
      header: 'Reason',
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      cell: (info) => info.getValue() || '-',
    },
    {
      accessorKey: 'rejectcomment',
      header: 'Comment',
      enableSorting: false,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      cell: (info) => info.getValue() || '-',
    },
    {
      accessorKey: 'rejectdatetime',
      header: 'Reject Time',
      enableSorting: true,
      sortingFn: 'datetime',
      cell: (info) => formatDateTime(info.getValue() as string | null),
    },
    {
      accessorKey: 'createdatetime',
      header: 'Created Time',
      // When the DECLINE was recorded, not the order's createdatetime.
      meta: { registryTitle: false },
      enableSorting: true,
      sortingFn: 'datetime',
      cell: (info) => formatDateTime(info.getValue() as string | null),
    },
    {
      accessorKey: 'estimateddriverpay',
      header: 'Est. Driver Pay',
      enableSorting: true,
      // Same as the report: no pay (null or 0.00) renders as a dash.
      cell: (info) => {
        const n = Number(info.getValue());
        return Number.isFinite(n) && n !== 0 ? formatCurrency(n) : '-';
      },
    },
    {
      accessorKey: 'resolvedby',
      header: 'Resolved By',
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      cell: (info) => info.getValue() || '-',
    },
  ], [proNumberColumn]);


  // Missing Waiting Time columns — one row per driverpay_lineitem (WAITING TIME
  // variants) with intime/outtime missing. The Event Date cell is a button that
  // opens DriverPayAdditionModal for the row's dprecno.
  const missingWaitingTimeColumns = useMemo<ColumnDef<any>[]>(() => [
    proNumberColumn,
    {
      accessorKey: 'keyaccount',
      header: 'Key Account',
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      cell: (info) => info.getValue() || '',
    },
    {
      accessorKey: 'container',
      header: 'Container',
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      cell: (info) => info.getValue() || '',
    },
    {
      accessorKey: 'movetype',
      header: 'Move Type',
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      cell: (info) => info.getValue() || '',
    },
    {
      accessorKey: 'scac',
      header: 'SCAC',
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      cell: (info) => info.getValue() || '',
    },
    {
      accessorKey: 'dpdriver',
      header: 'Driver',
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      cell: (info) => <>{String(info.getValue() || '')}<MuleBadge isMule={info.row.original.dpdriverIsMule} /></>,
      meta: { exportValue: (row: { original: Record<string, unknown> }) => `${row.original.dpdriver || ''}${row.original.dpdriverIsMule ? ' (Mule)' : ''}` },
    },
    {
      accessorKey: 'dpeventdate',
      header: 'Event Date',
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      cell: (info) => {
        const value = info.getValue() as string | null | undefined;
        const row = info.row.original as { pronumber?: number; dprecno?: number };
        if (!row?.pronumber || !row?.dprecno) return value || '';
        const display = (() => {
          if (!value || value === '0000-00-00') return '';
          const dateValue = new Date(value + 'T00:00:00');
          if (isNaN(dateValue.getTime())) return value;
          const currentYear = new Date().getFullYear();
          const month = String(dateValue.getMonth() + 1).padStart(2, '0');
          const day = String(dateValue.getDate()).padStart(2, '0');
          if (dateValue.getFullYear() !== currentYear) {
            return `${month}/${day}/${String(dateValue.getFullYear()).slice(-2)}`;
          }
          return `${month}/${day}`;
        })();
        return (
          <button
            onClick={(e) => {
              e.stopPropagation();
              setDriverPayAddition({ pronumber: row.pronumber!, recno: row.dprecno! });
            }}
            className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 underline cursor-pointer"
            title="Open driver-pay addition"
          >
            {display || '(open)'}
          </button>
        );
      },
    },
    {
      accessorKey: 'dpintime',
      header: 'In Time',
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      cell: (info) => info.getValue() || '',
    },
    {
      accessorKey: 'dpouttime',
      header: 'Out Time',
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      cell: (info) => info.getValue() || '',
    },
    {
      accessorKey: 'dplocation',
      header: 'Location',
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      cell: (info) => info.getValue() || '',
    },
    {
      accessorKey: 'dpdescription',
      header: 'Description',
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      cell: (info) => info.getValue() || '',
    },
  ], [proNumberColumn]);


  // Render card content - placeholder for dynamic cards
  // isFullScreen is used when rendering single card in full-screen mode
  /**
   * The documents table for `tableKey`, or null when that table is showing orders.
   *
   * A helper rather than inline JSX per host: every action-bar host needs the same
   * early return, and they are spread across card bodies and grid-cell drills.
   */
  const renderDocumentsFor = useCallback((tableKey: string) => {
    const docs = docsViewCards[tableKey];
    if (!docs) return null;
    return (
      <CardDocumentsTable
        pros={docs.pros}
        cardTitle={docs.title}
        settingsKey="dispatch-card-documents"
        onProClick={setSelectedProNumber}
        defaultRecipient={currentUserEmail}
        onBackToOrders={() => closeCardDocuments(tableKey)}
        initialSettings={tableSettings['table_dispatch-card-documents']}
        settingsReady={tableSettingsLoaded}
        settingsLoadFailed={tableSettingsLoadFailed}
        onSettingsSaved={handleTableSettingsSaved}
      />
    );
  }, [docsViewCards, currentUserEmail, closeCardDocuments, tableSettings,
      tableSettingsLoaded, tableSettingsLoadFailed, handleTableSettingsSaved]);

  const renderCardContent = (cardKey: string, isFullScreen: boolean = false) => {
    const cardConfig = cardConfigs.find(c => c.key === cardKey);
    if (!cardConfig) return null;

    // Documents mode: the SAME single table, repainted with document rows/columns.
    // The card keeps its own chrome (title, toolbar, action bar) — only the contents
    // change — so this never reads as a table nested inside the card.
    const cardDocs = renderDocumentsFor(cardKey);
    if (cardDocs) return cardDocs;

    // Check if card has filter configuration or is a bucket-based card
    const hasFilters = (cardConfig.filterConditions && cardConfig.filterConditions.length > 0) || cardConfig.bucket;
    const data = cardData[cardKey] || [];  // Backend now handles filtering
    const loading = cardDataLoading[cardKey] || false;

    // If card has filters or bucket, show DispatchTable with results
    if (hasFilters) {
      const currentHeight = cardConfig.cardHeight || 'lg';

      // Use persisted grouping/tabGrouping, or fall back to defaults for bucket cards
      let defaultGrouping: string[] | undefined = cardConfig.grouping;

      // Determine tab grouping field - use persisted value, or fall back to defaults for bucket cards
      // Note: 'none' is a special marker that means the user explicitly disabled tab grouping
      let tabGroupingField = cardConfig.tabGrouping;
      // Migrate stale 'destapptdate' tab grouping (early dispatches-not-accepted draft used that alias
      // before the bucket was switched to the field-def `leg_destapptdate`).
      if (cardConfig.bucket === 'dispatches-not-accepted' && tabGroupingField === 'destapptdate') {
        tabGroupingField = 'leg_destapptdate';
        setCardConfigs(prev => prev.map(card =>
          card.key === cardKey ? { ...card, tabGrouping: 'leg_destapptdate' } : card
        ));
      }
      const userExplicitlyDisabledTabs = tabGroupingField === 'none';

      // If tabGrouping is not set (undefined or empty string, but NOT 'none') and this is a bucket card, use defaults
      // Note: dispatched-driver-oos and drop-released-not-dispatched should NOT have default tab grouping
      if (!tabGroupingField && cardConfig.bucket && cardConfig.bucket !== 'dispatched-driver-oos' && cardConfig.bucket !== 'drop-released-not-dispatched' && cardConfig.bucket !== 'scheduled-not-dispatched') {
        if (cardConfig.bucket === 'per-diem-not-dispatched') {
          tabGroupingField = 'perdiemstartdate';
        } else if (cardConfig.bucket === 'cutoff-not-dispatched' || cardConfig.bucket === 'cutoff-not-terminated') {
          tabGroupingField = 'cutoffdatetime';
        } else if (cardConfig.bucket === 'dispatches-not-accepted') {
          tabGroupingField = 'leg_destapptdate';
        }

        // Persist the default tab grouping to the card config so it's saved
        if (tabGroupingField) {
          setCardConfigs(prev => prev.map(card =>
            card.key === cardKey && !card.tabGrouping ? { ...card, tabGrouping: tabGroupingField } : card
          ));
        }
      }

      // Custom grouping for scheduled-not-dispatched: Add computed fields for mile bands and past-due grouping
      let enhancedData = data;
      if (cardConfig.bucket === 'scheduled-not-dispatched') {
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        // Add computed fields to each row
        enhancedData = data.map((row: any) => {
          const miles = Number(row.d_legmiles) || 0;
          let mileBand = '';
          if (miles < 300) {
            mileBand = '0-300 Miles';
          } else if (miles < 550) {
            mileBand = '300-550 Miles';
          } else if (miles < 850) {
            mileBand = '550-850 Miles';
          } else {
            mileBand = '850+ Miles';
          }

          return {
            ...row,
            d_legmiles_band: mileBand
          };
        });
      }

      // Set default grouping to mile bands if not already set for scheduled-not-dispatched
      if (cardConfig.bucket === 'scheduled-not-dispatched' && !tabGroupingField) {
        tabGroupingField = 'd_legmiles_band';

        // Persist the default tab grouping and table grouping (use grouped date field)
        setCardConfigs(prev => prev.map(card =>
          card.key === cardKey && !card.tabGrouping ? { ...card, tabGrouping: 'd_legmiles_band' } : card
        ));
      }

      // If user explicitly disabled tabs, clear the field
      if (userExplicitlyDisabledTabs) {
        tabGroupingField = undefined;
      }

      // Show tabs only if tabGroupingField is set (either from user selection or from defaults)
      let showTabs = !!tabGroupingField;

      // Determine which columns to use based on the bucket type. Mismatched
      // Drivers has a leg-pair row shape with no order context, so it stays a
      // fixed hand-rolled column array. Not Validated Chassis, Invalid Container
      // Numbers, and Missing Waiting Time keep their hand-rolled base columns
      // but append any user-selected order-level fields (the backend adds them
      // to each bespoke query); everything else uses fully dynamic generation
      // from the bucket's required fields + the user's additions.
      const columnsToUse = cardKey === 'mismatched-drivers'
        ? mismatchedDriversColumns
        : cardKey === 'invalid-container-numbers'
        ? [
            ...invalidContainerNumbersColumns,
            ...generateColumnsForFields(
              (cardConfig.additionalFields || []).filter(
                (f) => !INVALID_CONTAINER_BASE_FIELDS.includes(f)
              )
            ),
          ]
        : cardKey === 'missing-waiting-time'
        ? [
            ...missingWaitingTimeColumns,
            ...generateColumnsForFields(
              (cardConfig.additionalFields || []).filter(
                (f) => !MISSING_WAITING_TIME_BASE_FIELDS.includes(f)
              )
            ),
          ]
        : cardKey === 'not-validated-chassis'
        ? [
            ...notValidatedChassisColumns,
            ...generateColumnsForFields(
              (cardConfig.additionalFields || []).filter(
                (f) => !CHASSIS_BASE_FIELDS.includes(f)
              )
            ),
          ]
        : cardKey === 'declined-cancelled-legs'
        ? [
            ...declinedLegsColumns,
            ...generateColumnsForFields(
              (cardConfig.additionalFields || []).filter(
                (f) => !DECLINED_BASE_FIELDS.includes(f)
              )
            ),
          ]
        : cardKey === 'declined-last-24h'
        ? [
            ...declinedLast24hColumns,
            ...generateColumnsForFields(
              (cardConfig.additionalFields || []).filter(
                (f) => !DECLINED_24H_BASE_FIELDS.includes(f)
              )
            ),
          ]
        : generateBucketColumns(cardConfig.bucket || cardKey, cardConfig.additionalFields || []);

      // Hardcoded-column buckets above use a fixed row shape (no dynamic field
      // picker), so default-layout editing — which maps fields to additionalFields
      // — doesn't apply to them. Only dynamic-column shared buckets get the editor.
      const supportsDynamicColumns = cardKey !== 'mismatched-drivers'
        && cardKey !== 'invalid-container-numbers'
        && cardKey !== 'missing-waiting-time'
        && cardKey !== 'not-validated-chassis'
        && cardKey !== 'declined-cancelled-legs'
        && cardKey !== 'declined-last-24h';

      // Build the per-bucket header-extra slot. For Not Validated Chassis this
      // is a Save button + a brief tally of pending changes.
      // Mass actions in this card's action bar: Change All / Change Template for
      // All (gated by the order-lc-fields permission) + Trace All, each scoped to
      // this card's visible/filtered rows.
      const cardBulkActions = buildProBulkActions({
        canChangeAll: canUseChangeAll,
        canChangeTemplate: canEditOrderLcFields,
        onChangeAll: (pros) => openChangeAll(pros),
        onChangeTemplateAll: (pros) => openChangeTemplateForAll(pros),
        onViewDocuments: (pros) => openCardDocuments(cardKey, pros, cardConfig.title),
      });
      const scopeMeta = cardScopeMeta[cardKey];
      // Composed into headerExtra rather than replacing it — the not-validated-chassis
      // card already owns that slot for its save/clear controls.
      const resultPill = (
        <CardResultPill
          includeClosed={Boolean(scopeMeta?.includeClosed)}
          truncated={scopeMeta?.truncated}
          limit={scopeMeta?.limit}
        />
      );
      let headerExtraNode: React.ReactNode = undefined;
      if (cardConfig.bucket === 'not-validated-chassis') {
        const pendingY = Object.values(chassisEdits).filter((e) => e.value === 'Y').length;
        const pendingBlank = Object.values(chassisEdits).filter((e) => e.value === '').length;
        const missingComment = Object.values(chassisEdits).filter((e) => e.value === 'Y' && !e.comment.trim()).length;
        const hasPending = pendingY > 0 || pendingBlank > 0;
        const canSave = hasPending && missingComment === 0 && !chassisSaving && !loading;
        const saveTitle = !hasPending
          ? 'No changes to save'
          : missingComment > 0
          ? 'Resolve missing comments before saving'
          : 'Save chassis validations';
        headerExtraNode = (
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleSaveChassisValidations}
              disabled={!canSave}
              title={saveTitle}
              className={`text-xs px-3 py-1 rounded font-medium transition-colors ${
                canSave
                  ? 'bg-blue-600 hover:bg-blue-700 text-white'
                  : 'bg-slate-600 text-gray-400 cursor-not-allowed'
              }`}
            >
              {chassisSaving ? 'Saving…' : 'Save'}
            </button>
            {hasPending && (
              <button
                type="button"
                onClick={() => setChassisEdits({})}
                disabled={chassisSaving}
                title="Discard all pending chassis edits"
                className="text-xs px-3 py-1 rounded font-medium transition-colors bg-slate-600 hover:bg-slate-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Clear
              </button>
            )}
            {hasPending && (
              <span className="text-xs text-amber-300">
                {pendingY > 0 && <span className="mr-2">{pendingY} to validate</span>}
                {pendingBlank > 0 && <span>{pendingBlank} to clear</span>}
              </span>
            )}
            {missingComment > 0 && (
              <span className="text-xs text-red-300">{missingComment} missing comment</span>
            )}
          </div>
        );
      } else if (cardConfig.bucket === 'declined-cancelled-legs') {
        const pendingResolve = Object.values(declinedEdits).filter((e) => e.resolve).length;
        const hasPending = pendingResolve > 0;
        const canSave = hasPending && !declinedSaving && !loading;
        const saveTitle = !hasPending ? 'No changes to save' : 'Resolve selected declined legs';
        headerExtraNode = (
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleSaveDeclinedResolves}
              disabled={!canSave}
              title={saveTitle}
              className={`text-xs px-3 py-1 rounded font-medium transition-colors ${
                canSave
                  ? 'bg-blue-600 hover:bg-blue-700 text-white'
                  : 'bg-slate-600 text-gray-400 cursor-not-allowed'
              }`}
            >
              {declinedSaving ? 'Saving…' : 'Save'}
            </button>
            {hasPending && (
              <button
                type="button"
                onClick={() => setDeclinedEdits({})}
                disabled={declinedSaving}
                title="Discard all pending resolve edits"
                className="text-xs px-3 py-1 rounded font-medium transition-colors bg-slate-600 hover:bg-slate-700 text-white disabled:bg-slate-700 disabled:text-gray-400 disabled:cursor-not-allowed"
              >
                Clear
              </button>
            )}
            {hasPending && (
              <span className="text-xs text-amber-300">{pendingResolve} to resolve</span>
            )}
          </div>
        );
      }

      return (
        <div className="w-full h-full">
          <TanTable
            data={enhancedData}
            columns={columnsToUse}
            loading={loading}
            errorMessage={cardErrors[cardKey]}
            onRetry={() => fetchCardData(cardKey, cardConfig.filterConditions || [], cardConfig.filterView || 'Orders', cardConfig.bucket, false, cardConfig.additionalFields)}
            settingsKey={`dispatch-card-${cardKey}`}
            {...(customCardScopeKey(cardKey)
              ? { templateScopeKey: customCardScopeKey(cardKey) }
              : {})}
            initialSettings={tableSettings[`table_dispatch-card-${cardKey}`]}
            defaultSettings={cardDefaults[`dispatch-card-${cardKey}`]}
            canEditDefaults={systemBucketDefaults.has(cardKey) && supportsDynamicColumns}
            onSaveDefaults={handleSaveDefaults}
            mandatoryGroupingFields={getMandatoryGroupingFields(cardConfig.bucket || cardKey)}
            settingsReady={tableSettingsLoaded}
            settingsLoadFailed={tableSettingsLoadFailed}
            onSettingsSaved={handleTableSettingsSaved}
            searchPlaceholder="Search orders..."
            fieldName={cardKey}
            enableGrouping={true}
            showTabs={showTabs}
            allowTabFieldSelection={false}
            defaultGrouping={defaultGrouping}
            tabGroupingField={tabGroupingField}
            enableColumnFilters={true}
            cardFilterConditions={cardConfig.filterConditions || []}
            cardTitle={cardConfig.title}
            onEditFilters={() => handleEditFilters(cardKey)}
            showEditFiltersButton={!systemBucketDefaults.has(cardKey)}
            editFiltersButtonIcon="cog"
            fillContainer={true}
            getGroupedRowClassName={
              cardConfig.bucket === 'scheduled-not-dispatched'
                ? (groupValue: any, columnId: string, subRows: any[]) => {
                    // Default: no special highlighting
                    return 'bg-theme-bg-tertiary';
                  }
                : undefined
            }
            onGroupingChange={(grouping) => handleGroupingChange(cardKey, grouping)}
            onTabGroupingChange={(tabGrouping) => handleTabGroupingChange(cardKey, tabGrouping)}
            onVisibleColumnsChange={(visibleColumns) => handleVisibleColumnsChange(cardKey, visibleColumns)}
            availableFields={cardKey !== 'mismatched-drivers' ? availableFields : undefined}
            onAdditionalFieldsChange={cardKey !== 'mismatched-drivers' ? (fields) => handleAdditionalFieldsChange(cardKey, fields) : undefined}
            additionalFields={cardConfig.additionalFields || []}
            onDuplicate={cardKey === 'trouble-tickets-dispatcher' || cardKey === 'mismatched-drivers' || cardKey === 'invalid-container-numbers' || cardKey === 'missing-waiting-time' || cardKey === 'not-validated-chassis' || cardKey === 'declined-cancelled-legs' || cardKey === 'declined-last-24h' ? undefined : () => handleDuplicate(cardConfig)}
            duplicateTooltip={cardKey === 'trouble-tickets-dispatcher' || cardKey === 'mismatched-drivers' || cardKey === 'invalid-container-numbers' || cardKey === 'missing-waiting-time' || cardKey === 'not-validated-chassis' || cardKey === 'declined-cancelled-legs' || cardKey === 'declined-last-24h' ? undefined : "Duplicate Card"}
            onBulkEdit={cardKey === 'not-validated-chassis' ? handleChassisBulkEdit : cardKey === 'declined-cancelled-legs' ? handleDeclinedBulkEdit : undefined}
            headerExtra={
              // The pill renders for a closed-inclusive card OR a capped one, and the
              // cap can fire in either scope — so this can't be gated on includeClosed.
              scopeMeta?.includeClosed || scopeMeta?.truncated ? (
                <div className="flex items-center gap-2">
                  {resultPill}
                  {headerExtraNode}
                </div>
              ) : (
                headerExtraNode
              )
            }
            bulkActions={cardBulkActions}
            cardLocked={cardConfig.locked || false}
            onToggleCardLock={(locked) => handleToggleCardLock(cardKey, locked)}
            collapsed={collapsed[cardKey] || false}
            multiTabSelect={true}
            activeTabs={cardActiveTabsMulti[cardKey]}
            onActiveTabsChange={(tabIds: string[]) => handleMultiTabChange(cardKey, tabIds)}
            tableHeaderBgColor={getTableHeaderBgColor(cardKey, data)}
            onToggleCollapse={() => toggleCollapse(cardKey)}
            globalResetTrigger={globalResetTrigger}
            globalPageSize={globalPageSize}
            globalShowFilters={globalShowFilters}
            globalSuperCompact={globalSuperCompact}
            globalCardHeight={globalCardHeight}
          />
        </div>
      );
    }

    // This should never happen - cards should only be created through the modal with filters
    // If a card somehow exists without filters, delete it
    return null;
  };

  // Render individual card (GridStack version - no drag handlers, GridStack handles drag/resize)
  const renderCard = (cardKey: string) => {
    const cardConfig = cardConfigs.find(c => c.key === cardKey);
    if (!cardConfig) return null;

    // In locked mode, don't show hidden cards
    if (!cardConfig.visible) return null;

    return (
      <div
        key={cardKey}
        className="h-full w-full"
      >
        <PermissionConfigurable
          componentId={`dispatch-${cardKey}`}
          componentName={cardConfig.title}
          className="h-full w-full"
        >
          <div className={`overflow-hidden ${!(cardConfig.filterConditions && cardConfig.filterConditions.length > 0) ? 'bg-gray-50 dark:bg-gray-800 shadow rounded-lg border border-gray-300 dark:border-gray-600' : ''}`}>
              {renderCardContent(cardKey)}
            </div>
        </PermissionConfigurable>
      </div>
    );
  };

  // Show loading indicator while configs or field definitions are being loaded
  // Also wait if field definitions are empty (still loading or need refetch)
  if (!configsLoaded || fieldDefinitionsLoading || (fieldDefinitions.length === 0 && !fieldDefinitionsError)) {
    const mins = Math.floor(dashboardLoadSeconds / 60);
    const secs = dashboardLoadSeconds % 60;
    const timeStr = `${mins}:${secs.toString().padStart(2, '0')}`;
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 dark:border-blue-400 mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading dashboard... {timeStr}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`${styles.pageContainer} ${showGridBorders ? styles.pageContainerBorders : ''} ${showGridHighlight ? styles.pageContainerHighlight : ''} ${showGridLabels ? styles.pageContainerLabels : ''}`}>
      {/* Tab Bar */}
      <div className={`${styles.tabBar} min-w-0 sticky top-0 z-20 bg-white dark:bg-gray-800 shadow-sm`} style={{ position: 'relative' }}>
        <GridLabel areaName="tabBar" position="top-right" />
        {/* Workspace Bar */}
        <div className="flex items-center min-w-0">
          {/* min-w-0: without it this flex item sizes to the tab strip's content
              and the whole page scrolls sideways instead of the strip itself. */}
          <div className="flex-1 min-w-0">
            <TabBar
              tabs={workspaceTabs}
              onTabChange={(tabId) => setCurrentWorkspaceId(tabId)}
              onAddTab={handleCreateWorkspace}
              onEditTab={handleRenameWorkspace}
              onDeleteTab={handleDeleteWorkspace}
              onReorderTabs={handleReorderWorkspaces}
              maxTabs={Infinity}
              protectedTabIds={PROTECTED_WORKSPACE_IDS}
              editable={true}
              noBorder
              compact
            />
          </div>
          {/* Collapse/Expand All Buttons & Global Controls */}
          <div className="flex items-center gap-1 text-xs px-4 py-1 bg-panel border-b border-gray-300 dark:border-gray-600 self-stretch">
            <button
              onClick={collapseAllCards}
              className="p-1 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-200 dark:hover:bg-gray-700 rounded"
              title="Collapse All"
            >
              <ChevronUpIcon className="h-4 w-4" />
            </button>
            <button
              onClick={expandAllCards}
              className="p-1 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-200 dark:hover:bg-gray-700 rounded"
              title="Expand All"
            >
              <ChevronDownIcon className="h-4 w-4" />
            </button>
            <button
              onClick={handleOpenTrash}
              className="p-1 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-200 dark:hover:bg-gray-700 rounded"
              title="Trashcan - View deleted workspaces and cards"
            >
              <TrashIcon className="h-4 w-4" />
            </button>

            {/* Global Table Controls */}
            <div className="relative" ref={globalControlsRef}>
              <button
                onClick={(e) => {
                  if (!showGlobalControls) {
                    const rect = e.currentTarget.getBoundingClientRect();
                    setGlobalControlsPosition({
                      top: rect.bottom + 8,
                      right: window.innerWidth - rect.right,
                    });
                  }
                  setShowGlobalControls(!showGlobalControls);
                }}
                className={`p-1 rounded transition-colors ${
                  showGlobalControls
                    ? 'bg-blue-600 text-white'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-200 dark:hover:bg-gray-700'
                }`}
                title="Global Table Controls"
              >
                <AdjustmentsHorizontalIcon className="h-4 w-4" />
              </button>

              {showGlobalControls && globalControlsPosition && createPortal(
                <div
                  ref={globalControlsDropdownRef}
                  className="fixed w-64 bg-theme-bg-elevated border border-theme-border-primary rounded-lg shadow-lg z-[9999]"
                  style={{ top: globalControlsPosition.top, right: globalControlsPosition.right }}
                >
                  <div className="px-4 py-2 border-b border-theme-border-primary">
                    <p className="text-sm font-semibold text-heading">Global Table Controls</p>
                    <p className="text-xs text-muted">Apply to all tables</p>
                  </div>

                  {/* Reset All Tables */}
                  <div className="p-2 border-b border-theme-border-primary">
                    <button
                      onClick={() => {
                        setGlobalResetTrigger(prev => prev + 1);
                        setShowGlobalControls(false);
                      }}
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left rounded hover:bg-theme-bg-tertiary text-label"
                    >
                      <ArrowPathIcon className="h-4 w-4" />
                      Reset All Tables
                    </button>
                    <button
                      onClick={() => {
                        setShowGlobalControls(false);
                        setShowResetLayoutConfirm(true);
                      }}
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left rounded hover:bg-theme-bg-tertiary text-label"
                    >
                      <ArrowPathIcon className="h-4 w-4" />
                      Reset Grid Layout
                    </button>
                  </div>

                  {/* Show/Hide Filters */}
                  <div className="p-2 border-b border-theme-border-primary">
                    <p className="px-3 py-1 text-xs font-semibold text-muted uppercase">Column Filters</p>
                    <div className="flex gap-1 px-3 py-1">
                      <button
                        onClick={() => {
                          setGlobalShowFilters(true);
                          setShowGlobalControls(false);
                        }}
                        className={`flex items-center gap-1 px-3 py-1 text-xs font-medium rounded transition-colors ${
                          globalShowFilters === true
                            ? 'bg-blue-600 text-white'
                            : 'bg-theme-bg-secondary text-label hover:bg-theme-bg-tertiary'
                        }`}
                      >
                        <FunnelIcon className="h-3 w-3" />
                        Show
                      </button>
                      <button
                        onClick={() => {
                          setGlobalShowFilters(false);
                          setShowGlobalControls(false);
                        }}
                        className={`flex items-center gap-1 px-3 py-1 text-xs font-medium rounded transition-colors ${
                          globalShowFilters === false
                            ? 'bg-blue-600 text-white'
                            : 'bg-theme-bg-secondary text-label hover:bg-theme-bg-tertiary'
                        }`}
                      >
                        <FunnelIcon className="h-3 w-3" />
                        Hide
                      </button>
                    </div>
                  </div>

                  {/* Super Compact */}
                  <div className="p-2 border-b border-theme-border-primary">
                    <p className="px-3 py-1 text-xs font-semibold text-muted uppercase">Super Compact</p>
                    <p className="px-3 text-xs text-gray-400 dark:text-gray-500 mb-1">Click again to reset to individual</p>
                    <div className="flex gap-1 px-3 py-1">
                      <button
                        onClick={() => {
                          setGlobalSuperCompact(globalSuperCompact === true ? undefined : true);
                          setShowGlobalControls(false);
                        }}
                        className={`flex items-center gap-1 px-3 py-1 text-xs font-medium rounded transition-colors ${
                          globalSuperCompact === true
                            ? 'bg-blue-600 text-white'
                            : 'bg-theme-bg-secondary text-label hover:bg-theme-bg-tertiary'
                        }`}
                      >
                        <svg className="h-3 w-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <line x1="4" y1="6" x2="20" y2="6" />
                          <line x1="4" y1="10" x2="20" y2="10" />
                          <line x1="4" y1="14" x2="20" y2="14" />
                          <line x1="4" y1="18" x2="20" y2="18" />
                        </svg>
                        All On
                      </button>
                      <button
                        onClick={() => {
                          setGlobalSuperCompact(globalSuperCompact === false ? undefined : false);
                          setShowGlobalControls(false);
                        }}
                        className={`flex items-center gap-1 px-3 py-1 text-xs font-medium rounded transition-colors ${
                          globalSuperCompact === false
                            ? 'bg-blue-600 text-white'
                            : 'bg-theme-bg-secondary text-label hover:bg-theme-bg-tertiary'
                        }`}
                      >
                        <svg className="h-3 w-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <line x1="4" y1="5" x2="20" y2="5" />
                          <line x1="4" y1="12" x2="20" y2="12" />
                          <line x1="4" y1="19" x2="20" y2="19" />
                        </svg>
                        All Off
                      </button>
                    </div>
                  </div>

                  {/* Reset Layout */}
                  <div className="p-2">
                    <button
                      onClick={() => {
                        // Reset all cards to single column layout (w=12, h=2)
                        if (gridInstanceRef.current) {
                          const grid = gridInstanceRef.current;
                          const items = grid.getGridItems();
                          items.forEach((el, index) => {
                            grid.update(el, { x: 0, y: index * 2, w: 12, h: 2 });
                          });
                        }
                        setShowGlobalControls(false);
                      }}
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left rounded hover:bg-theme-bg-tertiary text-label"
                    >
                      <ArrowPathIcon className="h-4 w-4" />
                      Reset Layout (Single Column)
                    </button>
                  </div>
                </div>,
                document.body
              )}
            </div>
          </div>
        </div>

      </div>

      {/* Filter Bar - Shown on all workspaces so users always see active global filters */}
      <FilterBar label="Filters:" className={styles.filterBar}>
        <GridLabel areaName="filterBar" position="top-right" />
        {/* The Driver Board is a driver-based view: it reads the DRIVER dispatcher
            filter, and V1's customer filter was inert on this grid. */}
        {isDriverBoardTab ? (
          <DriverDispatcherFilter
            value={selectedDriverDispatchers}
            onChange={setSelectedDriverDispatchers}
            className="min-w-[180px]"
          />
        ) : (
          <OrderDispatcherFilter
            value={selectedDispatchers}
            onChange={setSelectedDispatchers}
            className="min-w-[180px]"
          />
        )}
        <MultiSelect
          options={regionOptions}
          value={selectedRegions}
          onChange={setSelectedRegions}
          placeholder="All Regions"
          showClearOption={true}
          clearOptionLabel="All Regions"
          enableSearch={true}
          searchPlaceholder="Search regions..."
          size="sm"
          className="min-w-[180px]"
        />
        {!isDriverBoardTab && (
          <MultiSelect
            options={customerOptions}
            value={selectedCustomers}
            onChange={setSelectedCustomers}
            placeholder="All Customers"
            showClearOption={true}
            clearOptionLabel="All Customers"
            enableSearch={true}
            searchPlaceholder="Search customers..."
            size="sm"
            className="min-w-[200px]"
          />
        )}
        {/* Clear only what is actually on screen in this mode. */}
        {(isDriverBoardTab
          ? selectedDriverDispatchers.length > 0 || selectedRegions.length > 0
          : selectedDispatchers.length > 0 || selectedCustomers.length > 0 || selectedRegions.length > 0) && (
          <button
            onClick={() => {
              if (isDriverBoardTab) {
                setSelectedDriverDispatchers([]);
                setSelectedRegions([]);
                return;
              }
              setSelectedDispatchers([]);
              setSelectedCustomers([]);
              setSelectedRegions([]);
            }}
            className="text-xs text-blue-600 dark:text-blue-400 hover:underline whitespace-nowrap"
          >
            Clear Filters
          </button>
        )}
      </FilterBar>

      {/* Body */}
      <div className={`${styles.body} p-2 ${isSingleCardSelected || isDriverBoardTab ? 'flex flex-col' : ''}`} style={{ position: 'relative' }}>
        <GridLabel areaName="body" position="top-right" />

        {/* Driver Board tab: the pinned board is the whole body. Mounted only in
            this mode (not CSS-hidden) so its 60s poll stops when the user leaves.
            The flex column above plus flex-1/min-h-0 here is what bounds the
            height its fillContainer TanTable needs to render any rows. */}
        {isDriverBoardTab && (
          <div className="flex-1 min-h-0 flex flex-col">
            <Suspense fallback={<div className="p-4 text-sm text-muted">Loading driver board…</div>}>
              <DriverBoardTab />
            </Suspense>
          </div>
        )}

        {/* Bucket counts (collapsible via the chevron) in normal flow */}
        {!isDriverBoardTab && hasAnyStats && (
          <div className={isSingleCardSelected ? 'mb-4 flex-shrink-0' : 'mb-4'}>
            {renderCategorizedStatsPanel()}
          </div>
        )}

        {/* Single Card Full Screen Mode */}
        {!isDriverBoardTab && isSingleCardSelected && (() => {
          const selectedKey = Array.from(visibleCardKeys)[0];
          const cardConfig = cardConfigs.find(c => c.key === selectedKey);
          if (!cardConfig) return null;
          return (
            <div key={selectedKey} className="flex-1 min-h-0 flex flex-col">
              <PermissionConfigurable
                componentId={`dispatch-${selectedKey}`}
                componentName={cardConfig.title}
                className="flex-1 flex flex-col min-h-0"
              >
                <div className="flex-1 min-h-0 bg-gray-50 dark:bg-gray-800 rounded-lg shadow-md border border-gray-300 dark:border-gray-600 overflow-hidden flex flex-col">
                  {renderCardContent(selectedKey, true)}
                </div>
              </PermissionConfigurable>
            </div>
          );
        })()}

        {/* Main board with no bucket selected: prompt the user to pick one. No card
            data is loaded until a bucket is chosen from the counts above. */}
        {isMainBoardUnselected && hasAnyStats && (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <FunnelIcon className="h-12 w-12 text-gray-400 dark:text-gray-500 mb-3" />
            <h3 className="text-lg font-medium text-heading mb-1">Select a bucket to view</h3>
            <p className="text-sm text-muted">
              Choose a bucket above to load and view its orders.
            </p>
          </div>
        )}

        {/* Main Grid Layout - GridStack (hidden via CSS when single card selected to preserve layout) */}
        <div className={`relative ${isSingleCardSelected || isMainBoardUnselected || isDriverBoardTab ? 'hidden' : ''}`}>
          <div
            ref={gridRef}
            className="grid-stack"
            key={currentWorkspaceId}
          >
            {currentGridItems.map((item) => {
              const cardConfig = cardConfigs.find(c => c.key === item.id);
              if (!cardConfig) return null;

              return (
                <div
                  key={item.id}
                  className={`grid-stack-item ${collapsed[item.id] ? 'gs-collapsed' : ''}`}
                  gs-id={item.id}
                  gs-x={item.x}
                  gs-y={item.y}
                  gs-w={item.w}
                  gs-h={item.h}
                >
                  <div
                    className="grid-stack-item-content"
                  >
                    {/* h-full/w-full: in permissions mode this wrapper becomes a real div
                        between the bounded grid tile and the h-full card body — without a
                        height it breaks the chain and the row virtualizer renders every row
                        (hard-freezes the tab on large buckets; see LCDashboards). */}
                    <PermissionConfigurable
                      componentId={`dispatch-${item.id}`}
                      componentName={cardConfig.title}
                      className="h-full w-full"
                    >
                      <div className="h-full w-full bg-gray-50 dark:bg-gray-800 rounded-lg shadow-md border border-gray-300 dark:border-gray-600 overflow-hidden">
                        {renderCardContent(item.id)}
                      </div>
                    </PermissionConfigurable>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Saving overlay */}
          {isSaving && (
            <div className="absolute inset-0 bg-gray-500/50 dark:bg-gray-900/50 flex items-center justify-center z-50 rounded-lg">
              <div className="bg-panel px-6 py-4 rounded-lg shadow-lg flex items-center gap-3">
                <svg className="animate-spin h-5 w-5 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span className="text-gray-700 dark:text-gray-200 font-medium">Saving layout...</span>
              </div>
            </div>
          )}
        </div>

        {/* Empty state when no cards (never on the Driver Board tab: it owns no
            cards by design, so its "workspace" is always empty) */}
        {!isDriverBoardTab && cardConfigs.filter(c => c.workspaceId === currentWorkspaceId).length === 0 && (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <ExclamationTriangleIcon className="h-12 w-12 text-gray-400 dark:text-gray-500 mb-3" />
            <h3 className="text-lg font-medium text-heading mb-1">No cards in {workspaces.find(w => w.id === currentWorkspaceId)?.name || 'this workspace'}</h3>
            <p className="text-sm text-muted mb-4">
              Get started by adding your first card to {workspaces.find(w => w.id === currentWorkspaceId)?.name || 'this workspace'}
            </p>
            <button
              onClick={() => setIsFirstCardModalOpen(true)}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-accent-fg bg-accent rounded-lg shadow-sm hover:bg-accent-hover transition-colors"
            >
              <PlusIcon className="h-5 w-5" />
              Create Card
            </button>
          </div>
        )}
      </div>

      {/* Add Card Modal */}
      {isAddCardModalOpen && (
        <div className="fixed inset-0 bg-scrim bg-opacity-50 flex items-center justify-center z-[9999] p-4">
          <div
            ref={addCardDrag.panelRef}
            style={addCardDrag.panelStyle}
            className="bg-panel rounded-lg shadow-xl max-w-md w-full"
          >
            <ModalHeader
              title="Create New Card"
              onClose={() => {
                setIsAddCardModalOpen(false);
                setNewCardTitle('');
                setNewCardColumn(0);
              }}
              dragHandleProps={addCardDrag.dragHandleProps}
            />

            {/* Content */}
            <div className="p-6 space-y-4">
              <div>
                <label htmlFor="cardTitle" className="block text-sm font-medium text-label mb-2">
                  Card Title
                </label>
                <input
                  id="cardTitle"
                  type="text"
                  value={newCardTitle}
                  onChange={(e) => setNewCardTitle(e.target.value)}
                  placeholder="Enter card title"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleCreateCard();
                    }
                  }}
                />
              </div>

              <div>
                <label htmlFor="cardColumn" className="block text-sm font-medium text-label mb-2">
                  Column
                </label>
                <select
                  id="cardColumn"
                  value={newCardColumn}
                  onChange={(e) => setNewCardColumn(parseInt(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                >
                  {Array.from({ length: columnCount }, (_, i) => (
                    <option key={i} value={i}>
                      Column {i + 1}
                    </option>
                  ))}
                </select>
              </div>

              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
                <p className="text-sm text-blue-800 dark:text-blue-200">
                  Cards in workspace: {getWorkspaceCardCount(currentWorkspaceId)} / 8
                </p>
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={() => {
                  setIsAddCardModalOpen(false);
                  setNewCardTitle('');
                  setNewCardColumn(0);
                }}
                className="px-4 py-2 text-sm font-medium text-label bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateCard}
                className="px-4 py-2 text-sm font-medium text-accent-fg bg-accent rounded-lg hover:bg-accent-hover"
              >
                Create Card
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reset Layout Confirmation Modal */}
      {showResetLayoutConfirm && (
        <div className="fixed inset-0 bg-scrim bg-opacity-50 backdrop-blur-sm z-[70] flex items-center justify-center p-4">
          <div
            ref={resetLayoutDrag.panelRef}
            style={resetLayoutDrag.panelStyle}
            className="bg-panel rounded-lg shadow-xl max-w-md w-full"
          >
            <ModalHeader
              title="Reset Grid Layout"
              onClose={() => setShowResetLayoutConfirm(false)}
              dragHandleProps={resetLayoutDrag.dragHandleProps}
            />

            {/* Content */}
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                  <ArrowPathIcon className="h-6 w-6 text-amber-600 dark:text-amber-400" />
                </div>
                <p className="text-sm text-muted">
                  This will reset card positions
                </p>
              </div>
              <p className="text-sm text-label">
                Are you sure you want to reset the grid layout for{' '}
                <span className="font-semibold text-heading">
                  "{workspaces.find(w => w.id === currentWorkspaceId)?.name || 'this workspace'}"
                </span>
                ? All cards will return to their default positions.
              </p>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={() => setShowResetLayoutConfirm(false)}
                className="px-4 py-2 text-sm font-medium text-label bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleResetLayout}
                className="px-4 py-2 text-sm font-medium text-white bg-amber-600 dark:bg-amber-600 rounded-lg hover:bg-amber-700 dark:hover:bg-amber-700 transition-colors"
              >
                Reset Layout
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Trashcan Modal */}
      {showTrashModal && (
        <div className="fixed inset-0 bg-scrim bg-opacity-50 backdrop-blur-sm z-[70] flex items-center justify-center p-4">
          <div
            ref={trashDrag.panelRef}
            style={trashDrag.panelStyle}
            className="bg-panel rounded-lg shadow-xl max-w-lg w-full"
          >
            <ModalHeader
              title="Trashcan"
              onClose={() => setShowTrashModal(false)}
              dragHandleProps={trashDrag.dragHandleProps}
            />

            {/* Content */}
            <div className="flex items-center gap-3 px-6 pt-6">
              <div className="flex-shrink-0 w-12 h-12 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
                <TrashIcon className="h-6 w-6 text-gray-600 dark:text-gray-400" />
              </div>
              <p className="text-sm text-muted">
                Deleted items are kept for 30 days
              </p>
            </div>
            <div className="p-6 max-h-96 overflow-y-auto">
              {trashLoading ? (
                <p className="text-sm text-muted text-center py-4">Loading...</p>
              ) : trashItems.length === 0 ? (
                <p className="text-sm text-muted text-center py-4">Trashcan is empty</p>
              ) : (
                <div className="space-y-3">
                  {/* Group by workspace */}
                  {Object.entries(
                    trashItems.reduce((acc: Record<string, { name: string; cards: any[]; date: string }>, item: any) => {
                      if (!acc[item.workspace_id]) {
                        acc[item.workspace_id] = { name: item.workspace_name, cards: [], date: item.date_deleted || '' };
                      }
                      if (item.card_key) {
                        acc[item.workspace_id].cards.push(item);
                      }
                      // Use earliest date_deleted for the workspace
                      if (item.date_deleted && item.date_deleted < acc[item.workspace_id].date) {
                        acc[item.workspace_id].date = item.date_deleted;
                      }
                      return acc;
                    }, {})
                  ).map(([wsId, ws]) => (
                    <div key={wsId} className="border border-gray-200 dark:border-gray-700 rounded-lg p-3">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-heading">{ws.name}</p>
                          <p className="text-xs text-muted">
                            {ws.cards.length} card{ws.cards.length !== 1 ? 's' : ''}
                            {ws.date && ` \u00b7 Deleted ${new Date(ws.date).toLocaleDateString()}`}
                          </p>
                        </div>
                        <button
                          onClick={() => handleRestoreWorkspace(wsId)}
                          className="px-3 py-1.5 text-xs font-medium text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30 rounded-md hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-colors"
                        >
                          Restore
                        </button>
                      </div>
                      {ws.cards.length > 0 && (
                        <div className="mt-2 pl-3 border-l-2 border-rule space-y-1">
                          {ws.cards.map((card: any) => (
                            <p key={card.card_key} className="text-xs text-gray-600 dark:text-gray-400">
                              {card.card_title || card.card_key}
                            </p>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end p-6 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={() => setShowTrashModal(false)}
                className="px-4 py-2 text-sm font-medium text-label bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* New Workspace Modal */}
      {showNewWorkspaceModal && (
        <div className="fixed inset-0 bg-scrim bg-opacity-50 backdrop-blur-sm z-[70] flex items-center justify-center p-4">
          <div
            ref={newWorkspaceDrag.panelRef}
            style={newWorkspaceDrag.panelStyle}
            className="bg-panel rounded-lg shadow-xl max-w-md w-full"
          >
            <ModalHeader
              title="New Workspace"
              onClose={() => {
                setShowNewWorkspaceModal(false);
                setNewWorkspaceName('');
              }}
              dragHandleProps={newWorkspaceDrag.dragHandleProps}
            />

            {/* Content */}
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                  <PlusIcon className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                </div>
                <p className="text-sm text-muted">
                  Create a new workspace to organize your cards
                </p>
              </div>
              <label className="block text-sm font-medium text-label mb-2">
                Workspace Name
              </label>
              <input
                type="text"
                value={newWorkspaceName}
                onChange={(e) => setNewWorkspaceName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleConfirmCreateWorkspace();
                  } else if (e.key === 'Escape') {
                    setShowNewWorkspaceModal(false);
                    setNewWorkspaceName('');
                  }
                }}
                placeholder={`Workspace ${workspaces.length + 1}`}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-heading placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                autoFocus
              />
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={() => {
                  setShowNewWorkspaceModal(false);
                  setNewWorkspaceName('');
                }}
                className="px-4 py-2 text-sm font-medium text-label bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmCreateWorkspace}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
              >
                Create Workspace
              </button>
            </div>
          </div>
        </div>
      )}

      {/* First Card Creation Modal */}
      <FirstCardModal
        isOpen={isFirstCardModalOpen}
        dashboardType="dispatch"
        onClose={() => setIsFirstCardModalOpen(false)}
        hideStatButtonSettings={currentWorkspaceId !== 'default'}
        onSubmit={(cardName: string, conditions: FilterCondition[], view: string, bucket?: string, statButtonLabel?: string, statButtonCountField?: string, showStatButton?: boolean, isShared?: boolean, sharedTableSettings?: any, includeClosed?: boolean) => {
          // Generate a unique key for the new card
          const newKey = `card-${Date.now()}`;

          // Get the highest order number in the first column
          const columnCards = cardConfigs.filter(c => c.column === 0 && c.workspaceId === currentWorkspaceId);
          const maxOrder = columnCards.length > 0 ? Math.max(...columnCards.map(c => c.order)) : -1;

          // Calculate GridStack position for new card
          const gridPos = getNextGridPosition(currentWorkspaceId);

          // Create the new card config with GridStack position
          const newCard: CardConfig = {
            key: newKey,
            title: cardName,
            column: 0,
            order: maxOrder + 1,
            visible: true,
            span: 1,
            workspaceId: currentWorkspaceId,
            filterConditions: conditions,
            filterView: view,
            includeClosed: includeClosed,
            bucket: bucket,
            showStatButton: showStatButton,
            statButtonLabel: statButtonLabel,
            statButtonCountField: statButtonCountField,
            gsX: gridPos.gsX,
            gsY: gridPos.gsY,
            gsW: gridPos.gsW,
            gsH: gridPos.gsH
          };

          // If shared card included table settings, populate the cache BEFORE mounting the card
          // so TanTable sees initialSettings on first render
          if (sharedTableSettings) {
            const settingsKey = `table_dispatch-card-${newKey}`;
            handleTableSettingsSaved(settingsKey, sharedTableSettings);
            // Persist to backend in background
            userSettingsService.saveUserSetting(settingsKey, sharedTableSettings).catch(err => {
              console.error('Failed to save shared table settings:', err);
            });
          }

          // Add the card to the config (triggers re-render + TanTable mount)
          const updatedConfigs = [...cardConfigs, newCard];
          setCardConfigs(updatedConfigs);

          // Save the layout immediately so new card persists
          saveLayout(updatedConfigs, true);

          // Fetch data for this card. includeClosed is passed explicitly: cardConfigsRef
          // has not picked up setCardConfigs yet, so the ref lookup inside would miss it.
          fetchCardData(
            newKey, conditions, view, bucket, false, newCard.additionalFields,
            undefined, undefined, includeClosed,
          );

          // Close the modal
          setIsFirstCardModalOpen(false);
        }}
      />

      {/* Edit Card Modal */}
      {editingCardFiltersKey && (() => {
        const editCard = cardConfigs.find(c => c.key === editingCardFiltersKey);
        if (!editCard) return null;

        return (
          <FirstCardModal
            isOpen={true}
            dashboardType="dispatch"
            onClose={() => setEditingCardFiltersKey(null)}
            initialCardName={editCard.title}
            initialConditions={editCard.filterConditions}
            initialView={editCard.filterView}
            initialBucket={editCard.bucket}
            initialStatButtonLabel={editCard.statButtonLabel}
            initialStatButtonCountField={editCard.statButtonCountField}
            initialShowStatButton={editCard.showStatButton}
            initialIsShared={editCard.isShared}
            initialIncludeClosed={editCard.includeClosed}
            hideStatButtonSettings={editCard.workspaceId !== 'default'}
            isEditMode={true}
            onSubmit={(cardName: string, conditions: FilterCondition[], view: string, bucket?: string, statButtonLabel?: string, statButtonCountField?: string, showStatButton?: boolean, isShared?: boolean, sharedTableSettings?: any, includeClosed?: boolean) => {
              // Update the card configuration (visibleColumns preserved - managed separately on dashboard)
              const updatedConfigs = cardConfigs.map(card => {
                if (card.key === editingCardFiltersKey) {
                  return {
                    ...card,
                    title: cardName,
                    filterConditions: conditions,
                    filterView: view,
                    includeClosed: includeClosed,
                    bucket: bucket,
                    showStatButton: showStatButton,
                    statButtonLabel: statButtonLabel,
                    statButtonCountField: statButtonCountField,
                    isShared: isShared
                  };
                }
                return card;
              });
              // IMPORTANT: Update ref BEFORE triggering state changes
              // This ensures fetchAllCardData reads the updated filter conditions
              cardConfigsRef.current = updatedConfigs;

              setCardConfigs(updatedConfigs);

              // Save the layout immediately so edits persist
              saveLayout(updatedConfigs, true);

              // Fetch updated data using batch-cards endpoint (not the old bucket-specific endpoint)
              // Find the updated card config to fetch
              const updatedCard = updatedConfigs.find(c => c.key === editingCardFiltersKey);
              if (updatedCard && (updatedCard.bucket || (updatedCard.filterConditions && updatedCard.filterConditions.length > 0))) {
                // Use batch endpoint for bucket cards (including single card)
                // For non-bucket cards, this will fall back to individual fetch
                fetchAllCardData([updatedCard], false, false);
              }

              // Close the modal
              setEditingCardFiltersKey(null);
            }}
            onDelete={() => {
              // Remove the card from cardConfigs
              const cardKey = editingCardFiltersKey;
              const updatedConfigs = cardConfigs.filter(card => card.key !== cardKey);
              setCardConfigs(updatedConfigs);

              // Save the layout immediately so deletion persists
              saveLayout(updatedConfigs, true);

              // MEMORY OPTIMIZATION: Clear the data for this card to free memory
              if (cardKey) {
                setCardData(prev => {
                  const newData = { ...prev };
                  delete newData[cardKey];
                  return newData;
                });
                // Drop any captured Documents scope too — card keys are timestamps so a
                // stale entry could not be reused, but it pins a PRO array for the rest
                // of the session.
                closeCardDocuments(cardKey);
              }

              // Close the modal
              setEditingCardFiltersKey(null);
            }}
          />
        );
      })()}

      {/* ProDetailsView Modal */}
      {selectedProNumber && (
        <ProDetailsView
          isOpen={true}
          pronumber={selectedProNumber}
          onClose={() => setSelectedProNumber(null)}
        />
      )}

      {/* Driver Pay Addition Modal — opened from the Missing Waiting Time bucket */}
      {driverPayAddition && (
        <DriverPayAdditionModal
          isOpen={true}
          pronumber={driverPayAddition.pronumber}
          recno={driverPayAddition.recno}
          onClose={() => setDriverPayAddition(null)}
          onSaved={() => {
            // Refetch the Missing Waiting Time card so the row reflects the edit
            const card = cardConfigs.find((c) => c.key === 'missing-waiting-time');
            if (card) {
              fetchCardData(
                card.key,
                card.filterConditions || [],
                card.filterView || 'Orders',
                card.bucket,
                false,
                card.additionalFields,
              );
            }
          }}
        />
      )}

      {/* Loading overlay while the clicked ticket is fetched — the editor
          opens once the by-recno fetch resolves. */}
      {ticketLoading && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-scrim/40">
          <div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin" />
        </div>
      )}

      {/* Trouble ticket editor — rendered as a right-side drawer to keep the
          prior UX, backed by the shared TroubleTicketEditModal. */}
      {selectedTicket && (
        <TroubleTicketEditModal
          variant="drawer"
          pronumber={selectedTicket.pronumber}
          ticket={selectedTicket}
          billTo={selectedTicket.billto ?? undefined}
          onOpenPro={(pro) => setSelectedProNumber(String(pro))}
          onClose={() => setSelectedTicket(null)}
          onSaved={() => { triggerImmediateRefresh(); setSelectedTicket(null); }}
          onRescheduleApplied={() => triggerImmediateRefresh()}
        />
      )}

      {/* Duplicate Card Modal */}
      <DuplicateCardModal
        isOpen={duplicateCardConfig !== null}
        onClose={() => setDuplicateCardConfig(null)}
        onSubmit={handleDuplicateCard}
        originalCardName={duplicateCardConfig?.title || ''}
        workspaces={workspaces}
        currentWorkspaceId={currentWorkspaceId}
        getWorkspaceCardCount={getWorkspaceCardCount}
      />

      {/* Change All — order drawer in change-all mode on the first pro (anchor);
          flagged fields fan out to the rest on Save. Refresh the board on close. */}
      <ProDetailSlideOver
        pronumber={changeAllPros && changeAllPros.length > 0 ? changeAllPros[0] : null}
        isOpen={!!changeAllPros}
        onClose={() => {
          setChangeAllPros(null);
          triggerImmediateRefresh();
        }}
        changeAll={changeAllPros ? { targetPros: changeAllPros } : null}
      />

      {/* Change Template for All — bulk apply to the card's visible/filtered pros. */}
      <ChangeTemplateBulkModal
        isOpen={ctBulkOpen}
        pros={ctBulkPros}
        onClose={() => setCtBulkOpen(false)}
        onApplied={() => triggerImmediateRefresh()}
      />
    </div>
  );
}

export default function DispatchDashboard() {
  return (
    <ViewedProsProvider>
      <DispatchDashboardContent />
    </ViewedProsProvider>
  );
}
